<style scoped>
/* The ledger's toggle, in the corner above the right rail's column. It is the
   first card of that column: the rail's cards are 48px wide with their icons
   centred 36px in from the edge, and 12px of gutter apart, so this one is the
   same width, hangs 12px in, and leaves the rail's own 60px start below it. */
.ledger-toggle {
	position: absolute;
	top: 12px;
	right: 12px;
	z-index: 20;
	width: 48px;
	height: 36px;
	border: 0;
	border-radius: 6px;
	background: #fff;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	color: #495057;
	font-size: 1.1rem;
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	transition: background 0.15s ease;
}

.ledger-toggle:hover {
	background: #f0f0f0;
}

.ledger-toggle.active {
	background: #e9ecef;
	color: #212529;
}

.map-review-map {
	position: absolute;
	/* The map fills the viewport; the details, layers, search, filters and tools
	   panels float over it off its left and right edges (see the template). */
	inset: 0;
}

/* The map fills its container. */
.map-review-map :deep(.maplibregl-map) {
	width: 100%;
	height: 100%;
}

.map-review-map :deep(.maplibregl-canvas) {
	width: 100%;
	height: 100%;
}

/* The draw-mode selector, floating over the map while a draw is armed. It sits
   at the top of the map rather than in the sidebar because the draw is a
   gesture on the map and the operator's eyes are there; it is deliberately
   small, and it disappears the moment the draw is disarmed. */
.draw-mode-bar {
	position: absolute;
	top: 8px;
	left: 50%;
	transform: translateX(-50%);
	z-index: 5;
	display: flex;
	align-items: center;
	gap: 6px;
	padding: 5px 10px;
	background: #fff;
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	font-size: 0.8125rem;
}

.draw-mode-title {
	font-weight: 600;
	color: #495057;
}

.draw-mode-btn {
	border: 1px solid #ced4da;
	background: #fff;
	border-radius: 0.25rem;
	padding: 2px 8px;
	font-size: 0.75rem;
	line-height: 1.4;
}

.draw-mode-btn:hover {
	background: #f1f3f5;
}

.draw-mode-btn.active {
	background: #e7f5ff;
	border-color: #74c0fc;
	color: #1864ab;
}

.draw-mode-radius {
	width: 5rem;
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	padding: 2px 6px;
	font-size: 0.75rem;
}

.draw-mode-unit {
	border: 1px solid #ced4da;
	border-radius: 0.25rem;
	padding: 2px 4px;
	font-size: 0.75rem;
}

.draw-mode-hint {
	color: #6c757d;
	font-size: 0.75rem;
}

/* ── The details panel ─────────────────────────────────────────────────
   The selected record's details are the page's one left panel, in the shared
   tool rail (ts/components/panel/ToolRail.vue) the Reveal workbench and this
   page's own layers, search, filters and tools panes use. The details have
   exactly one home, and nothing is allowed to sit over the panel. What the map
   is read BY — the search box, the layer rows, the filters and the tools — is
   not here: it reads the map, so it is the panels off the map's right edge (see
   the template). */
/* The workbench column — `--rail-w`, the fixed rail and the topbar that clears
   it — is declared once in the global sheet (ts/style/style.scss), because the
   lab's trap view stands on the same column. */

/* The layers, search, filters and tools panes, in the shared tool rail's body
   off the map's right edge — one pane per rail tab. The body owns the box — its
   width, its background, its padding and its scroll — so a pane is only its
   content: what was the rail column's own width, padding and scrollbar is gone,
   so nothing doubles up inside the body. The type scale is the one thing the
   panes still set for themselves. */
.control-pane {
	font-size: 0.8125rem;
}

/* The details pane, the left panel body's whole content. The body owns the
   width, the padding and the scroll, so the pane is only its content and its
   type scale. */
.detail-pane {
	font-size: 0.8125rem;
}

.detail-pane-empty {
	margin: 0;
	color: #667085;
	font-size: 0.8125rem;
}

.control-pane-row {
	display: flex;
	align-items: center;
	gap: 6px;
	line-height: 1.6;
}

/* The four band rows refine the Features row they sit under. */
.control-pane-row--child {
	padding-left: 18px;
}

/* The visibility rows are labels wrapping a checkbox: clicking anywhere on the
   row toggles it, so the whole line is a pointer target. */
label.control-pane-row {
	cursor: pointer;
}

.control-pane-checkbox {
	margin: 0;
	flex-shrink: 0;
}

/* A hidden row dims its swatch and label; the count stays readable so the
   operator can still see what is off and how much it holds. The checkbox keeps
   full strength, so the control to bring the layer back stays obvious. */
.control-pane-row--hidden .control-pane-swatch,
.control-pane-row--hidden .control-pane-label {
	opacity: 0.4;
}

.control-pane-swatch {
	display: inline-block;
	width: 12px;
	height: 12px;
	border-radius: 50%;
	border: 2px solid #fff;
	box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.2);
	flex-shrink: 0;
}

/* The due-soon horizon rides the "Due soon" legend row inline, right after the
   count and tiny, so it adds no height to the pane's rows. It is deliberately
   NOT right-anchored: the trailing day readout grows from "5d" to "30d", and a
   right-anchored slider would slide left under it as the digits change. The
   visible track is a pseudo-element pinned to the wrapper's vertical centre,
   and the range input fills that same wrapper, so the thumb (the input's full
   height) sits centred on the track however each engine aligns it internally. */
.control-pane-due-soon-slider {
	position: relative;
	display: inline-flex;
	align-items: center;
	flex: 0 0 auto;
	width: 52px;
	height: 10px;
}

.control-pane-due-soon-slider::before {
	content: "";
	position: absolute;
	left: 0;
	right: 0;
	top: 50%;
	height: 4px;
	margin-top: -2px;
	border-radius: 2px;
	background: #dee2e6;
	pointer-events: none;
}

.control-pane-due-soon-slider-input {
	position: relative;
	z-index: 1;
	appearance: none;
	-webkit-appearance: none;
	width: 100%;
	height: 10px;
	margin: 0;
	background: transparent;
	cursor: pointer;
}

.control-pane-due-soon-slider-input::-webkit-slider-runnable-track {
	height: 10px;
	background: transparent;
}

.control-pane-due-soon-slider-input::-webkit-slider-thumb {
	appearance: none;
	-webkit-appearance: none;
	width: 10px;
	height: 10px;
	margin: 0;
	border: none;
	border-radius: 50%;
	background: var(--due-soon-color, #e69f00);
	cursor: pointer;
}

.control-pane-due-soon-slider-input::-moz-range-track {
	height: 10px;
	background: transparent;
}

.control-pane-due-soon-slider-input::-moz-range-thumb {
	width: 10px;
	height: 10px;
	border: none;
	border-radius: 50%;
	background: var(--due-soon-color, #e69f00);
	cursor: pointer;
}

.control-pane-due-soon-value {
	flex: 0 0 auto;
	color: #6c757d;
	font-weight: 400;
	font-variant-numeric: tabular-nums;
}

.control-pane-approximate {
	margin-top: 6px;
	padding-top: 6px;
	border-top: 1px solid #f1f3f5;
	color: #b45309;
	display: flex;
	align-items: center;
	gap: 4px;
}

/* The selected record's details, the left panel's whole content. Their home is
   the panel and only the panel: the layout inside is designed against a column
   whose width is a constant of the panel, which is what the floating balloon
   they replaced never had — its height used to be decided by where the operator
   clicked, it flipped sides, and it clamped itself to the viewport. */
.map-popup {
	/* ── The popup design system ───────────────────────────────────────────
	   One named type scale, one spacing rhythm, one neutral ramp with one
	   accent and one provenance palette, one radius/border/shadow family,
	   shared by all five popups. The tokens live on .map-popup itself so
	   nothing here can bleed into the rest of the app.

	   Type (5 steps)          Space (6 steps)
	   --pp-text-2xs   11px    --pp-space-1    2px
	   --pp-text-xs    12px    --pp-space-2    4px
	   --pp-text-sm    13px    --pp-space-3    8px
	   --pp-text-md    14px    --pp-space-4   12px
	   --pp-text-lg    15px    --pp-space-5   14px
	                           --pp-space-6   16px

	   Rhythm: a group stands 10px above the one before it, under a named
	   eyebrow, and its own rows are ruled — a faint hairline between one fact
	   and the next. The rule is the binding inside a group and the space is
	   the separation between groups, so only one mechanism is doing grouping
	   work at each level and neither has to be paid for twice.
	   The panel is height-capped at ~380px on a 900px viewport, so the scale
	   is set by what fits a record in one screenful, not by the canvas. */
	--pp-text-2xs: 0.6875rem;
	--pp-text-xs: 0.75rem;
	--pp-text-sm: 0.8125rem;
	--pp-text-md: 0.875rem;
	--pp-text-lg: 0.9375rem;

	--pp-space-1: 2px;
	--pp-space-2: 4px;
	--pp-space-3: 8px;
	--pp-space-4: 12px;
	--pp-space-5: 14px;
	--pp-space-6: 14px;

	--pp-ink-1: #1b1f24;
	--pp-ink-2: #4a5259;
	--pp-ink-3: #667085;
	--pp-line-1: #e4e8ec;
	/* The hairline between rows. Translucent rather than a fixed grey, so it
	   reads the same on every section's tint as it does on white. */
	--pp-row-line: rgba(16, 24, 40, 0.09);
	--pp-line-2: #dfe3e8;
	--pp-surface-1: #ffffff;
	--pp-surface-2: #f7f8fa;
	--pp-accent: #1a5fb4;
	--pp-accent-soft: #e8f1fb;

	/* Provenance chips: the hue encodes the source and nothing else. Every ink
	   clears 4.5:1 on its own chip. */
	--pp-chip-direct-ink: #4a5259;
	--pp-chip-direct-bg: #f4f5f7;
	--pp-chip-direct-line: #dfe3e8;
	--pp-chip-skip-trace-ink: #1864ab;
	--pp-chip-skip-trace-bg: #e7f5ff;
	--pp-chip-skip-trace-line: #a5d8ff;
	--pp-chip-county-assessor-ink: #8a4b08;
	--pp-chip-county-assessor-bg: #fff4e5;
	--pp-chip-county-assessor-line: #ffd8a8;
	--pp-chip-rmo-ink: #237a34;
	--pp-chip-rmo-bg: #ebfbee;
	--pp-chip-rmo-line: #b2f2bb;
	--pp-chip-virtual-inspection-ink: #6741d9;
	--pp-chip-virtual-inspection-bg: #f3f0ff;
	--pp-chip-virtual-inspection-line: #d0bfff;

	--pp-radius-sm: 6px;
	--pp-radius-md: 10px;
	--pp-radius-lg: 14px;
	--pp-radius-pill: 999px;
	/* ONE grid for the whole popup. The label gutter is a single track sized
	   to the longest label the popup actually renders — not one column per
	   section — so every value in the popup, in every section, on every row
	   type, starts at the same x. `max-content` is what guarantees no label
	   ever wraps: the track is exactly as wide as its widest label. */
	--pp-key-gap: 6px;
	/* One row pitch for every row type: 3px of air above and below one line of
	   body text at 1.45. Nothing in the popup is tighter than this. */
	--pp-row-pad: 3px;
	--pp-line: 1px solid var(--pp-line-2);

	display: block;
	font-size: var(--pp-text-sm);
	line-height: 1.45;
	color: var(--pp-ink-1);
	/* Counts, quantities, rates and dates keep their columns from jittering. */
	font-variant-numeric: tabular-nums;
	/* A 250-character note or a 28-email list wraps instead of stretching the
	   popup or clipping its right edge. `break-word` first for older Safari,
	   which predates `anywhere`. */
	overflow-wrap: break-word;
	overflow-wrap: anywhere;
}

/* Every control in the popup shows where the keyboard is. */
.map-popup :is(button, a, [tabindex]):focus-visible {
	outline: 2px solid var(--pp-accent);
	outline-offset: 2px;
}

/* The drawer's head row. It sticks to the top of the drawer's own scroll, so
   the Back walk and the Close control are reachable however long the record is,
   and the Close sits at the far right whether or not there is anywhere to walk
   back to. */
.map-popup-head {
	position: sticky;
	top: 0;
	z-index: 3;
	display: flex;
	align-items: center;
	gap: var(--pp-space-2);
	padding: var(--pp-space-2) 0 var(--pp-space-1);
	background: var(--pp-surface-1);
}

.map-popup-close {
	margin-left: auto;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border: 0;
	border-radius: var(--pp-radius-sm);
	background: transparent;
	font-size: 1.25rem;
	line-height: 1;
	color: var(--pp-ink-3);
	cursor: pointer;
}

.map-popup-close:hover {
	background: var(--pp-surface-2);
	color: var(--pp-ink-1);
}

.map-popup-title {
	font-size: var(--pp-text-lg);
	font-weight: 600;
	line-height: 1.2;
	letter-spacing: -0.015em;
	color: var(--pp-ink-1);
	margin-bottom: var(--pp-space-1);
}

/* The Back control sits opposite the close control, so the walking affordance
   is always in the same place however deep the graph is. */
.map-popup-back {
	display: inline-flex;
	align-items: center;
	min-height: 24px;
	margin-left: calc(-1 * var(--pp-space-2));
	padding: 0 var(--pp-space-2);
	border: 0;
	border-radius: var(--pp-radius-sm);
	background: transparent;
	font-size: var(--pp-text-xs);
	font-weight: 600;
	line-height: 1.4;
	color: var(--pp-accent);
	cursor: pointer;
}

.map-popup-back:hover {
	background: var(--pp-surface-2);
}

.map-popup-subtitle {
	font-size: var(--pp-text-xs);
	font-weight: 400;
	line-height: 1.3;
	color: var(--pp-ink-3);
}

/* Links between popup nodes: plain buttons so they inherit the popup's
   typography but read as clickable. The hit target is a pseudo-element rather
   than padding, so a link never inflates the height of the row it sits in and
   every row keeps the popup's one pitch. */
.popup-link {
	position: relative;
	display: inline-block;
	padding: 0;
	border: 0;
	background: transparent;
	font-size: inherit;
	font-family: inherit;
	line-height: 1.45;
	color: var(--pp-accent);
	font-weight: 600;
	text-align: left;
	cursor: pointer;
}

.popup-link::after {
	content: "";
	position: absolute;
	left: 0;
	right: 0;
	top: calc(-1 * var(--pp-row-pad));
	bottom: calc(-1 * var(--pp-row-pad));
}

.popup-link:hover {
	text-decoration: underline;
}

/* A list starts on the value rail and lays its own key column out inside it,
   sized to the list's own widest key (--pp-list-key-col, measured), so every
   reading in the list starts on the same x. */
.popup-section > .popup-list {
	margin-left: calc(var(--pp-key-col, 0px) + var(--pp-key-gap));
}

/* A list row — a Feature, an Observation, a Treatment, a Contact's Site. It is
   data, not a fact: its key and the thing that qualifies it both start inside
   the value column, and neither is allowed into the label gutter. A list key is
   an identifier (a Feature number, a date), so if it were allowed into the
   gutter it would size the gutter above every field label in the popup and
   leave a dead channel in front of each short label. */
.popup-link-row {
	display: grid;
	grid-template-columns: var(--pp-list-key-col, max-content) minmax(0, 1fr);
	column-gap: var(--pp-space-3);
	align-items: baseline;
	/* One body line plus the row's own air, whatever the key's type size, so
	   every row type keeps the popup's one pitch. */
	min-height: calc(var(--pp-row-pad) * 2 + 1.45 * var(--pp-text-sm));
}

/* The rule binds a row to the row above it; the first row of a list sits under
   the eyebrow that already separates it from what came before. */
.popup-link-row + .popup-link-row {
	border-top: 1px solid var(--pp-row-line);
}

.popup-link-row > :first-child {
	grid-column: 1;
}

.popup-link-row > :last-child {
	grid-column: 2;
}

/* The row's key carries the row's own air, so a link that is a whole row is a
   24px target without the row growing past the popup's one pitch. It is set at
   the label size rather than the body size: a list key is a key, exactly like a
   field label, so the popup has one key size as well as one key column. */
.popup-link-row > .popup-link {
	display: block;
	padding: var(--pp-row-pad) 0;
	min-height: 24px;
	font-size: var(--pp-text-xs);
}

.popup-link-row > .popup-link::after {
	content: none;
}

/* A Site's person: one row however many roles that Site links them by. The name
   is data rather than a field label, so the row starts on the value rail with
   every other value, and the provenance chip finishes at the row's right edge. */
.popup-person {
	display: flex;
	align-items: flex-start;
	gap: var(--pp-space-3);
	padding: var(--pp-row-pad) 0;
	border-top: 1px solid var(--pp-row-line);
}

.popup-person-main {
	display: flex;
	flex: 1 1 auto;
	min-width: 0;
	flex-wrap: wrap;
	align-items: baseline;
	gap: 0 var(--pp-space-2);
}

.popup-person-sources {
	display: flex;
	flex-shrink: 0;
	gap: var(--pp-space-1);
	margin-left: auto;
}

/* A contact's role on the Site: an outline chip, deliberately not the filled
   provenance chip — the hue encodes the source and nothing else, so a second
   fact about the same row gets a second, neutral form. */
.popup-role-chip {
	display: inline-block;
	flex-shrink: 0;
	border: 1px solid var(--pp-line-2);
	border-radius: var(--pp-radius-sm);
	padding: 0 var(--pp-space-2);
	font-size: var(--pp-text-2xs);
	font-weight: 500;
	line-height: 1.6;
	color: var(--pp-ink-2);
	white-space: nowrap;
}

/* The primary link of a contact's several: the one the Site leads with. */
.popup-role-chip--primary {
	background: var(--pp-accent-soft);
	border-color: var(--pp-accent-soft);
	color: var(--pp-accent);
}

/* A group's own summary of the list beneath it — the class distribution of a
   Site's Features. One quiet line rather than a second label column, so the
   distribution is visible without out-weighing the list it summarises. */
.popup-rollup {
	color: var(--pp-ink-2);
	font-size: var(--pp-text-2xs);
	line-height: 1.45;
}

/* A contact's source, shown as a small chip so the provenance travels with the
   name. One neutral chip per source; the colour only separates them. */
.popup-source-chip {
	display: inline-block;
	flex-shrink: 0;
	border-radius: var(--pp-radius-pill);
	padding: 1px var(--pp-space-2);
	font-size: var(--pp-text-2xs);
	font-weight: 500;
	line-height: 1.5;
	letter-spacing: 0.01em;
	background: var(--pp-chip-direct-bg);
	color: var(--pp-chip-direct-ink);
	border: 1px solid var(--pp-chip-direct-line);
	white-space: nowrap;
	justify-self: end;
}

.popup-source-chip--skip_trace {
	background: var(--pp-chip-skip-trace-bg);
	border-color: var(--pp-chip-skip-trace-line);
	color: var(--pp-chip-skip-trace-ink);
}

.popup-source-chip--county_assessor {
	background: var(--pp-chip-county-assessor-bg);
	border-color: var(--pp-chip-county-assessor-line);
	color: var(--pp-chip-county-assessor-ink);
}

.popup-source-chip--rmo {
	background: var(--pp-chip-rmo-bg);
	border-color: var(--pp-chip-rmo-line);
	color: var(--pp-chip-rmo-ink);
}

.popup-source-chip--virtual_inspection {
	background: var(--pp-chip-virtual-inspection-bg);
	border-color: var(--pp-chip-virtual-inspection-line);
	color: var(--pp-chip-virtual-inspection-ink);
}

/* The skip-trace action: a small, quiet control sized to sit with the source
   chips rather than a full-width button. It spends credits, so it stays
   clickable, but it must not read as the popup's primary action. */
.popup-action-btn {
	display: inline-flex;
	align-items: center;
	min-height: 24px;
	margin-top: var(--pp-space-1);
	border: var(--pp-line);
	background: var(--pp-surface-2);
	color: var(--pp-ink-2);
	border-radius: var(--pp-radius-pill);
	padding: 0 var(--pp-space-3);
	font-size: var(--pp-text-xs);
	font-weight: 500;
	line-height: 1.4;
	cursor: pointer;
}

.popup-action-btn:hover:not(:disabled) {
	background: var(--pp-line-1);
	color: var(--pp-ink-1);
}

.popup-action-btn:disabled {
	opacity: 0.55;
	cursor: default;
}

/* Fold a long list: the action sits in the group's own eyebrow, beside the
   count it acts on, instead of taking a row of its own under the list. */
.popup-fold {
	margin-left: var(--pp-space-2);
	padding: 0 var(--pp-space-1);
	min-height: 24px;
	border: 0;
	background: transparent;
	color: var(--pp-accent);
	font-size: var(--pp-text-2xs);
	font-weight: 600;
	letter-spacing: normal;
	line-height: 1.4;
	text-transform: none;
	cursor: pointer;
}

.popup-fold:hover {
	text-decoration: underline;
}

/* A contact's raw skip-trace history: one entry per person. The entry draws no
   box of its own — a tinted card would duplicate the row rules — so it is a
   plain block and its rows are the popup's own rows, sharing the one measured
   label column and the value rail. It is deliberately NOT `display: contents`:
   that was used to let the rows join a shared grid, and Safari's handling of
   `display: contents` is the buggiest of any engine's, so the popup stops
   depending on it altogether. */
.popup-history-card {
	display: block;
}

/* The person's name leads the entry on the value rail; the flags that qualify
   them finish on the row's right edge. */
.popup-history-name {
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: var(--pp-space-3);
	padding: var(--pp-row-pad) 0;
}

.popup-history-index {
	color: var(--pp-ink-3);
	font-size: var(--pp-text-2xs);
	font-weight: 400;
}

/* ── One label column, measured ────────────────────────────────────────
   Every fact row is its own two-column grid — a label cell and a value cell —
   and every one of them is handed the SAME label width, measured from the
   popup's own labels and applied as `--pp-key-col`. That is what puts every
   value in the popup on one rail.

   CSS `subgrid` would express this in one line, and it is what this file used
   before: sections and rows subgridded onto the popup's grid. It is not
   supported by every browser our operators run, and where it is unsupported
   the declaration is dropped silently — each row collapses to a single column
   and every label stacks above its own value, which reads as a mangled popup
   rather than a broken feature. A measured width is one variable, works
   everywhere, and costs a single frame of measurement (see
   `measurePopupColumns`). */

/* One fact per row: the label takes the measured gutter, and the value starts
   6px later — the same 6px every other value in the popup gets. The faint rule
   between rows is the binding between a label and the value beside it, so the
   pair never drifts apart. This is the one grouping mechanism inside a
   section; sections themselves are separated by space and the eyebrow alone. */
.popup-kv {
	display: grid;
	grid-template-columns: var(--pp-key-col, max-content) minmax(0, 1fr);
	column-gap: var(--pp-key-gap);
	align-items: baseline;
	padding: var(--pp-row-pad) 0;
	border-top: 1px solid var(--pp-row-line);
}

/* The first row of a table sits directly under its eyebrow, which already
   separates it from what came before. */
.popup-label + * {
	border-top: 0;
}

/* A history entry's person line is a row of data, not a fact: it starts on the
   value rail and runs to the row's right edge. */
.popup-kv--lead {
	display: flex;
	justify-content: space-between;
	gap: var(--pp-space-3);
	margin-left: calc(var(--pp-key-col, 0px) + var(--pp-key-gap));
	border-top: 1px solid var(--pp-row-line);
}

/* The person's name is data, so it wraps like data rather than holding a
   column open, and it reads from the left. */
.popup-kv--lead .popup-kv-key {
	white-space: normal;
	text-align: left;
}

.popup-label + .popup-history-card > .popup-kv--lead {
	border-top: 0;
}

/* The row types with no label to put in the gutter — a person, a history
   entry's own line — start on the value rail: indented by the label column and
   its gutter rather than sitting in the column. */
.popup-section > .popup-person,
.popup-section > .popup-kv--lead {
	margin-left: calc(var(--pp-key-col, 0px) + var(--pp-key-gap));
}

.popup-kv-key {
	grid-column: 1;
	color: var(--pp-ink-2);
	font-size: var(--pp-text-xs);
	white-space: nowrap;
	/* Left-aligned in the one global column: every label in the popup starts on
	   the same left edge, so the eye has one predictable return point straight
	   down the popup however many sections it crosses. The column is only ever
	   as wide as the popup's longest label, so a short label leaves a channel
	   that is bounded by that one label and never moves. */
	text-align: left;
}

/* The value starts on the rail. */
.popup-kv > :last-child {
	grid-column: 2 / -1;
}

/* A count is a value like any other: it starts on the shared rail rather than
   inside a box of its own, so "11" and "3" begin at the same x as every other
   value in the popup. The digits stay tabular, so they still compare down the
   column. */
.popup-num {
	display: inline;
}

/* A dimension's value with the observation that set it. The value keeps the
   rail; its provenance sits beneath it in the value column, so the sentence
   "as of … · Observation #N" stays on one line instead of wrapping inside the
   value column and paying twice for the link's 24px target. Label, value and
   provenance sit inside one row, so the row's own rule binds them. */
.popup-cell {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
}

.popup-asof {
	color: var(--pp-ink-3);
	font-size: var(--pp-text-2xs);
	line-height: 1.45;
	text-align: left;
}

/* The provenance line is set a size smaller, so its link needs a taller hit
   area than the row's own air gives it — still without moving the line. */
.popup-asof .popup-link::after {
	top: -5px;
	bottom: -5px;
}

/* A fact stated on its own, without a key beside it — the Site's address. It is
   a value, so it starts on the rail like every other value in the popup. */
.popup-value {
	margin-left: calc(var(--pp-key-col, 0px) + var(--pp-key-gap));
	font-size: var(--pp-text-sm);
	line-height: 1.45;
	color: var(--pp-ink-1);
}

/* The Observation's subject: the reading is what the record is *about*, so it
   takes the one size between body and title. Nothing else in the popup does. */
.popup-reading {
	font-size: var(--pp-text-md);
	font-weight: 600;
	line-height: 1.3;
	letter-spacing: -0.01em;
}

/* An empty state is a different kind of thing from a value: the popup looked
   and found nothing, and says so in a voice of its own rather than pretending
   to be a fact. */
.popup-empty {
	font-size: var(--pp-text-xs);
	font-style: italic;
	color: var(--pp-ink-3);
}

/* The absence banner: prominent, above the dimensions. */
.popup-banner {
	margin-top: var(--pp-space-3);
	padding: var(--pp-space-3) var(--pp-space-4);
	border-radius: var(--pp-radius-md);
	line-height: 1.45;
	font-size: var(--pp-text-xs);
}

.popup-banner--inactive {
	background: var(--pp-chip-county-assessor-bg);
	border: 1px solid var(--pp-chip-county-assessor-line);
	color: var(--pp-chip-county-assessor-ink);
}

.popup-banner-title {
	font-size: var(--pp-text-sm);
	font-weight: 600;
	margin-bottom: 2px;
}

.map-popup-note {
	color: var(--pp-ink-2);
	font-size: var(--pp-text-xs);
	line-height: 1.45;
}

/* ── Sections are regions ──────────────────────────────────────────────
   Each section is a pale region, tinted by what it is *about*, so a colour
   means the same thing wherever it appears: where the record is, the people
   attached to it, its features, the field readings, the work done, the running
   activity. The tint is the section's own mechanism — a common region (Palmer
   1992) — and the hairline between rows is the row's — connectedness (Palmer &
   Rock 1994). They do different jobs at different levels, so they read together
   rather than competing. What they replace is a third signal, a rule drawn off
   each eyebrow, which said the same thing as the tint in a second visual
   language. */
.popup-section {
	padding: 8px 10px 5px;
	margin-bottom: 10px;
	border-radius: 8px;
	background: var(--sec-bg, transparent);
}

/* Pale enough that the ink keeps its contrast: every body ratio stays above
   4.5:1 on every tint, and the tints stay far apart in hue so a glance is
   enough to tell one section from the next. */
.popup-section--place {
	--sec-bg: #f2f7fd;
}

.popup-section--people {
	--sec-bg: #f2f9f4;
}

.popup-section--features {
	--sec-bg: #fdf8ef;
}

.popup-section--observations {
	--sec-bg: #f6f4fd;
}

.popup-section--treatments {
	--sec-bg: #fdf4f6;
}

.popup-section--activity {
	--sec-bg: #f4f6f8;
}

/* Two sections of the same family can sit next to each other — the Observation
   popup opens with Recorded, Condition and Inspection, and the Site popup ends
   with Activity and Note. A family that repeats takes its second tint by
   alternate, so no two neighbours ever share a background and the boundary
   stays visible without the colour having to shout. */
.popup-section--place-alt {
	--sec-bg: #eef4fc;
}

.popup-section--people-alt {
	--sec-bg: #edf6f0;
}

.popup-section--features-alt {
	--sec-bg: #fbf5ea;
}

.popup-section--observations-alt {
	--sec-bg: #f2eefc;
}

.popup-section--treatments-alt {
	--sec-bg: #fbf0f3;
}

.popup-section--activity-alt {
	--sec-bg: #f0f3f6;
}

/* The eyebrow: small, spaced, quiet. It names the block the tint beneath it
   already delimits, so it draws no rule of its own — one region, one signal. */
.popup-label {
	display: flex;
	align-items: center;
	gap: var(--pp-space-3);
	font-size: var(--pp-text-2xs);
	font-weight: 600;
	line-height: 1.45;
	letter-spacing: 0.09em;
	text-transform: uppercase;
	/* A step darker than the muted ink: the eyebrow sits on a tint rather than
	   on white, and this keeps it above 4.5:1 on the strongest of them. */
	color: #5b6470;
	margin-bottom: 2px;
}

.popup-product-link {
	font-weight: 600;
	color: var(--pp-accent);
}

/* Bootstrap's muted is close to the ramp but not on it; every muted fact in the
   popup lands on the same step. */
.map-popup .text-muted {
	color: var(--pp-ink-3) !important;
}

.map-popup a {
	color: var(--pp-accent);
}

.map-overlay {
	position: absolute;
	inset: 0;
	z-index: 15;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: rgba(255, 255, 255, 0.55);
	/* The loading/empty state covers the whole map but has nothing to click:
	   let drag/scroll zoom reach the map underneath it. */
	pointer-events: none;
}
</style>

<template>
	<FullLayout>
		<!-- The ledger's toggle: the one control the topbar carried that the map
		     cannot do without. The bar itself is gone — the page's name is the
		     browser tab's, and the browser's own back reaches the dashboard — so
		     the Ledger button stands alone in the corner the topbar's actions
		     were in, above the right rail's column.

		     It is declared before the map, as the topbar was, so a drawer dragged
		     to cover the map covers it too: the drawer's own grab bar is the way
		     back down from there, and the toggle must not sit over its controls. -->
		<button
			type="button"
			class="ledger-toggle"
			:class="{ active: store.drawerOpen }"
			:aria-expanded="store.drawerOpen"
			aria-controls="map-review-ledger"
			aria-label="Ledger"
			title="Ledger"
			@click="toggleDrawer"
		>
			<i class="bi bi-table"></i>
		</button>

		<div class="map-review-map">
			<mgl-map
				:map-style="mapStyleURL(ALIDADE_SMOOTH)"
				:center="initialCenter"
				:zoom="5"
				@map:load="onMapLoad"
				@map:moveend="onMapMove"
			>
				<!-- The district's site fabric, the map's base layer. The
				     org-scoped `site` tegola map serves every Site in
				     the district — parcel, street (right-of-way) and other — so
				     space with no Feature or Treatment still draws, and the
				     streets between parcels are part of the fabric. The
				     selection fill/outline redraws the one Site the popup
				     describes above the base.

				     The fill and the outline are painted from the assignment
				     expression: a Site the district has assigned carries the
				     technician's own hue (see assignedSiteColor), and one with
				     no assignment keeps the flat neutral of the fabric, so the
				     plan of who is working where reads off the map itself. -->
				<SourceTegola
					id="map-review-sites"
					map-name="site"
					:params="siteParams"
				>
					<mgl-fill-layer
						layer-id="map-review-sites-fill"
						source-layer="service-area-site"
						:minzoom="SITE_MIN_ZOOM"
						:layout="{
							visibility: layerVisibility.sites ? 'visible' : 'none',
						}"
						:paint="siteFillPaint"
					/>
					<mgl-line-layer
						layer-id="map-review-sites-outline"
						source-layer="service-area-site"
						:minzoom="SITE_MIN_ZOOM"
						:layout="{
							visibility: layerVisibility.sites ? 'visible' : 'none',
						}"
						:paint="siteOutlinePaint"
					/>
					<mgl-fill-layer
						layer-id="map-review-sites-selected-fill"
						source-layer="service-area-site"
						:minzoom="SITE_MIN_ZOOM"
						:layout="{
							visibility: layerVisibility.sites ? 'visible' : 'none',
						}"
						:filter="selectedSiteFilter"
						:paint="{
							'fill-color': SITE_COLOR,
							'fill-opacity': 0.35,
						}"
					/>
					<mgl-line-layer
						layer-id="map-review-sites-selected-outline"
						source-layer="service-area-site"
						:minzoom="SITE_MIN_ZOOM"
						:layout="{
							visibility: layerVisibility.sites ? 'visible' : 'none',
						}"
						:filter="selectedSiteFilter"
						:paint="{
							'line-color': SITE_COLOR,
							'line-width': 2,
						}"
					/>
				</SourceTegola>

				<!-- The district's own Zones: the named units it divides its ground
				     by, served as an org-scoped vector tile layer and painted from
				     the `zone` tile map — the district's units are a handful, but
				     the map no longer downloads their geometry as one GeoJSON
				     FeatureCollection, and the tile carries each unit's active
				     version's extent (the zone's own id is the MVT feature id).
				     The layer is re-requested after any zone write (see
				     zoneParams). The units sit ABOVE the site fabric and under
				     every record layer: the fabric stays the map's base, a zone is
				     the district's own overlay on it, and no record dot is hidden
				     by a fill. The dashes are what say a district drew this unit
				     rather than the fabric. -->
				<SourceTegola
					id="map-review-zone-tiles"
					map-name="zone"
					:params="zoneParams"
				>
					<mgl-fill-layer
						layer-id="map-review-zones-fill"
						source-layer="service-area-zone"
						:layout="{
							visibility: layerVisibility.zones ? 'visible' : 'none',
						}"
						:paint="{
							'fill-color': ZONE_COLOR,
							'fill-opacity': 0.04,
						}"
					/>
					<mgl-line-layer
						layer-id="map-review-zones-outline"
						source-layer="service-area-zone"
						:layout="{
							visibility: layerVisibility.zones ? 'visible' : 'none',
						}"
						:paint="{
							'line-color': ZONE_COLOR,
							'line-width': 1,
							'line-opacity': 0.7,
							'line-dasharray': [3, 2],
						}"
					/>
					<!-- The zones the shared spec selects — the ids the resolver
					     narrows the grid, the counts and every ledger by — redrawn
					     above the rest, so a unit that is narrowing the work reads
					     as selected rather than as one more outline. -->
					<mgl-fill-layer
						layer-id="map-review-zones-selected-fill"
						source-layer="service-area-zone"
						:layout="{
							visibility: layerVisibility.zones ? 'visible' : 'none',
						}"
						:filter="selectedZoneFilter"
						:paint="{
							'fill-color': ZONE_COLOR,
							'fill-opacity': 0.18,
						}"
					/>
					<mgl-line-layer
						layer-id="map-review-zones-selected-outline"
						source-layer="service-area-zone"
						:layout="{
							visibility: layerVisibility.zones ? 'visible' : 'none',
						}"
						:filter="selectedZoneFilter"
						:paint="{
							'line-color': ZONE_COLOR,
							'line-width': 2.5,
						}"
					/>
				</SourceTegola>

				<!-- The population view's bivariate cells, when the operator has
				     sent them to the map. The payload's cells are already
				     dissolved to the service area and carry closed rings, so
				     nothing is clipped or rebuilt here; the fill is the
				     contract's nine-class palette over each feature's `class`,
				     and the key that explains it lives in the Analysis rail
				     beside the map it was read from. It sits above the standing
				     zones and under the record layers, so the traps, the
				     Features and the selected record stay readable over it. -->
				<mgl-geo-json-source
					v-if="store.populationOnMap && store.populationVisible"
					source-id="map-review-population"
					:data="populationFC"
				>
					<mgl-fill-layer
						layer-id="map-review-population-fill"
						:paint="populationFillPaint"
					/>
				</mgl-geo-json-source>

				<!-- The waste view's bivariate cells, when the operator has sent
				     them to the map. The payload is the filtered read, so a
				     filter change repaints this layer from the new cells — and
				     while that read is in flight the source is empty rather than
				     the previous filter's fill. Thin cells arrive classless and
				     draw pale, never coloured. It sits beside the population
				     layer, above the standing zones and under the records. -->
				<mgl-geo-json-source
					v-if="store.wasteOnMap && store.wasteVisible"
					source-id="map-review-waste"
					:data="wasteFC"
				>
					<mgl-fill-layer
						layer-id="map-review-waste-fill"
						:paint="wasteFillPaint"
					/>
				</mgl-geo-json-source>

				<!-- The temporary zones: the buffers materialised from a
				     surface's kept rows, held inline in the shared spec and
				     drawn from that geometry alone — there are no tiles to ask
				     for, and nothing here narrows the source, because the spec
				     is what narrows the ledger. They sit above the standing
				     zones and under every record layer, and they draw as an
				     outline only: a buffer is a boundary the ledger computed,
				     transient by nature, and a fill would read as ground the
				     district claims. The dashes are what say computed rather
				     than drawn, and the shorter, tighter rhythm keeps them
				     from reading as one more standing unit. -->
				<mgl-geo-json-source
					source-id="map-review-temp-zones"
					:data="tempZonesFC"
				>
					<mgl-line-layer
						layer-id="map-review-temp-zones-outline"
						:layout="{
							visibility: layerVisibility.tempZones ? 'visible' : 'none',
						}"
						:paint="{
							'line-color': TEMP_ZONE_COLOR,
							'line-width': 1.5,
							'line-opacity': 0.9,
							'line-dasharray': [2, 2],
						}"
					/>
				</mgl-geo-json-source>

				<!-- Features, served as an org-scoped vector tile layer like the
				     treatments. The map no longer reads Feature points through
				     the Site list hydrate (GET /api/site): the tile carries each
				     Feature's location plus the effective control-window end and
				     the active manual revisit as epoch milliseconds, so the
				     points paint immediately and the legend counts come from the
				     server. One layer draws the points the shared filter spec
				     selects — the server's resolved selection, never a second
				     filter implementation here — narrowed by the control pane's
				     visibility rows. An exact selection no larger than the
				     shipping cap arrives as ids; a larger one rides as an opaque
				     selection token the tile applies, so the layer carries no
				     membership filter at all. A point the filter excludes is not
				     drawn at all: there is deliberately no dim underlay, so a
				     filtered-out place cannot linger faintly over the ones that
				     match. The colour is the point's band, computed from the
				     tile's window_end_ms against now and the due-soon horizon,
				     and is the band's only cue; the Feature ledger's
				     control_state filter narrows the table and the map together. -->
				<SourceTegola
					id="map-review-feature-tiles"
					map-name="feature"
					:params="featureParams"
				>
					<mgl-circle-layer
						layer-id="map-review-features-highlight"
						source-layer="service-area-feature"
						:filter="featureHighlightFilterComposed"
						:paint="{
							'circle-radius': 5,
							'circle-color': featureBandFillExpr,
							'circle-stroke-color': '#ffffff',
							'circle-stroke-width': 1.5,
						}"
					/>
					<!-- Feature points the server pinned with an active manual
					     revisit date, drawn above the state points as a thin
					     coloured ring: a vivid violet no band fill uses, so a
					     pin reads even when the underlying point is filtered
					     out. It is a whole-layer toggle of its own (the "Pinned
					     revisit" row), independent of the band filters; its violet
					     hue no band fill uses. The tile carries the pin as
					     `revisit_ms`, present only while the plan is active. -->
					<mgl-circle-layer
						layer-id="map-review-features-pinned"
						source-layer="service-area-feature"
						:layout="{
							visibility: layerVisibility.pinned ? 'visible' : 'none',
						}"
						:filter="pinnedFilter"
						:paint="{
							'circle-radius': 9,
							'circle-color': 'rgba(0,0,0,0)',
							'circle-stroke-color': PINNED_RING_COLOR,
							'circle-stroke-width': 1.5,
						}"
					/>
					<!-- The Feature the table row or the popup selects, drawn
					     above the Feature layer so the selection has an
					     identifiable anchor even when the shared filter would
					     not draw it. -->
					<mgl-circle-layer
						layer-id="map-review-point-selected"
						source-layer="service-area-feature"
						:layout="{
							visibility: layerVisibility.features ? 'visible' : 'none',
						}"
						:filter="selectedFeatureFilter"
						:paint="{
							'circle-radius': 9,
							'circle-color': '#ffffff',
							'circle-stroke-color': FEATURE_COLOR,
							'circle-stroke-width': 3,
						}"
					/>
				</SourceTegola>

				<!-- The district's treatments, served as an org-scoped vector
				     tile layer so the filters cover all ~116k rows, not a
				     client-side page. One layer draws the ones the shared spec
				     keeps; a treatment the filter excludes is not drawn at all,
				     with no dim underlay left over. The over-rate view is the
				     Treatment ledger's applied_rate filter, which narrows this
				     layer through the shared spec; there is no separate over-rate
				     layer here. -->
				<SourceTegola
					id="map-review-treatment-tiles"
					map-name="treatment"
					:params="treatmentParams"
				>
					<mgl-circle-layer
						layer-id="map-review-treatments-highlight"
						source-layer="service-area-treatment"
						:layout="{
							visibility: layerVisibility.treatments ? 'visible' : 'none',
						}"
						:filter="treatmentMapFilter"
						:paint="{
							'circle-radius': 5,
							'circle-color': TREATMENT_COLOR,
							'circle-stroke-color': '#ffffff',
							'circle-stroke-width': 1.5,
						}"
					/>
					<!-- The treatment the popup describes, drawn above the filter
					     result so the selected point reads even when the shared
					     filter would not draw it. -->
					<mgl-circle-layer
						layer-id="map-review-treatments-selected"
						source-layer="service-area-treatment"
						:layout="{
							visibility: layerVisibility.treatments ? 'visible' : 'none',
						}"
						:filter="selectedTreatmentFilter"
						:paint="{
							'circle-radius': 9,
							'circle-color': '#ffffff',
							'circle-stroke-color': TREATMENT_COLOR,
							'circle-stroke-width': 3,
						}"
					/>
				</SourceTegola>

				<!-- The district's observations, served as an org-scoped vector
				     tile layer like the treatments. The tile carries exactly the
				     set the shared spec resolves to — the server applies the
				     filter, shipping a selection token in the URL instead of an
				     id set — so this layer has no filter of its own and a feature
				     the spec excludes is never in the tile. An unknown or expired
				     token draws empty, never stale.
				     One row per FEATURE the selected observations sit at, at
				     every zoom (an observation is a record, not a place: its
				     Feature is where it plots, and a Feature carries many of
				     them). The fill carries that Feature's most recent selected
				     observation's production reading, so the layer says whether
				     production was found — the reading that was missing before. -->
				<SourceTegola
					id="map-review-observation-tiles"
					map-name="observation"
					:params="observationParams"
				>
					<mgl-circle-layer
						layer-id="map-review-observations"
						source-layer="service-area-observation"
						:layout="{
							visibility: layerVisibility.observations ? 'visible' : 'none',
						}"
						:paint="{
							'circle-radius': 5,
							'circle-color': observationColorExpr,
							'circle-stroke-color': '#ffffff',
							'circle-stroke-width': 1.5,
						}"
					/>
				</SourceTegola>

				<!-- The district's standing trapping network: one dot per kept
				     Trap Station, at the station's ACTIVE recorded location. The
				     split from the off-station layer is where a trap result is
				     drawn, not a judgement about it — an instance that has a
				     station is read through its station, so no result is drawn
				     twice. No magnitude is encoded: the dots say where, never
				     how much. -->
				<SourceTegola
					id="map-review-trap-station-tiles"
					map-name="trap-station"
					:params="trapStationParams"
				>
					<mgl-circle-layer
						layer-id="map-review-trap-stations"
						source-layer="service-area-trap-station"
						:filter="trapStationMapFilter"
						:layout="{
							visibility:
								layerVisibility.traps && layerVisibility.trapStations
									? 'visible'
									: 'none',
						}"
						:paint="{
							'circle-radius': 5,
							'circle-color': TRAP_STATION_COLOR,
							'circle-stroke-color': '#ffffff',
							'circle-stroke-width': 1.5,
						}"
					/>
				</SourceTegola>

				<!-- The off-station Trap Instances: one dot per kept instance with
				     no station, at the coordinates it captured on its set. An
				     off-station instance is normal — not an error and not a
				     backlog — and it is drawn here because there is no station to
				     read it through. -->
				<SourceTegola
					id="map-review-trap-instance-tiles"
					map-name="trap-instance"
					:params="trapInstanceParams"
				>
					<mgl-circle-layer
						layer-id="map-review-trap-instances"
						source-layer="service-area-trap-instance-offstation"
						:filter="trapInstanceMapFilter"
						:layout="{
							visibility:
								layerVisibility.traps && layerVisibility.trapInstances
									? 'visible'
									: 'none',
						}"
						:paint="{
							'circle-radius': 5,
							'circle-color': TRAP_INSTANCE_COLOR,
							'circle-stroke-color': '#ffffff',
							'circle-stroke-width': 1.5,
						}"
					/>
				</SourceTegola>

				<!-- The district's SAVED H3 layers, each as its own source and its
				     own fill (and, for a three-variable layer, its own dissolve
				     outline) rather than one shared grid: a saved layer is a
				     layer, so it toggles, sits in the stack and repaints
				     independently of every other one. Nothing here overwrites
				     the fabric or the record layers above and below it —
				     MapLibre draws in declaration order, and this block sits
				     between the trap layers and the selection halo.

				     One source per ladder rung: the resolution is a tile
				     parameter, so two rungs are two URLs and MapLibre asks only
				     for the band the camera is in (see h3Rungs). The count of
				     variables IS the encoding — one variable is the lab's
				     sequential ramp, two the 3×3 bivariate, three that fill plus
				     the third variable's dissolved contour — and all three are
				     the lab's own painters over the lab's own series tile. -->
				<template v-for="entry in h3Render" :key="entry.id">
					<template
						v-for="rung in entry.rungs"
						:key="`${entry.id}-${rung.res}`"
					>
						<mgl-vector-source
							:source-id="`h3-${entry.id}-${rung.res}`"
							:tiles="rung.tiles"
						>
							<mgl-fill-layer
								:layer-id="`h3-${entry.id}-${rung.res}-fill`"
								source-layer="service-area-h3-series"
								:minzoom="rung.min"
								:maxzoom="rung.max"
								:paint="entry.fillPaint"
							/>
							<!-- The cell's own edge, so a blob reads as many cells
							     and not one. -->
							<mgl-line-layer
								:layer-id="`h3-${entry.id}-${rung.res}-edge`"
								source-layer="service-area-h3-series"
								:minzoom="rung.min"
								:maxzoom="rung.max"
								:paint="entry.edgePaint"
							/>
						</mgl-vector-source>
						<!-- The third variable, dissolved server-side into the
						     boundary of the class's union, above the fill and
						     below the halo. -->
						<mgl-vector-source
							v-if="rung.outlineTiles && entry.outlinePaint"
							:source-id="`h3-${entry.id}-outline-${rung.res}`"
							:tiles="rung.outlineTiles"
						>
							<mgl-line-layer
								:layer-id="`h3-${entry.id}-outline-${rung.res}`"
								source-layer="service-area-h3-outline-multi"
								:minzoom="rung.min"
								:maxzoom="rung.max"
								:paint="entry.outlinePaint"
							/>
						</mgl-vector-source>
					</template>
				</template>

				<!-- The selected record's halo. The polygon fill above shows the
				     Site the record belongs to, which is a large shape and does
				     not say WHICH point was picked; this marks the record's own
				     anchor with a green glow and a ring, above every data layer
				     so nothing can hide it, and holds at most one point. A glow
				     rather than a filled dot because the treatment layer is
				     already green. -->
				<mgl-geo-json-source
					source-id="map-review-selected-halo"
					:data="selectedHaloFC"
				>
					<mgl-circle-layer
						layer-id="map-review-selected-halo-far"
						:paint="{
							'circle-radius': 22,
							'circle-color': SELECTED_HALO_GLOW_FAR,
							'circle-blur': 1,
						}"
					/>
					<mgl-circle-layer
						layer-id="map-review-selected-halo-near"
						:paint="{
							'circle-radius': 13,
							'circle-color': SELECTED_HALO_GLOW,
							'circle-blur': 0.6,
						}"
					/>
					<mgl-circle-layer
						layer-id="map-review-selected-halo-ring"
						:paint="{
							'circle-radius': 9,
							'circle-color': 'rgba(0,0,0,0)',
							'circle-stroke-color': SELECTED_HALO_COLOR,
							'circle-stroke-width': 2.5,
						}"
					/>
					<mgl-circle-layer
						layer-id="map-review-selected-halo-core"
						:paint="{
							'circle-radius': 3.5,
							'circle-color': SELECTED_HALO_COLOR,
							'circle-stroke-color': '#ffffff',
							'circle-stroke-width': 1.5,
						}"
					/>
				</mgl-geo-json-source>

				<!-- The address-search pin: a result that carries no Site has no
				     polygon to highlight, so the flown-to point is marked
				     directly. It draws above every data layer and holds at most
				     one point. -->
				<mgl-geo-json-source
					source-id="map-review-search-pin"
					:data="searchPinFC"
				>
					<mgl-circle-layer
						layer-id="map-review-search-pin-dot"
						:paint="{
							'circle-radius': 8,
							'circle-color': SEARCH_PIN_COLOR,
							'circle-stroke-color': '#ffffff',
							'circle-stroke-width': 2,
						}"
					/>
				</mgl-geo-json-source>
			</mgl-map>

			<!-- The draw-mode selector: the map's own control, over the map, and
			     only while a draw is armed. The mode is shared state (the store's
			     `drawMode`) because it is a capability of drawing itself rather
			     than of one tool — whichever tool is armed draws the way this
			     says. The three modes are the shape maths in @/map/drawModes;
			     the radius field belongs to the circle alone and is stated in
			     the district's own unit. -->
			<div
				v-if="zoneArmed"
				class="draw-mode-bar"
				role="group"
				aria-label="Drawing mode"
			>
				<span class="draw-mode-title">Draw</span>
				<button
					v-for="m in DRAW_MODES"
					:key="m"
					type="button"
					class="draw-mode-btn"
					:class="{ active: store.drawMode === m }"
					:aria-pressed="store.drawMode === m"
					:title="DRAW_MODE_TITLES[m]"
					@click="store.setDrawMode(m)"
				>
					{{ DRAW_MODE_LABELS[m] }}
				</button>
				<template v-if="store.drawMode === 'circle'">
					<input
						v-model.number="drawRadius"
						class="draw-mode-radius"
						type="number"
						min="0"
						step="any"
						aria-label="Circle radius"
						title="The radius a click draws; a drag sizes its own"
					/>
					<select
						v-model="drawRadiusUnit"
						class="draw-mode-unit"
						aria-label="Circle radius unit"
						title="The unit the radius is stated in: metres, kilometres, feet or miles"
					>
						<option
							v-for="unit in DRAW_RADIUS_UNITS"
							:key="unit.value"
							:value="unit.value"
						>
							{{ unit.label }}
						</option>
					</select>
				</template>
				<span v-else-if="store.drawMode === 'polygon'" class="draw-mode-hint">
					Click each point · double-click or Enter to close · Backspace undoes
				</span>
				<span v-else class="draw-mode-hint">Drag to draw</span>
			</div>

			<!-- The details, analysis and ask panels: the page's three left
			     panels, in the shared tool rail
			     (ts/components/panel/ToolRail.vue) the Reveal workbench puts its
			     panels on. The details are what the map or the ledger selected;
			     the analysis is the report, one question at a time; the ask is
			     the plain-English way to set filters or narrow the records. What
			     the map is read BY — the search box, the layer rows, the filters
			     and the tools — is not here: it reads the map, so it is the panes
			     off the map's right edge. The shaping tools are not here either:
			     they live in the rail's Tools pane.

			     This column starts at the top of the window: nothing sits above
			     it since the topbar went, and the map's own controls are at the
			     top centre and the top right. The right rail keeps its own 60px
			     start, which is the ledger's toggle above it. -->
			<ToolRail
				ref="detailRail"
				side="left"
				v-model="leftTab"
				:tabs="[
					{ key: 'details', icon: 'bi bi-info-circle', title: 'Details' },
					{
						key: 'analysis',
						icon: 'bi bi-bar-chart-line',
						title: 'Analysis',
						bodyWidth: '420px',
					},
					{
						key: 'ask',
						icon: 'bi bi-stars',
						title: 'Ask — set filters, choose a table, or narrow the records',
					},
				]"
				topOffset="12px"
				:raised="store.drawerExpanded"
				:bottom-offset="
					store.drawerExpanded ? '12px' : railBottomOffset('12px', 12)
				"
			>
				<!-- The selection drawer. With one record selected it shows the
			     node graph — a Site, a Feature, an Observation, a Treatment or a
			     Contact — with links and a Back control. With two or more ledger
			     rows ticked it becomes the selection summary and the actions that
			     type supports, so the write and the count it covers are read in
			     one place rather than assembled from separate toolbar controls.

			     The body is chunked into three to five named groups. Inside a
			     group the facts are a ruled table: one label column, sized to
			     the popup's own longest label so no label ever wraps, a small
			     gutter, and a faint hairline between rows binding each label to
			     the value beside it. Groups are separated by space and their
			     eyebrow alone — no rule is drawn around a group as well. A list
			     long enough to push the record past a screenful folds behind one
			     click from its own eyebrow, with its count; no field is ever
			     folded away. -->
				<template #details>
					<div class="detail-pane">
						<SelectionActions v-if="store.ledgerSelectionCount > 1" />
						<div v-else-if="nav.length > 0" ref="popupEl" class="map-popup">
							<div class="map-popup-head">
								<button
									v-if="nav.length > 1"
									type="button"
									class="map-popup-back"
									aria-label="Back"
									@click="goBack"
								>
									‹ Back
								</button>
								<button
									type="button"
									class="map-popup-close"
									aria-label="Close"
									@click="clearSelection"
								>
									×
								</button>
							</div>

							<div v-if="popupLoading" class="map-popup-note">
								<span
									class="spinner-border spinner-border-sm me-2"
									role="status"
								></span>
								Loading…
							</div>
							<div v-else-if="popupError" class="map-popup-note text-danger">
								{{ popupError }}
							</div>

							<!-- Site: its name (label, else address, else id) in the title, what
				     kind of fabric it is and how many Features it holds in the
				     subtitle, then where it is and who owns it, its people, what it
				     holds, what has happened there, and its own note. -->
							<template v-else-if="current?.kind === 'site' && popupSite">
								<div class="map-popup-title">{{ popupSiteTitle }}</div>
								<div class="map-popup-subtitle">
									{{ siteKindLabel(popupSite.kind) }} ·
									{{ popupSite.features?.length ?? 0 }} features
								</div>

								<!-- WHERE. The title is the address whenever the site has no
					     name of its own, so the address is printed once: Where
					     carries it only when the title is something else, and
					     otherwise starts at what it adds — the owner. -->
								<div
									v-if="siteWhereRows.length > 0"
									class="popup-section popup-section--place"
								>
									<div class="popup-label">Where</div>
									<div
										v-for="row in siteWhereRows"
										:key="row.label"
										class="popup-kv"
									>
										<span class="popup-kv-key">{{ row.label }}</span>
										<span>{{ row.value }}</span>
									</div>
								</div>

								<!-- CONTACTS: the site's people, one row per person however many
					     roles the site links them by, each with the source that put
					     it here. The skip-trace action sits with the people it
					     fills in, so the lookup's result lands in view. -->
								<div class="popup-section popup-section--people">
									<div class="popup-label">
										Contacts ({{ siteContactRows.length }})
									</div>
									<div
										v-for="person in siteContactRows"
										:key="person.contact_id"
										class="popup-person"
									>
										<span class="popup-person-main">
											<button
												type="button"
												class="popup-link"
												@click="openContactFromSite(person)"
											>
												{{ person.name || `Contact #${person.contact_id}` }}
											</button>
											<span
												v-for="role in person.roles"
												:key="role.role"
												class="popup-role-chip"
												:class="{
													'popup-role-chip--primary': role.is_primary,
												}"
											>
												{{ siteContactRoleLabel(role.role) }}
											</span>
										</span>
										<span class="popup-person-sources">
											<span
												v-for="source in person.sources"
												:key="source"
												class="popup-source-chip"
												:class="`popup-source-chip--${source}`"
											>
												{{ sourceLabel(source) }}
											</span>
										</span>
									</div>
									<div v-if="siteContactRows.length === 0" class="popup-empty">
										No contacts recorded.
									</div>
									<button
										type="button"
										class="popup-action-btn"
										:disabled="skipTraceBusy || skipTraceUnconfigured"
										:title="skipTraceUnconfiguredTitle"
										@click="runSkipTrace"
									>
										{{ skipTraceBusy ? "Tracing…" : "Skip trace" }}
									</button>
								</div>

								<!-- FEATURES: what is on the site. The roll-up is the site's own
					     distribution over habitat classes and the list is the way
					     in; a site with more Features than a screenful shows the
					     first few and folds the rest behind its count. -->
								<div class="popup-section popup-section--features">
									<div class="popup-label">
										Features ({{ popupSite.features?.length ?? 0 }})
										<button
											v-if="(popupSite.features?.length ?? 0) > POPUP_FOLD_AT"
											type="button"
											class="popup-fold"
											@click="toggleList('site-features')"
										>
											{{
												listOpen("site-features")
													? "Show fewer"
													: `Show all ${popupSite.features?.length ?? 0}`
											}}
										</button>
									</div>
									<div v-if="siteFeatureRollup" class="popup-rollup">
										{{ siteFeatureRollup }}
									</div>
									<div class="popup-list">
										<div
											v-for="feature in siteVisibleFeatures"
											:key="feature.id"
											class="popup-link-row"
										>
											<button
												type="button"
												class="popup-link"
												@click="openFeatureFromSite(feature)"
											>
												Feature #{{ feature.id }}
											</button>
											<span class="text-muted">{{
												featureTypeLabel(feature.type)
											}}</span>
										</div>
									</div>
									<div
										v-if="(popupSite.features?.length ?? 0) === 0"
										class="popup-empty"
									>
										No features in this site.
									</div>
								</div>

								<!-- ACTIVITY: rolled up through the site's Features. -->
								<div class="popup-section popup-section--activity">
									<div class="popup-label">Activity</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Observations</span>
										<span class="popup-num">{{
											popupSite.activity?.observations ?? 0
										}}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Treatments</span>
										<span class="popup-num">{{
											popupSite.activity?.treatments ?? 0
										}}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Observed</span>
										<span v-if="popupSite.activity?.last_observed_at">
											<LocalizedTime
												:datetime="popupSite.activity.last_observed_at"
											/>
											<template v-if="popupSite.activity.last_observed">
												·
												{{
													INSPECTION_READING_LABELS[
														popupSite.activity.last_observed
													] ?? popupSite.activity.last_observed
												}}
											</template>
										</span>
										<span v-else class="text-muted">—</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Treated</span>
										<span v-if="popupSite.activity?.last_treated_at">
											<LocalizedTime
												:datetime="popupSite.activity.last_treated_at"
											/>
											· {{ popupSite.activity.last_product || "—" }}
										</span>
										<span v-else class="text-muted">—</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Suppress until</span>
										<span v-if="popupSiteSuppressionEnd">
											<LocalizedTime :datetime="popupSiteSuppressionEnd" />
										</span>
										<span v-else class="text-muted">—</span>
									</div>
								</div>

								<!-- NOTE: the site's own note, where it has one. -->
								<div
									v-if="popupSite.notes"
									class="popup-section popup-section--activity-alt"
								>
									<div class="popup-label">Note</div>
									<div class="map-popup-note">{{ popupSite.notes }}</div>
								</div>
							</template>

							<!-- Feature: what it is (every dimension with a value, and the ones
				     with none named in one line), its production state, its
				     location and parent Site, and every observation and treatment
				     recorded on it. -->
							<template v-else-if="current?.kind === 'feature' && popupFeature">
								<div class="map-popup-title">
									Feature #{{ popupFeature.id }}
								</div>
								<div class="map-popup-subtitle">
									{{ featureTypeLabel(popupFeature.type) }}
								</div>

								<!-- The absence an Observation asserted: a banner, above
					     everything, naming and linking the Observation. -->
								<div
									v-if="popupFeature.inactive_reason"
									class="popup-banner popup-banner--inactive"
								>
									<div class="popup-banner-title">
										Inactive — {{ popupFeature.inactive_reason }}
									</div>
									<div>
										An observation found this feature absent.
										<template
											v-if="popupFeature.inactive_observation_id != null"
										>
											<button
												type="button"
												class="popup-link"
												@click="
													openConditionObservation(
														popupFeature.inactive_observation_id,
													)
												"
											>
												Observation #{{ popupFeature.inactive_observation_id }}
											</button>
										</template>
									</div>
								</div>

								<div class="popup-section popup-section--place">
									<div class="popup-label">Where</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Location</span>
										<span>{{ popupFeatureLocation }}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Site</span>
										<button
											type="button"
											class="popup-link"
											@click="openSiteFromFeature(popupFeature.site_id)"
										>
											Site #{{ popupFeature.site_id }}
										</button>
									</div>
								</div>

								<!-- WHAT IT IS: the dimensions that carry a value, each with the
					     Observation that set it, then the ones with no value at all
					     named in a single line rather than five blank rows. A blank
					     dimension is not a fact; which dimensions are blank is. -->
								<div class="popup-section popup-section--features">
									<div class="popup-label">What it is</div>
									<div
										v-for="characteristic in popupFeatureValued"
										:key="characteristic.dimension"
										class="popup-kv"
									>
										<span class="popup-kv-key">
											{{ dimensionLabel(characteristic.dimension) }}
										</span>
										<span class="popup-cell">
											<span>{{
												dimensionValueLabel(
													characteristic.dimension,
													characteristic.value,
												)
											}}</span>
											<span
												v-if="
													characteristic.observation_id != null &&
													characteristic.observed_at
												"
												class="popup-asof"
											>
												as of
												<LocalizedTime :datetime="characteristic.observed_at" />
												·
												<button
													type="button"
													class="popup-link"
													@click="
														openConditionObservation(
															characteristic.observation_id,
														)
													"
												>
													Observation #{{ characteristic.observation_id }}
												</button>
											</span>
										</span>
									</div>
									<div
										v-if="popupFeatureValued.length === 0"
										class="popup-empty"
									>
										No dimension recorded.
									</div>
									<div v-if="popupFeatureUnrecorded" class="popup-rollup">
										Not recorded: {{ popupFeatureUnrecorded }}
									</div>
								</div>

								<div class="popup-section popup-section--features-alt">
									<div class="popup-label">State</div>
									<template v-if="popupFeature.production_state">
										<div class="popup-kv">
											<span class="popup-kv-key">Production</span>
											<span class="popup-cell">
												<span>{{
													productionStateLabel(
														popupFeature.production_state.state,
													)
												}}</span>
												<span class="popup-asof">
													as of
													<LocalizedTime
														:datetime="
															popupFeature.production_state.observed_at
														"
													/>
													<template
														v-if="
															popupFeature.production_state.observation_id !=
															null
														"
													>
														·
														<button
															type="button"
															class="popup-link"
															@click="
																openConditionObservation(
																	popupFeature.production_state.observation_id,
																)
															"
														>
															Observation #{{
																popupFeature.production_state.observation_id
															}}
														</button>
													</template>
												</span>
											</span>
										</div>
										<div
											v-if="popupFeature.production_state.reason"
											class="text-muted"
										>
											{{ popupFeature.production_state.reason }}
										</div>
									</template>
									<div v-else class="text-muted">—</div>
								</div>

								<div class="popup-section popup-section--observations">
									<div class="popup-label">
										Observations ({{ popupFeature.observations?.length ?? 0 }})
										<button
											v-if="
												(popupFeature.observations?.length ?? 0) > POPUP_FOLD_AT
											"
											type="button"
											class="popup-fold"
											@click="toggleList('feature-observations')"
										>
											{{
												listOpen("feature-observations")
													? "Show fewer"
													: `Show all ${popupFeature.observations?.length ?? 0}`
											}}
										</button>
									</div>
									<div class="popup-list">
										<div
											v-for="observation in popupFeatureVisibleObservations"
											:key="observation.id"
											class="popup-link-row"
										>
											<button
												type="button"
												class="popup-link"
												:title="observation.observed_at"
												@click="openObservationFromFeature(observation)"
											>
												{{ compactStamp(observation.observed_at) }}
											</button>
											<span>{{
												INSPECTION_READING_LABELS[observation.inspection] ??
												observation.inspection
											}}</span>
										</div>
									</div>
									<div
										v-if="(popupFeature.observations?.length ?? 0) === 0"
										class="popup-empty"
									>
										No observations recorded.
									</div>
								</div>

								<div class="popup-section popup-section--treatments">
									<div class="popup-label">
										Treatments ({{ popupFeature.treatments?.length ?? 0 }})
										<button
											v-if="
												(popupFeature.treatments?.length ?? 0) > POPUP_FOLD_AT
											"
											type="button"
											class="popup-fold"
											@click="toggleList('feature-treatments')"
										>
											{{
												listOpen("feature-treatments")
													? "Show fewer"
													: `Show all ${popupFeature.treatments?.length ?? 0}`
											}}
										</button>
									</div>
									<div class="popup-list">
										<div
											v-for="treatment in popupFeatureVisibleTreatments"
											:key="treatment.id"
											class="popup-link-row"
										>
											<button
												type="button"
												class="popup-link"
												:title="treatment.start_datetime"
												@click="openTreatmentFromFeature(treatment.id)"
											>
												{{ compactStamp(treatment.start_datetime) }}
											</button>
											<span>{{ treatment.product_name || "—" }}</span>
										</div>
									</div>
									<div
										v-if="(popupFeature.treatments?.length ?? 0) === 0"
										class="popup-empty"
									>
										No treatments recorded.
									</div>
								</div>
							</template>

							<!-- Treatment: the recorded facts — when it ran, what was put down
				     and how much, who applied it, and where — with Place linking to
				     the Feature and Site it was applied to. -->
							<template
								v-else-if="current?.kind === 'treatment' && popupTreatment"
							>
								<div class="map-popup-title">
									Treatment #{{ popupTreatment.id }}
								</div>

								<div class="popup-section popup-section--treatments">
									<div class="popup-label">Time</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Start</span>
										<span>
											<LocalizedTime
												:datetime="popupTreatment.start_datetime"
											/>
										</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">End</span>
										<span>
											<LocalizedTime
												v-if="popupTreatment.end_datetime"
												:datetime="popupTreatment.end_datetime"
											/>
											<span v-else>—</span>
										</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Duration</span>
										<span>
											{{
												formatDuration(
													durationSeconds(
														popupTreatment.start_datetime,
														popupTreatment.end_datetime,
													),
												)
											}}
										</span>
									</div>
								</div>

								<!-- APPLICATION: the product, how much of it, and over what. -->
								<div class="popup-section popup-section--treatments-alt">
									<div class="popup-label">Application</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Product</span>
										<span>
											<RouterLink
												v-if="popupTreatment.product_id != null"
												:to="{
													name: 'Inventory Product Detail',
													params: { id: popupTreatment.product_id },
												}"
												class="popup-product-link"
												title="View your district's product details"
											>
												{{ popupTreatment.product_name || "Product" }}
											</RouterLink>
											<span v-else>{{
												popupTreatment.product_name || "—"
											}}</span>
										</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Amount</span>
										<span>{{ formatAmount(popupTreatment) }}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Area</span>
										<span>{{ formatArea(popupTreatment) }}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Geometry</span>
										<span>{{ geometryLabel(popupTreatment) }}</span>
									</div>
								</div>

								<div class="popup-section popup-section--people">
									<div class="popup-label">Applicator</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Name</span>
										<span>{{ popupTreatment.creator_name || "—" }}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Cert.</span>
										<span v-if="popupTreatment.certification_number">
											{{ popupTreatment.certification_number }}
										</span>
										<span v-else class="text-muted">—</span>
									</div>
									<a
										v-if="popupTreatment.certification_uri"
										:href="popupTreatment.certification_uri"
										target="_blank"
										rel="noopener"
									>
										View certificate
									</a>
								</div>

								<div class="popup-section popup-section--place-alt">
									<div class="popup-label">Place</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Feature</span>
										<button
											type="button"
											class="popup-link"
											@click="
												openFeatureFromTreatment(popupTreatment.feature_id)
											"
										>
											Feature #{{ popupTreatment.feature_id
											}}<template v-if="popupTreatmentFeatureType">
												—
												{{
													featureTypeLabel(popupTreatmentFeatureType)
												}}</template
											>
										</button>
									</div>
									<div v-if="popupTreatmentSiteId != null" class="popup-kv">
										<span class="popup-kv-key">Site</span>
										<span>
											<button
												type="button"
												class="popup-link"
												@click="openSiteFromTreatment(popupTreatmentSiteId)"
											>
												Site #{{ popupTreatmentSiteId }}
											</button>
											<span v-if="popupTreatmentSiteAddress">
												— {{ popupTreatmentSiteAddress }}</span
											>
										</span>
									</div>
									<div v-else class="popup-empty">Site not resolved.</div>
								</div>

								<div class="popup-section popup-section--observations-alt">
									<div class="popup-label">Leading observation</div>
									<button
										v-if="popupTreatment.observation_id != null"
										type="button"
										class="popup-link"
										@click="
											openObservationFromTreatment(
												popupTreatment.observation_id,
											)
										"
									>
										Observation #{{ popupTreatment.observation_id }}
									</button>
									<div v-else class="popup-empty">Not linked.</div>
								</div>
							</template>

							<!-- Observation: what was seen, by whom, what the production check
				     made during it found, where it was recorded, and the treatments
				     under it. -->
							<template
								v-else-if="current?.kind === 'observation' && popupObservation"
							>
								<div class="map-popup-title">
									Observation #{{ popupObservation.id }}
								</div>

								<div class="popup-section popup-section--observations">
									<div class="popup-label">Recorded</div>
									<div class="popup-kv">
										<span class="popup-kv-key">At</span>
										<span>
											<LocalizedTime :datetime="popupObservation.observed_at" />
										</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Technician</span>
										<span>{{ observationPersonLine(popupObservation) }}</span>
									</div>
								</div>

								<div
									v-if="
										conditionLines(popupObservation.condition).length > 0 ||
										popupObservation.notes
									"
									class="popup-section popup-section--observations-alt"
								>
									<div class="popup-label">Condition</div>
									<div
										v-for="line in conditionLines(popupObservation.condition)"
										:key="line.label"
										class="popup-kv"
									>
										<span class="popup-kv-key">{{ line.label }}</span>
										<span>{{ line.text }}</span>
									</div>
									<div v-if="popupObservation.notes" class="map-popup-note">
										{{ popupObservation.notes }}
									</div>
								</div>

								<div class="popup-section popup-section--observations">
									<div class="popup-label">Inspection</div>
									<div class="popup-value popup-reading">
										{{
											INSPECTION_READING_LABELS[popupObservation.inspection] ??
											popupObservation.inspection
										}}
									</div>
									<div v-if="popupObservation.density" class="popup-kv">
										<span class="popup-kv-key">Density</span>
										<span>{{
											INSPECTION_DENSITY_LABELS[popupObservation.density] ??
											popupObservation.density
										}}</span>
									</div>
									<div v-if="popupObservation.stage" class="popup-kv">
										<span class="popup-kv-key">Stage</span>
										<span>{{
											INSPECTION_STAGE_LABELS[popupObservation.stage] ??
											popupObservation.stage
										}}</span>
									</div>
								</div>

								<div class="popup-section popup-section--place-alt">
									<div class="popup-label">Place</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Feature</span>
										<button
											type="button"
											class="popup-link"
											@click="
												openFeatureFromObservation(popupObservation.feature_id)
											"
										>
											Feature #{{ popupObservation.feature_id
											}}<template v-if="popupObservation.feature">
												—
												{{
													featureTypeLabel(popupObservation.feature)
												}}</template
											>
										</button>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Site</span>
										<span>
											<button
												type="button"
												class="popup-link"
												@click="
													openSiteFromObservation(popupObservation.site_id)
												"
											>
												Site #{{ popupObservation.site_id }}
											</button>
											<span v-if="popupObservation.site">
												— {{ popupObservation.site }}</span
											>
										</span>
									</div>
								</div>

								<div class="popup-section popup-section--treatments">
									<div class="popup-label">
										Treatments ({{ popupObservation.treatments?.length ?? 0 }})
										<button
											v-if="
												(popupObservation.treatments?.length ?? 0) >
												POPUP_FOLD_AT
											"
											type="button"
											class="popup-fold"
											@click="toggleList('observation-treatments')"
										>
											{{
												listOpen("observation-treatments")
													? "Show fewer"
													: `Show all ${popupObservation.treatments?.length ?? 0}`
											}}
										</button>
									</div>
									<div class="popup-list">
										<div
											v-for="treatment in popupObservationVisibleTreatments"
											:key="treatment.id"
											class="popup-link-row"
										>
											<button
												type="button"
												class="popup-link"
												:title="treatment.start_datetime"
												@click="openTreatmentFromObservation(treatment.id)"
											>
												{{ compactStamp(treatment.start_datetime) }}
											</button>
											<span>{{ treatment.product || "—" }}</span>
										</div>
									</div>
									<div
										v-if="(popupObservation.treatments?.length ?? 0) === 0"
										class="popup-empty"
									>
										No treatments recorded.
									</div>
								</div>
							</template>

							<!-- Mosquito counts: one Trap Instance and what it produced.
				     The instance's own parameters lead, then its sample's FULL
				     species × sex matrix — the breakdown the summary cell
				     stands for — then the reading of the sample's pools and
				     tests. The matrix is always the sample's whole composition,
				     whether or not a counts clause is narrowing the ledger. -->
							<template
								v-else-if="
									current?.kind === 'trap_instance' && popupTrapInstance
								"
							>
								<div class="map-popup-title">
									Trap instance #{{ popupTrapInstance.id }}
								</div>

								<div class="popup-section popup-section--observations">
									<div class="popup-label">Set</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Set</span>
										<span>
											<LocalizedTime :datetime="popupTrapInstance.date" />
										</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Collected</span>
										<span v-if="popupTrapInstance.collected_at">
											<LocalizedTime
												:datetime="popupTrapInstance.collected_at"
											/>
										</span>
										<span v-else>Still out</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Interval</span>
										<span v-if="popupTrapInstance.interval != null">
											{{ popupTrapInstance.interval }} day(s)
										</span>
										<span v-else>—</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">State</span>
										<span>{{
											INSTANCE_STATE_LABELS[popupTrapInstance.state]
										}}</span>
									</div>
								</div>

								<div class="popup-section popup-section--place-alt">
									<div class="popup-label">Place</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Site</span>
										<span>
											<button
												type="button"
												class="popup-link"
												@click="
													openSiteFromObservation(popupTrapInstance.site_id)
												"
											>
												Site #{{ popupTrapInstance.site_id }}
											</button>
											<span v-if="popupTrapInstance.site">
												— {{ popupTrapInstance.site }}</span
											>
										</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Station</span>
										<span v-if="popupTrapInstance.station_id != null">
											<button
												type="button"
												class="popup-link"
												@click="
													pushNode({
														kind: 'trap_station',
														id: popupTrapInstance.station_id,
														anchor: popupAnchor,
													})
												"
											>
												{{ popupTrapInstance.station }}
											</button>
										</span>
										<!-- An off-station instance is normal, not a gap. -->
										<span v-else>Off station</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Trap type</span>
										<span>{{ popupTrapInstance.trap_type || "—" }}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Lure</span>
										<span>{{ popupTrapInstance.lure || "—" }}</span>
									</div>
								</div>

								<div class="popup-section popup-section--treatments">
									<div class="popup-label">Counts</div>
									<div
										v-if="popupTrapInstance.counts.state === 'not_collected'"
										class="popup-empty"
									>
										Not collected: the instance is Set, so no sample exists yet.
									</div>
									<div
										v-else-if="
											popupTrapInstance.counts.state === 'not_analyzed'
										"
										class="popup-empty"
									>
										Not analyzed: the sample's composition is not yet known.
									</div>
									<template v-else>
										<div class="popup-kv">
											<span class="popup-kv-key">Total</span>
											<span>
												{{ popupTrapInstance.counts.total.toLocaleString() }}
												({{ popupTrapInstance.counts.female.toLocaleString() }}
												female)
											</span>
										</div>
										<div class="popup-list">
											<div
												v-for="entry in popupTrapInstance.counts.matrix"
												:key="`${entry.species_id}-${entry.sex}`"
												class="popup-link-row"
											>
												<span>{{ entry.species }}</span>
												<span>{{ entry.sex }}</span>
												<span>{{ entry.count.toLocaleString() }}</span>
											</div>
										</div>
										<div
											v-if="popupTrapInstance.counts.matrix.length === 0"
											class="popup-empty"
										>
											Analyzed: no species were caught.
										</div>
									</template>
								</div>

								<div class="popup-section popup-section--place-alt">
									<div class="popup-label">Testing</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Positivity</span>
										<span>{{
											POSITIVITY_LABELS[popupTrapInstance.positivity] ??
											popupTrapInstance.positivity
										}}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Pools</span>
										<span>{{ popupTrapInstance.pools.toLocaleString() }}</span>
									</div>
								</div>
							</template>

							<!-- Traps: one Trap Station — the district's standing decision
				     about where traps go, the Site it belongs to, its Active life
				     and its trapping series. -->
							<template
								v-else-if="current?.kind === 'trap_station' && popupTrapStation"
							>
								<div class="map-popup-title">
									Trap station #{{ popupTrapStation.id }}
								</div>

								<div class="popup-section popup-section--observations">
									<div class="popup-label">Station</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Site</span>
										<span>
											<button
												type="button"
												class="popup-link"
												@click="
													openSiteFromObservation(popupTrapStation.site_id)
												"
											>
												Site #{{ popupTrapStation.site_id }}
											</button>
											<span v-if="popupTrapStation.site">
												— {{ popupTrapStation.site }}</span
											>
										</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Active</span>
										<span>{{
											popupTrapStation.active ? "Active" : "Retired"
										}}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Instances</span>
										<span>
											<button
												type="button"
												class="popup-link"
												@click="openInstancesOfStation(popupTrapStation.id)"
											>
												{{ popupTrapStation.instances.toLocaleString() }}
											</button>
										</span>
									</div>
								</div>

								<!-- The station's trapping series as a shape: one line per
							     species over the collection dates, read from the
							     instances the station relation resolves to. -->
								<div class="popup-section popup-section--treatments">
									<div class="popup-label">Counts over time</div>
									<TrapStationCountsChart :rows="popupTrapStationInstances" />
								</div>
							</template>

							<!-- Mosquito Counts: one Species Count (§26), normalised —
				     what was caught, of which species and sex, and how many. Its
				     Trap Instance, Station and Site are reached through the
				     sample, so the Station and Site are links. -->
							<template
								v-else-if="
									current?.kind === 'species_count' && popupSpeciesCount
								"
							>
								<div class="map-popup-title">
									Mosquito count #{{ popupSpeciesCount.id }}
								</div>

								<div class="popup-section popup-section--observations">
									<div class="popup-label">Count</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Species</span>
										<span>{{ popupSpeciesCount.species }}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Sex</span>
										<span>{{ popupSpeciesCount.sex }}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Count</span>
										<span>{{ popupSpeciesCount.count.toLocaleString() }}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Set</span>
										<span>
											<LocalizedTime :datetime="popupSpeciesCount.set_at" />
										</span>
									</div>
								</div>

								<div class="popup-section popup-section--place-alt">
									<div class="popup-label">Place</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Site</span>
										<span>
											<button
												type="button"
												class="popup-link"
												@click="
													openSiteFromObservation(popupSpeciesCount.site_id)
												"
											>
												Site #{{ popupSpeciesCount.site_id }}
											</button>
											<span v-if="popupSpeciesCount.site">
												— {{ popupSpeciesCount.site }}</span
											>
										</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Station</span>
										<span v-if="popupSpeciesCount.station_id != null">
											<button
												type="button"
												class="popup-link"
												@click="
													pushNode({
														kind: 'trap_station',
														id: popupSpeciesCount.station_id,
														anchor: popupAnchor,
													})
												"
											>
												{{ popupSpeciesCount.station }}
											</button>
										</span>
										<!-- An off-station instance is normal, not a gap. -->
										<span v-else>Off station</span>
									</div>
								</div>
							</template>

							<!-- Zone: the district's own unit, one row of the Zones grid.
				     Its facts are its active version's — its name, its comment,
				     the Features that version contains, and its life. Its two
				     writes sit with that life: a correction supersedes the active
				     version and both stand, so it opens the zone form on this
				     unit's own name and comment, and a retirement keeps every
				     version but ends the unit. Retiring is irreversible through
				     this UI, so it confirms first. -->
							<template v-else-if="current?.kind === 'zone' && popupZone">
								<div class="map-popup-title">{{ popupZone.name }}</div>
								<div class="map-popup-subtitle">Zone</div>

								<div class="popup-section popup-section--place">
									<div class="popup-label">The unit</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Comment</span>
										<span v-if="popupZone.comment">{{
											popupZone.comment
										}}</span>
										<span v-else class="text-muted">—</span>
									</div>
									<div
										class="popup-kv"
										title="Features this zone's active version contains."
									>
										<span class="popup-kv-key">Records</span>
										<span>{{ popupZone.records.toLocaleString() }}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Created</span>
										<span>
											<LocalizedTime :datetime="popupZone.created" />
										</span>
									</div>
								</div>

								<div class="popup-section popup-section--activity">
									<div class="popup-label">Life</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Active</span>
										<span v-if="popupZone.active">Active</span>
										<span v-else>Retired</span>
									</div>
									<button
										type="button"
										class="popup-action-btn"
										:disabled="!popupZone.active"
										:title="
											popupZone.active
												? 'Correct this zone: a new version supersedes the active one and both stand.'
												: 'This zone is retired.'
										"
										@click="correctZone(popupZone)"
									>
										Correct
									</button>
									<button
										type="button"
										class="popup-action-btn"
										:disabled="!popupZone.active || zoneRetiring"
										:title="
											popupZone.active
												? 'Retire this zone. Its versions are kept; it stops drawing and stops narrowing the ledger.'
												: 'This zone is retired.'
										"
										@click="retireZone(popupZone)"
									>
										{{ zoneRetiring ? "Retiring…" : "Retire zone" }}
									</button>
								</div>
							</template>

							<!-- Contact: a Site's person. Its stored fields lead, then every person
				     a skip-trace run returned for it (the raw history, folded), then
				     the Sites it is linked to with the roles and source of each
				     link. The vendor is never named — the source chip says Skip
				     trace. -->
							<template v-else-if="current?.kind === 'contact' && popupContact">
								<div class="map-popup-title">
									{{ popupContact.name || `Contact #${popupContact.id}` }}
								</div>
								<div class="map-popup-subtitle">Contact</div>

								<div class="popup-section popup-section--people">
									<div class="popup-label">Details</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Name</span>
										<span>{{ popupContact.name || "—" }}</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Traced</span>
										<span v-if="popupContact.last_traced_at">
											<LocalizedTime :datetime="popupContact.last_traced_at" />
										</span>
										<span v-else class="text-muted">—</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Emails</span>
										<span>
											<template v-if="popupContact.emails.length > 0">
												<div v-for="email in popupContact.emails" :key="email">
													{{ email }}
												</div>
											</template>
											<span v-else class="text-muted">—</span>
										</span>
									</div>
									<div class="popup-kv">
										<span class="popup-kv-key">Phones</span>
										<span>
											<template v-if="popupContact.phones.length > 0">
												<div
													v-for="phone in popupContact.phones"
													:key="phone.e164"
												>
													{{ formatPhoneUS(phone.e164) }}
												</div>
											</template>
											<span v-else class="text-muted">—</span>
										</span>
									</div>
								</div>

								<div class="popup-section popup-section--people-alt">
									<div class="popup-label">
										History ({{ popupContact.history.length }})
										<button
											v-if="popupContact.history.length > POPUP_FOLD_AT"
											type="button"
											class="popup-fold"
											@click="toggleList('contact-history')"
										>
											{{
												listOpen("contact-history")
													? "Show fewer"
													: `Show all ${popupContact.history.length}`
											}}
										</button>
									</div>
									<div
										v-for="(person, index) in popupContactVisibleHistory"
										:key="index"
										class="popup-history-card"
									>
										<div class="popup-kv popup-kv--lead">
											<span class="popup-kv-key">
												{{ person.full_name || "—" }}
												<span class="popup-history-index"
													>#{{ index + 1 }}</span
												>
											</span>
											<span>
												<template v-if="person.property_owner">Owner</template>
												<template v-if="person.deceased"> · Deceased</template>
												<template v-if="person.litigator">
													· Litigator</template
												>
											</span>
										</div>
										<div
											v-if="person.dob || person.age != null"
											class="popup-kv"
										>
											<span class="popup-kv-key">DOB / age</span>
											<span>
												{{ person.dob || "—" }}
												<template v-if="person.age != null">
													· {{ person.age }}</template
												>
											</span>
										</div>
										<div v-if="historyMailingAddress(person)" class="popup-kv">
											<span class="popup-kv-key">Mailing</span>
											<span>{{ historyMailingAddress(person) }}</span>
										</div>
										<div
											v-if="historyPhones(person).length > 0"
											class="popup-kv"
										>
											<span class="popup-kv-key">Phones</span>
											<span>
												<div
													v-for="phone in historyPhones(person)"
													:key="phone"
												>
													{{ phone }}
												</div>
											</span>
										</div>
										<div
											v-if="historyEmails(person).length > 0"
											class="popup-kv"
										>
											<span class="popup-kv-key">Emails</span>
											<span>
												<div
													v-for="email in historyEmails(person)"
													:key="email"
												>
													{{ email }}
												</div>
											</span>
										</div>
									</div>
									<div
										v-if="popupContact.history.length === 0"
										class="popup-empty"
									>
										No history recorded.
									</div>
								</div>

								<div class="popup-section popup-section--people">
									<div class="popup-label">
										Sites ({{ popupContactSiteRows.length }})
									</div>
									<div
										v-for="link in popupContactSiteRows"
										:key="link.site_id"
										class="popup-person"
									>
										<span class="popup-person-main">
											<button
												type="button"
												class="popup-link"
												@click="openSiteFromContact(link.site_id)"
											>
												Site #{{ link.site_id }}
											</button>
											<span
												v-for="role in link.roles"
												:key="role.role"
												class="popup-role-chip"
												:class="{
													'popup-role-chip--primary': role.is_primary,
												}"
											>
												{{ siteContactRoleLabel(role.role) }}
											</span>
											<span v-if="link.last_traced_at" class="popup-asof">
												<LocalizedTime :datetime="link.last_traced_at" />
											</span>
										</span>
										<span class="popup-person-sources">
											<span
												v-for="source in link.sources"
												:key="source"
												class="popup-source-chip"
												:class="`popup-source-chip--${source}`"
											>
												{{ sourceLabel(source) }}
											</span>
										</span>
									</div>
									<div
										v-if="popupContactSiteRows.length === 0"
										class="popup-empty"
									>
										Not linked to a site.
									</div>
								</div>
							</template>
						</div>
						<p v-else class="detail-pane-empty">
							Tick two or more ledger rows to see the selection and its actions
							here. Pick a site, a feature, an observation or a contact — on the
							map, or by clicking one row — for that record's details.
						</p>
					</div>
				</template>

				<template #analysis>
					<AnalysisPane />
				</template>

				<template #ask>
					<AskPane />
				</template>
			</ToolRail>

			<!-- The layers, search, filters, symbology and tools panes: the map's
			     own controls, in the shared right-edge tool rail (the same rail the
			     Reveal workbench puts its cart and exports in). What the
			     operator reads the map BY comes first — the layer-visibility
			     rows, the finder (every ledger table and the address hierarchy
			     in one free-text box), and the filters tool — the toggle for the
			     ledger's column controls, Clear, and what is applied — then the
			     member's own symbology view (the tags and kind
			     defaults they override, which doubles as the map's legend), and
			     the tools that write, shape and ship records, moved out of the
			     topbar, under the fifth. Each icon is always
			     visible, and clicking the active icon collapses the body again.
			     The body floats over the map rather than taking a column of the
			     rail, because what it reads is the map. Its 60px start clears
			     the topbar and its 48px stop clears the map's own attribution in
			     the corner, so the pane scrolls in nearly the whole height of
			     the map. While the ledger is open both rails end above it
			     instead (see railBottomOffset). -->
			<ToolRail
				side="right"
				v-model="rightTab"
				:tabs="[
					{ key: 'layers', icon: 'bi bi-layers', title: 'Layers' },
					{ key: 'search', icon: 'bi bi-search', title: 'Search' },
					{
						key: 'filters',
						icon: 'bi bi-funnel',
						title: 'Filters — show the ledger controls and what is applied',
						badgeCount: store.appliedFilters.length,
					},
					{ key: 'symbology', icon: 'bi bi-palette', title: 'Symbology' },
					{ key: 'tools', icon: 'bi bi-tools', title: 'Tools' },
				]"
				topOffset="60px"
				:bottom-offset="railBottomOffset('48px', 60)"
			>
				<template #layers>
					<div class="control-pane">
						<label
							class="control-pane-row"
							:class="{ 'control-pane-row--hidden': !layerVisibility.sites }"
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.sites"
							/>
							<span
								class="control-pane-swatch"
								:style="{ background: SITE_FILL_COLOR }"
							></span>
							<span class="control-pane-label">
								Sites ({{ store.counts.site.toLocaleString()
								}}{{ store.filtersActive ? " matching" : "" }})
							</span>
						</label>
						<!-- The zone layer's own whole-layer toggle, beside the Sites row
					     because the layer draws just above the fabric and under every
					     record layer. Its swatch is an outline rather than a fill, since
					     a zone is a boundary the district drew and the layer's fill is
					     only a tint of that boundary. It is independent of the record
					     rows: it neither dims with them nor narrows them. -->
						<label
							class="control-pane-row"
							:class="{ 'control-pane-row--hidden': !layerVisibility.zones }"
							title="The district's own named units, drawn from the zone tile layer."
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.zones"
							/>
							<span
								class="control-pane-swatch"
								:style="{
									background: 'transparent',
									borderColor: ZONE_COLOR,
								}"
							></span>
							<span class="control-pane-label">
								Zones ({{ store.counts.zone.toLocaleString()
								}}{{ store.filtersActive ? " matching" : "" }})
							</span>
						</label>
						<!-- The temporary zones' own whole-layer row, beside the Zones
					     one because the two layers draw on top of each other:
					     the buffers a surface's kept rows were materialised
					     into sit just above the district's units. It stays
					     rendered with nothing to draw, like the pinned-revisit
					     row, so the layer is discoverable before the first
					     buffer is made; its count is the spec's own, not a
					     server count, because a buffer is no row. Its swatch is
					     an outline like the zones', since a buffer is a boundary
					     too. -->
						<label
							class="control-pane-row"
							:class="{
								'control-pane-row--hidden': !layerVisibility.tempZones,
							}"
							title="Buffers materialised from a surface's kept rows."
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.tempZones"
							/>
							<span
								class="control-pane-swatch"
								:style="{
									background: 'transparent',
									borderColor: TEMP_ZONE_COLOR,
								}"
							></span>
							<span class="control-pane-label">
								Temporary zones ({{ tempZones.length.toLocaleString() }})
							</span>
						</label>
						<label
							class="control-pane-row"
							:class="{ 'control-pane-row--hidden': !layerVisibility.features }"
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.features"
							/>
							<span class="control-pane-label">
								Features ({{ store.counts.feature.toLocaleString()
								}}{{ store.filtersActive ? " matching" : "" }})
							</span>
						</label>
						<label
							v-for="band in CONTROL_BANDS"
							:key="band"
							class="control-pane-row control-pane-row--child"
							:class="{ 'control-pane-row--hidden': !layerVisibility[band] }"
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility[band]"
							/>
							<span
								class="control-pane-swatch"
								:style="{
									background: BAND_COLOR[band],
								}"
							></span>
							<span class="control-pane-label">
								{{ BAND_LABEL[band] }} ({{ bandCounts[band].toLocaleString() }})
							</span>
							<!-- The due-soon horizon rides the row it bands, so it reads
						     as one thought with the count and adds no line to the
						     pane. It is deliberately tiny; the row sets the height.
						     The track is a pseudo-element pinned to the wrapper's
						     vertical centre and the input fills the wrapper, so the
						     thumb lands centred on the track in every engine. -->
							<span
								v-if="band === 'due_soon'"
								class="control-pane-due-soon-slider"
								:style="{ '--due-soon-color': BAND_COLOR.due_soon }"
							>
								<input
									id="due-soon-days"
									v-model.number="dueSoonDays"
									type="range"
									class="control-pane-due-soon-slider-input"
									:min="DUE_SOON_MIN_DAYS"
									:max="DUE_SOON_MAX_DAYS"
									step="1"
									:aria-label="dueSoonWindowLabel"
									:title="dueSoonWindowLabel"
									@input="nowMs = Date.now()"
								/>
							</span>
							<span
								v-if="band === 'due_soon'"
								class="control-pane-due-soon-value"
								>{{ dueSoonDays }}d</span
							>
						</label>
						<label
							class="control-pane-row control-pane-row--child"
							:class="{ 'control-pane-row--hidden': !layerVisibility.inactive }"
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.inactive"
							/>
							<span class="control-pane-label">
								Show inactive ({{ inactiveCount.toLocaleString() }})
							</span>
						</label>
						<!-- The pinned revisit overlay's own row. It sits with the Feature
				     rows it annotates and stays rendered (unlike the old count
				     readout) so the control is discoverable before the first pin
				     exists. It dims like its siblings when its own layer is off,
				     and when the Features group row above it is off. -->
						<label
							class="control-pane-row control-pane-row--child"
							:class="{
								'control-pane-row--hidden':
									!layerVisibility.pinned || !layerVisibility.features,
							}"
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.pinned"
							/>
							<span
								class="control-pane-swatch"
								:style="{
									background: 'transparent',
									borderColor: PINNED_RING_COLOR,
								}"
							></span>
							<span class="control-pane-label">
								Pinned revisit ({{ pinnedCount.toLocaleString() }})
							</span>
						</label>
						<label
							class="control-pane-row"
							:class="{
								'control-pane-row--hidden': !layerVisibility.treatments,
							}"
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.treatments"
							/>
							<span
								class="control-pane-swatch"
								:style="{ background: TREATMENT_COLOR }"
							></span>
							<span class="control-pane-label">
								Treatments ({{ store.counts.treatment.toLocaleString()
								}}{{ store.filtersActive ? " matching" : "" }})
							</span>
						</label>
						<!-- The observation layer's own whole-layer row. It sits with the
				     treatments as a top-level layer, not under the Feature rows:
				     its layer is the server-filtered tile set, independent of the
				     Feature points, so it neither dims with nor narrows them. It
				     stays rendered so the layer is discoverable before any count
				     loads. The two swatches are the dot's two readings, so the
				     legend lives with the toggle. -->
						<label
							class="control-pane-row"
							:class="{
								'control-pane-row--hidden': !layerVisibility.observations,
							}"
							title="One dot per feature the matching observations sit at. Red: the latest observation found production. Blue: it did not, or was never checked."
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.observations"
							/>
							<span
								class="control-pane-swatch"
								:style="{ background: OBSERVATION_COLOR }"
							></span>
							<span
								class="control-pane-swatch"
								:style="{ background: OBSERVATION_NOT_FOUND_COLOR }"
							></span>
							<span class="control-pane-label">
								Observations ({{ store.counts.observation.toLocaleString()
								}}{{ store.filtersActive ? " matching" : "" }})
							</span>
						</label>
						<!-- The trap family's group row and its two whole-layer
					     children, beside the other themes' and after
					     Observations, so the trapping programme's picture
					     reads in one place. The group row carries the family
					     total — the kept stations plus the off-station
					     instances that have no station — and dims like the
					     Features group, overriding the two rows below it.
					     Each child count is the live, spec-resolved count of
					     exactly what its tile draws. -->
						<label
							class="control-pane-row"
							:class="{ 'control-pane-row--hidden': !layerVisibility.traps }"
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.traps"
							/>
							<span class="control-pane-label">
								Traps ({{
									(
										store.counts.trap_station + store.counts.trap_instance
									).toLocaleString()
								}}{{ store.filtersActive ? " matching" : "" }})
							</span>
						</label>
						<label
							class="control-pane-row control-pane-row--child"
							:class="{
								'control-pane-row--hidden':
									!layerVisibility.traps || !layerVisibility.trapStations,
							}"
							title="One dot per kept trap station, at the station's active location."
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.trapStations"
							/>
							<span
								class="control-pane-swatch"
								:style="{ background: TRAP_STATION_COLOR }"
							></span>
							<span class="control-pane-label">
								Trap stations ({{ store.counts.trap_station.toLocaleString()
								}}{{ store.filtersActive ? " matching" : "" }})
							</span>
						</label>
						<label
							class="control-pane-row control-pane-row--child"
							:class="{
								'control-pane-row--hidden':
									!layerVisibility.traps || !layerVisibility.trapInstances,
							}"
							title="One dot per kept trap instance with no station, at the coordinates it captured on its set."
						>
							<input
								type="checkbox"
								class="control-pane-checkbox"
								v-model="layerVisibility.trapInstances"
							/>
							<span
								class="control-pane-swatch"
								:style="{ background: TRAP_INSTANCE_COLOR }"
							></span>
							<span class="control-pane-label">
								Off-station traps ({{
									store.counts.trap_instance.toLocaleString()
								}}{{ store.filtersActive ? " matching" : "" }})
							</span>
						</label>
						<!-- The field layers the Analysis pane has sent to the map,
					     listed as rows with the standing ones: a checkbox that
					     hides the fill and a trash that takes the layer off. -->
						<FieldLayerTray />
						<!-- The district's saved H3 layers, listed as layer rows
					     beside the standing ones: the same checkbox idiom, plus a
					     pencil to correct one and a control to make a new one.
					     They are org-scoped rows, so they travel with the
					     district's account; whether one is drawn is this
					     device's own choice. -->
						<H3LayerTray
							:layers="h3Layers"
							:hidden="h3Prefs.hidden"
							:loading="h3Loading"
							:error="h3Error"
							:units="h3Units"
							:area-unit="h3AreaUnit"
							@toggle="toggleH3Layer"
							@edit="openH3Editor"
							@create="openH3Editor(null)"
							@retry="loadH3Layers"
						/>
						<!-- The H3 resolution ladder, the offset every saved H3 layer
					     is cut at. It is a section of this pane, under the layers
					     it cuts, rather than the on-map panel it used to be: the
					     lab's own offset semantics (default -1, the -3..+3 range,
					     H3's clamp and the per-zoom cell-size cap) are reused, so
					     a saved layer and a lab grid read the same grid. -->
						<H3ResolutionControl
							v-model:offset="h3Prefs.offset"
							:zoom="mapZoom"
						/>
						<div v-if="highlightApproximate" class="control-pane-approximate">
							<i class="bi bi-exclamation-triangle"></i>
							<span>Map highlight is approximate for these filters.</span>
						</div>
					</div>
				</template>

				<!-- The search pane: the map's finder, the one free-text box that
				     covers every ledger table and the address hierarchy at once, under
				     the rail's search icon. The pane owns no map side effect — it
				     reports the picked result and the view flies and opens it — and
				     writes neither the filter spec nor the legacy per-table search. -->
				<template #search>
					<SearchPane
						@select="onSearchSelect"
						@filter-address="onFilterAddress"
						@error="onSearchError"
					/>
				</template>

				<!-- The filters pane: the address narrowing and the applied clauses,
				     under the rail's funnel icon. It reads and writes the shared
				     store directly, so a clause set here and a clause set in the
				     ledger header cannot disagree. -->
				<template #filters>
					<FiltersPane />
				</template>

				<!-- The symbology pane: the member's own view of the map, under the
				     palette icon. It lists the catalog's tags and subject kinds in
				     the order the resolution applies them, writes this member's
				     overrides and is the map's legend; a write asks the view to
				     reload the catalog the Site layers paint from. -->
				<template #symbology>
					<SymbologyPane :catalog="catalog" @refresh="reloadSymbology" />
				</template>

				<!-- The tools pane: the map's shaping tools, moved out of the
				     topbar. The zones, the buffers and the VectorSurv catch-up —
				     everything that writes, shapes or ships records — in one pane,
				     while the panes before it keep only what the map is read BY. -->
				<template #tools>
					<ToolsPane
						:zone-armed="zoneArmed"
						:zone-has-draft="zoneDraftGeometry !== null"
						:zone-correcting-name="zoneCorrecting?.name ?? ''"
						:zone-saveable="zoneSaveable"
						:zone-saving="zoneSaving"
						:zone-importing="zoneImporting"
						:zone-options="zoneOptions"
						:buffer-busy="bufferBusy"
						:temp-zone-saving="tempZoneSaving"
						v-model:zone-name="zoneDraftName"
						v-model:zone-comment="zoneDraftComment"
						@toggle-zone-draw="toggleZoneDraw"
						@clear-zone-filter="clearZoneFilter"
						@toggle-zone="toggleZone"
						@zone-import-file="importZoneFile"
						@save-zone="saveZoneDraft"
						@discard-zone="clearZoneDraft"
						@buffer="materializeBuffer"
						@save-buffer-zone="saveTempZoneAsZone"
					/>
				</template>
			</ToolRail>

			<!-- The loading state, and the empty one. A failed ledger round is
			     neither: the tile layers paint whether or not the ledger answered,
			     so the map says nothing about it. The failure is reported in the
			     ledger, where the query's retry is. -->
			<div v-if="loading" class="map-overlay">
				<div class="spinner-border text-primary" role="status"></div>
				<p class="mt-2 mb-0 text-muted">Loading map data…</p>
			</div>
			<div
				v-else-if="store.loadState === 'resolved' && isEmpty"
				class="map-overlay"
			>
				<p class="mb-0 text-muted">No features or treatments to display.</p>
			</div>

			<TableDrawer />
			<!-- The saved-layer sheet. Its draft is its own (it opens on the row
			     being corrected, or on a blank draft for a new one), and it
			     reports the write upward so the list is replaced from the
			     server's own answer. -->
			<H3LayerEditor
				v-if="h3EditorOpen"
				:layer="h3EditorLayer"
				:units="h3Units"
				:area-unit="h3AreaUnit"
				:saving="h3EditorSaving"
				:error="h3EditorError"
				@save="saveH3Layer"
				@cancel="closeH3Editor"
				@remove="removeH3Layer"
			/>
		</div>

		<ToastNotification
			v-model:show="toastShow"
			:title="toastTitle"
			:message="toastMessage"
		/>
	</FullLayout>
</template>

<script setup lang="ts">
import "maplibre-gl/dist/maplibre-gl.css";

import {
	computed,
	nextTick,
	onMounted,
	reactive,
	ref,
	shallowRef,
	toRaw,
	watch,
} from "vue";
import type { Map as MaplibreMap, MapMouseEvent } from "maplibre-gl";
import {
	MglMap,
	MglCircleLayer,
	MglFillLayer,
	MglGeoJsonSource,
	MglLineLayer,
	type MglEvent,
} from "vue-maplibre-gl";
import type { GeoJSON } from "geojson";
import { useRoute, useRouter } from "vue-router";

import FullLayout from "@/components/layout/Full.vue";
import LocalizedTime from "@/components/LocalizedTime.vue";
import {
	INSPECTION_DENSITY_LABELS,
	INSPECTION_READING_LABELS,
	INSPECTION_STAGE_LABELS,
	INSTANCE_STATE_LABELS,
	MAP_REVIEW_TABLES,
	POSITIVITY_LABELS,
	RELATION_COLUMN,
} from "@/components/map-review/columns";
import AnalysisPane from "@/components/map-review/AnalysisPane.vue";
import FieldLayerTray from "@/components/map-review/FieldLayerTray.vue";
import FiltersPane from "@/components/map-review/FiltersPane.vue";
import H3LayerEditor from "@/components/map-review/H3LayerEditor.vue";
import H3LayerTray from "@/components/map-review/H3LayerTray.vue";
import AskPane from "@/components/map-review/AskPane.vue";
import H3ResolutionControl from "@/components/map-review/H3ResolutionControl.vue";
import SearchPane from "@/components/map-review/SearchPane.vue";
import SelectionActions from "@/components/map-review/SelectionActions.vue";
import SymbologyPane from "@/components/map-review/SymbologyPane.vue";
import TableDrawer from "@/components/map-review/TableDrawer.vue";
import type { SearchSelection } from "@/components/map-review/searchResults";
import ToolsPane from "@/components/map-review/ToolsPane.vue";
import TrapStationCountsChart from "@/components/map-review/TrapStationCountsChart.vue";
import ToolRail from "@/components/panel/ToolRail.vue";
import ToastNotification from "@/components/ToastNotification.vue";
import {
	durationSeconds,
	formatAddress,
	formatDuration,
	formatPhoneUS,
} from "@/format";
import {
	type LadderRung,
	clampH3Offset,
	ladderForOffset,
} from "@/lib/h3/ladder";
import {
	h3Encode,
	h3MetricIndex,
	h3OutlineClass,
	h3OutlineTiles,
	h3SeriesGridTiles,
} from "@/lib/h3/layer";
import { dimensionLabel, dimensionValueLabel } from "@/lib/dimensionLabels";
import { featureTypeLabel } from "@/lib/featureType";
import {
	BAND_COLOR,
	BAND_LABEL,
	CONTROL_BANDS,
	DUE_SOON_MAX_DAYS,
	DUE_SOON_MIN_DAYS,
	type ControlBand,
} from "@/lib/mapReviewBands";
import {
	SITE_FILL_COLOR,
	SITE_MIN_ZOOM,
	TRAP_INSTANCE_COLOR,
	TRAP_STATION_COLOR,
	andExpr,
	asLayerFilter,
	featureBandColorExpr,
	featureTileSelectionFilter,
	featureVisibilityFilter,
	type LayerVisibility,
	type MapFilterExpr,
	POPULATION_FILL_OPACITY,
	populationFillColorExpr,
	WASTE_FILL_OPACITY,
	wasteFillColorExpr,
	siteAssignmentColorExpr,
	trapSelectionFilter,
	treatmentSelectionFilter,
	zoneSelectionFilter,
} from "@/lib/mapReviewLayers";
import {
	loadDueSoonDays,
	loadH3LayerPrefs,
	loadLayerVisibility,
	saveDueSoonDays,
	saveH3LayerPrefs,
	saveLayerVisibility,
	type H3LayerPrefs,
} from "@/lib/mapReviewPrefs";
import {
	decodeMapReviewUrl,
	encodeMapReviewUrl,
	type MapReviewUrlCamera,
	type MapReviewUrlItem,
	type MapReviewUrlState,
} from "@/lib/mapReviewUrl";
import { symbolPaint } from "@/lib/symbology";
import { loadSymbologyCatalog, symbologyCatalog } from "@/lib/symbologyCatalog";
import { ALIDADE_SMOOTH } from "@/map/constants";
import {
	MULTI_FILL_OPACITY,
	bivariateFillColor,
	type MultiDomains,
} from "@/lib/h3/multi";
import { outlineLinePaint } from "@/lib/h3/outline";
import { resolvedAreaUnit } from "@/lib/h3/series";
import SourceTegola from "@/map/SourceTegola.vue";
import {
	DRAW_MODE_LABELS,
	DRAW_MODES,
	distanceToMeters,
	type DrawMode,
} from "@/map/drawModes";
import { boundsDefault, boundsForServiceArea, mapStyleURL } from "@/map/util";
import { usePolygonDraw } from "@/map/usePolygonDraw";
import { useFeatureStore } from "@/store/feature";
import { useStoreMapReview } from "@/store/map-review";
import { useStoreIntervention } from "@/store/intervention";
import { useStoreProduct } from "@/store/product";
import { useSessionStore } from "@/store/session";
import { useStoreSite } from "@/store/site";
import { useStoreTreatment } from "@/store/treatment";
import { useUserStore } from "@/store/user";
import { apiClient } from "@/client";
import type {
	Address,
	ContactDetailDTO,
	Feature,
	FeatureDetail,
	LabH3MultiResponse,
	LabH3Unit,
	Location,
	MapReviewBufferRequest,
	MapReviewBufferResponse,
	MapReviewH3Layer,
	MapReviewH3LayerRequest,
	MapReviewLengthUnit,
	MapReviewSelection,
	MapReviewTrapInstanceRow,
	MapReviewObservationRow,
	MapReviewRow,
	MapReviewSpeciesCountRow,
	MapReviewTempZone,
	MapReviewTrapStationRow,
	MapReviewSiteRow,
	MapReviewTable,
	MapReviewZoneImportResult,
	MapReviewZoneListRow,
	MapReviewZoneRow,
	Site,
	SiteContactSummaryDTO,
	SkipTraceConfigDTO,
	SkipTracePersonDTO,
	SkipTraceResultDTO,
	Treatment,
} from "@/type/api";

// vue-maplibre-gl ships its own copy of maplibre-gl's typings, whose paint
// expression unions disagree with the app's maplibre-gl (they part company on
// `line-dasharray` and `visibility`). The layer component's own prop type is
// therefore the one a composed expression is handed to — the same seam
// MapLabPane casts at — so the H3 layers' dynamic fills are cast there once.
type FillPaint = NonNullable<
	InstanceType<typeof MglFillLayer>["$props"]["paint"]
>;
type LinePaint = NonNullable<
	InstanceType<typeof MglLineLayer>["$props"]["paint"]
>;

// The site fabric's fill, the `site` tile floor it must match, and the trap
// family's two dot colours all live in @/lib/mapReviewLayers, beside the palettes:
// the lab trap view draws the same fabric and the same trap dots, and one
// spelling is what keeps the two maps' layers the same picture. The hues below
// are this view's own.
//
// The selected Site polygon, drawn above the base.
const SITE_COLOR = "#38bdf8";
const FEATURE_COLOR = "#f59e0b";
const TREATMENT_COLOR = "#22c55e";

// The district's own Zones — the named units it divides its ground by. Pink is
// the one hue no other layer or overlay uses (the bands are plum, orange, teal
// and grey; the overlays add sky, amber, green, slate, violet, red and blue),
// so a unit never reads as one of the records it narrows. The layer's fill is
// a tint of the same hue, and the drawn ring uses it too, so the shape the
// operator draws is the shape the zone will draw.
const ZONE_COLOR = "#db2777";

// A temporary zone — a buffer materialised from a surface's kept rows — is the
// standing zone's derived twin, so it takes the one hue family the map has left
// for an area: the cyan beside the zones' pink, which no band, record layer or
// overlay uses (see ZONE_COLOR above). It is deliberately NOT the zones' own
// pink: a buffer is computed from the ledger rather than drawn by the district,
// and the two must not read as the same kind of boundary when they overlap. The
// line alone is drawn, dashed on a tighter rhythm than a zone's, so a computed
// edge never reads as one the district drew.
const TEMP_ZONE_COLOR = "#0891b2";

// The address-search pin. It is not a data layer — it marks where a searched
// address landed when that result carried no Site to outline — so it uses the
// one dark neutral no data layer uses, reading as a marker rather than another
// habitat band.
const SEARCH_PIN_COLOR = "#111827";

// The halo that marks the record the details panel is describing. Green as
// asked for, but deliberately a soft glow plus a ring rather than another
// filled dot: the treatment layer is already green (#22c55e), so a solid green
// dot would read as one more treatment point instead of as "this one is
// selected". The ring's own green is a step deeper so it separates from a
// treatment dot at a glance.
const SELECTED_HALO_COLOR = "#16a34a";
const SELECTED_HALO_GLOW = "rgba(34, 197, 94, 0.32)";
const SELECTED_HALO_GLOW_FAR = "rgba(34, 197, 94, 0.16)";

// A pinned Feature — one the server reports an active manual revisit date for —
// is marked with a thin coloured ring above its point. Violet is the one hue no
// band fill or other map overlay uses: the bands are plum (expired), orange
// (due_soon), teal (controlled) and grey (unknown), and the site/treatment
// layers add sky (#38bdf8), amber (#f59e0b), green (#22c55e) and slate
// (#94a3b8). The same constant is the control row's swatch, so the swatch can
// never drift from the ring it labels.
const PINNED_RING_COLOR = "#7c3aed";

// An observation — the record of what a tech saw at one Feature — is drawn as
// one dot per FEATURE it was recorded at, and the dot's colour is what the
// production check made during that Feature's most recent observation found:
// found or not found. OBSERVATION_COLOR is the layer's own hue and means
// "production found" — red is the hue no other layer uses (the bands are plum
// #7B2D6B, orange #E69F00, teal #009E73 and grey #9ca3af; the overlays add sky
// #38bdf8, amber #f59e0b, green #22c55e, slate #94a3b8 and the pinned ring's
// violet #7c3aed), and found-production is the reading the layer exists to
// surface. Its counterpart is Okabe–Ito blue, the colourblind-safe partner of
// red (the same pairing the applied-rate verdict uses in the ledger), which is
// likewise unused on this map.
//
// The control row draws both as its legend, so the colours are learnable from
// the pane rather than from a tooltip.
const OBSERVATION_COLOR = "#dc2626";
const OBSERVATION_NOT_FOUND_COLOR = "#0072B2";

// The dot's fill by the tile's `production` property (1 when the Feature's most
// recent observation found production, else 0). The layer draws one row per
// Feature, so this is the only per-row attribute it reads.
const observationColorExpr: any = [
	"case",
	["==", ["get", "production"], 1],
	OBSERVATION_COLOR,
	OBSERVATION_NOT_FOUND_COLOR,
];

// The Feature point's fill, by the client band (see @/lib/mapReviewBands). The
// band is computed from the tile's effective window end (`window_end_ms`, epoch
// milliseconds, absent when nothing contributes) against the live clock and the
// due-soon horizon, so the horizon slider re-bands the points with no refetch.
// The expression is built from the same band predicates the layer filter uses,
// so the fill and the filter cannot drift.
const featureBandFillExpr = computed<any>(() =>
	featureBandColorExpr(nowMs.value, dueSoonDays.value, BAND_COLOR),
);

// The Site fabric's fill and outline, by the technician the Site is assigned to
// (see the assignment join in etc/tegola.toml). Built from the organisation's
// members, so the `match` names every technician the district has — and only
// them — and every other Site, assigned or not, keeps SITE_FILL_COLOR. The
// mapping itself is @/lib/mapReviewLayers' assignedSiteColor, which is shared by
// any other surface that shows a technician's colour.
const siteColorExpr = computed<any>(() =>
	siteAssignmentColorExpr(orgUserIDs.value, SITE_FILL_COLOR),
);

// The symbology catalog this session resolved (doc/in-progress/
// site-tagging-and-symbology.md §4.5/§4.6). It is the module's one copy, kept
// under this view's own name because the Site layers and the Symbology pane
// must read the same object: an override written in the pane forces one reload
// and the map repaints from the answer.
const catalog = symbologyCatalog;

/**
 * The Site fabric's fill and outline, twice: the base paint the map has always
 * drawn, and the catalog's paint over it once one has loaded.
 *
 * The catalog's expression is a `match` on the tile's `symbol_tag_id` — the
 * district's ranked tag for the Site (§4.4) — so a tagged Site draws the symbol
 * that won and an untagged one keeps the base rendering. The two COLOUR channels
 * pass the assignment expression as `symbolPaint`'s base on purpose (§4.3): a
 * null resolved colour means "the map's own base rendering", which for a Site is
 * the technician colour, so without it a tag whose symbol states no fill would
 * paint the neutral and an assigned Site would lose its technician's hue. The
 * opacities and the stroke width have no base: their nulls fall to the kind's
 * product default, exactly as the server's own defaults state them.
 *
 * Until the catalog lands — or when its read failed — each paint is the literal
 * it has always been, so the map is never briefly unsymbolized and a transport
 * fault never repaints the fabric.
 */
const siteFillPaint = computed<FillPaint>(() => {
	if (catalog.value === null) {
		return {
			"fill-color": siteColorExpr.value,
			"fill-opacity": 0.06,
		} as unknown as FillPaint;
	}
	return {
		"fill-color": symbolPaint(catalog.value, "fill_color", siteColorExpr.value),
		"fill-opacity": symbolPaint(catalog.value, "fill_opacity"),
	} as unknown as FillPaint;
});

const siteOutlinePaint = computed<LinePaint>(() => {
	if (catalog.value === null) {
		return {
			"line-color": siteColorExpr.value,
			"line-width": 0.5,
			"line-opacity": 0.6,
		} as unknown as LinePaint;
	}
	return {
		"line-color": symbolPaint(
			catalog.value,
			"stroke_color",
			siteColorExpr.value,
		),
		"line-width": symbolPaint(catalog.value, "stroke_width"),
		"line-opacity": symbolPaint(catalog.value, "stroke_opacity"),
	} as unknown as LinePaint;
});

/** An override written in the Symbology pane changes what the server resolves,
 * so the copy this view paints from is stale: reload it, and the Site layers
 * repaint from the new answer. A failed reload keeps the catalog already in
 * hand — the pane has already surfaced the write's own failure, and a second
 * toast for a background refresh would be noise over a still-correct map. */
function reloadSymbology(): void {
	void loadSymbologyCatalog(true).catch(() => {});
}

// Map
const mapRef = shallowRef<MaplibreMap | null>(null);

// The shared filter/selection state. The drawer and this map both read and
// write this one store, so a filter set anywhere is in force everywhere.
const store = useStoreMapReview();
const session = useSessionStore();
const storeSite = useStoreSite();
const storeFeature = useFeatureStore();
const storeTreatment = useStoreTreatment();
const storeProduct = useStoreProduct();
const storeIntervention = useStoreIntervention();
const storeUser = useUserStore();
// The organisation's members, as ids: the site fabric is painted one colour per
// technician by the assignment the tile carries (see siteColorExpr), and the
// expression can only name a colour for a technician it was given. Loaded with
// the page's other reads; until it arrives every Site draws the flat fabric.
const orgUserIDs = ref<number[]>([]);
// The map's first paint. It ends the moment the session lands — every layer
// paints from the internalized tile endpoints and the ledger carries its own
// loading state — so nothing but the session gates the "Loading map data…"
// overlay.
const loading = ref(true);

// ── The URL's workbench state ──────────────────────────────────────────────
//
// The workbench state is addressable: `state=1` plus the non-default fields name
// the ledger, its table, the selected record, paging, sort, the whole filter
// spec, the panel, the camera and the layer checkboxes (see the grammar in
// `@/lib/mapReviewUrl`). Three rules follow from that, and they are the reason
// this block reads the way it does:
//
//   - The marker decides which tier is read. With `state=1` the URL is the whole
//     truth for every field it covers — an absent field is its default, and the
//     stored analysis and saved layers are ignored. Without it, nothing in the
//     address bar names workbench state and the visit restores from storage
//     exactly as it always has.
//   - The decode is synchronous. `route.query` is in hand at setup, so the state
//     is adopted before the first render: there is no frame in which the page
//     holds the storage tier's state and then jumps to the URL's.
//   - An invalid URL discards the whole state and behaves as a bare one, the
//     same all-or-nothing rule a corrupt stored analysis follows. A hand-edited
//     spec never reaches the server as a 422.
//
// The query is snapshotted once: the writer below replaces the address bar on
// every view change, and re-decoding that would fight the view.
const route = useRoute();
const router = useRouter();
const incoming = decodeMapReviewUrl({ ...route.query });
if (incoming.present) store.hydrate(incoming.state);

// The camera the URL supplied, when it supplied one. It is a constant, not
// state: it is consulted while the page settles (so the district framing is
// skipped and the camera is jumped to once) and never afterwards.
const suppliedCamera: MapReviewUrlCamera | null = incoming.present
	? incoming.state.camera
	: null;

// The camera the map is showing, as the URL writer names it. Seeded from the
// link so a page opened with a camera writes the same one back; every `moveend`
// refreshes it.
const mapCamera = ref<MapReviewUrlCamera | null>(suppliedCamera);

// The drawing mode every armed draw uses, and the circle's radius. The mode is
// shared state (the store's `drawMode`), because it is a capability of drawing
// itself rather than of one tool: whichever tool is armed draws the way the
// selector says, and both of this view's draws read it. The radius belongs to
// the circle alone, is stated in the district's own unit, and is converted to
// the metres the geometry path takes; a drag sizes its own circle and reports
// the radius back here, so the form keeps the one the operator last drew with.
const DRAW_MODE_TITLES: Record<DrawMode, string> = {
	freehand: "Draw the shape freehand, as a drag",
	circle: "Click or drag a centre; the radius is stated in the unit beside it",
	polygon: "Click each point, then close with a double-click or Enter",
};
const DRAW_RADIUS_UNITS: { value: MapReviewLengthUnit; label: string }[] = [
	{ value: "m", label: "Metres" },
	{ value: "km", label: "Kilometres" },
	{ value: "ft", label: "Feet" },
	{ value: "mi", label: "Miles" },
];
const drawRadius = ref<number>(1);
const drawRadiusUnit = ref<MapReviewLengthUnit>("km");
const drawMode = computed<DrawMode>(() => store.drawMode);
const drawRadiusMeters = computed(() =>
	distanceToMeters(drawRadius.value, drawRadiusUnit.value),
);

/** Take a circle draw's own radius back into the form, in the unit the form is
 * stating: the operator sees exactly the circle they drew, in their own unit,
 * and can adjust it for the next one. */
function onDrawRadius(meters: number): void {
	const metersPerUnit = distanceToMeters(1, drawRadiusUnit.value);
	drawRadius.value = Number((meters / metersPerUnit).toFixed(3));
}

// The due-soon horizon in days: a Feature whose effective window_end falls
// within this many days of now bands as due_soon rather than controlled. The
// control pane's slider sets it; the map re-bands its tile points client-side
// and the legend's band tally is refetched from the server. It is a device
// preference, restored from localStorage on load.
const dueSoonDays = ref(loadDueSoonDays());

// The slider's accessible name and hover tooltip: it sits inline in the row
// that already carries the visible "Due soon" legend text, so the range only
// needs to name the horizon it sets.
const dueSoonWindowLabel = computed(
	() =>
		`Due soon window: ${dueSoonDays.value} day${dueSoonDays.value === 1 ? "" : "s"}`,
);

// The clock the banding reads, shared by the map's Feature points and the
// legend so the two always agree. Refreshed when the horizon slider moves or
// the "Show inactive" row flips, which is also when the server's band tally is
// refetched; no refetch of the rows is involved.
const nowMs = ref(Date.now());

// The control pane's per-row visibility checkboxes. Everything starts visible so
// the map is unchanged until the operator hides a row. A link names the rows that
// differ from the defaults and its word is the whole truth for this visit;
// without one the choice is a device preference, restored from localStorage (a
// flag added since the stored state was written keeps its default). See
// lib/mapReviewLayers.ts for how the group and band rows compose.
const layerVisibility = reactive<LayerVisibility>(
	incoming.present ? incoming.state.layers : loadLayerVisibility(),
);

// The map view preferences are device-local: set once, expected to stick.
watch(dueSoonDays, (days) => saveDueSoonDays(days));
watch(layerVisibility, () => saveLayerVisibility({ ...layerVisibility }), {
	deep: true,
});

// The due-soon horizon and the "Show inactive" row both change the legend's
// Feature tally, so the server count is refetched when either moves (and the
// map's band clock is stamped to the same instant). A filter change never
// refetches it: the legend is the district's whole-set tally, not the page's.
watch([dueSoonDays, () => layerVisibility.inactive], () => {
	nowMs.value = Date.now();
	void store.loadFeatureCounts(dueSoonDays.value, layerVisibility.inactive);
});

// ── Saved H3 layers ────────────────────────────────────────────────────────
//
// The district's own saved H3 layers (org-scoped, one row per layer in
// public.h3_layer) slot into THIS layer system rather than beside it: each
// saved layer is composed into its own MapLibre source + fill/outline layers
// from the lab's tested helpers, drawn in the same stack as the fabric, feature
// and treatment layers, and toggled by the same control-pane checkbox idiom.
//
// The saved definition is the district's; VISIBILITY is the operator's own
// (the device's prefs), so one person hiding a layer cannot hide it for the
// district, and nothing about who is looking is written to the row. The
// resolution ladder is the same per-user setting: one offset, presented as an
// on-map control, that every H3 layer is cut at.
const h3Layers = ref<MapReviewH3Layer[]>([]);
const h3Loading = ref(false);
const h3Error = ref("");
/** The quantity units the district's data actually carries, for the editor's
 * unit picker: the lab's own count endpoint answers them, so the picker can
 * only offer a unit that exists. */
const h3Units = ref<LabH3Unit[]>([]);
const h3Prefs = reactive<H3LayerPrefs>(loadH3LayerPrefs());
watch(h3Prefs, () => saveH3LayerPrefs({ ...h3Prefs }), { deep: true });

/** Each saved layer's paintable domain — the terciles and busiest cell the lab
 * counts for the same population the tile draws — keyed by the layer's row id.
 * A layer with no entry yet paints from the lab's own empty-domain floors and
 * repaints when its count lands. */
const h3Domains = ref<Record<number, MultiDomains>>({});
/** The zoom the ladder control reads, noted on load and every move. */
const mapZoom = ref(5);
/** The editor: the layer being corrected (null = a new one) while the sheet is
 * open. */
const h3EditorLayer = ref<MapReviewH3Layer | null>(null);
const h3EditorOpen = ref(false);
const h3EditorSaving = ref(false);
const h3EditorError = ref("");
/** A monotonic request generation per layer, so a definition edited while its
 * domain is in flight cannot be painted with the previous one's numbers. */
const h3DomainGeneration = new Map<number, number>();

/** The district's area unit, resolved the way platform/units resolves it — the
 * same resolution the lab's own series grid reads, so a sum drawn here is in
 * the unit the workbench's ledger states. */
const h3AreaUnit = computed(() =>
	resolvedAreaUnit(session.organization?.unit_preference ?? null),
);

const h3TileBase = computed(() => session.urls?.tegola ?? "");

/** The saved layers the operator has switched on. */
const h3VisibleLayers = computed(() =>
	h3Layers.value.filter((layer) => !h3Prefs.hidden.includes(layer.id)),
);

/** The ladder: one resolution per zoom band, shifted by the operator's offset,
 * H3-clamped and capped by the zoom's own cell-size guard. It is the lab's own
 * ladder (`ladderForOffset`), reused rather than re-spelled, so the two grids
 * cannot be read at different resolutions. */
const h3Rungs = computed<LadderRung[]>(() =>
	ladderForOffset(clampH3Offset(h3Prefs.offset)),
);

/** The H3 cell edge, a hairline that keeps a blob legibly many cells — the same
 * cue the lab's grids draw, in a neutral ink rather than a variable's hue. */
const H3_CELL_EDGE_PAINT = {
	"line-color": "#0f172a",
	"line-width": 0.5,
	"line-opacity": 0.35,
} as unknown as LinePaint;

/** One rung of one saved layer: the resolution it draws at, the zoom band it
 * owns, and its tile URLs. */
interface H3RenderRung {
	res: number;
	min: number;
	max: number;
	tiles: string[];
	/** The third variable's dissolve, or null for a layer of one or two. */
	outlineTiles: string[] | null;
}

/** One visible saved layer, composed for the template. */
interface H3RenderLayer {
	id: number;
	fillPaint: FillPaint;
	edgePaint: LinePaint;
	outlinePaint: LinePaint | null;
	rungs: H3RenderRung[];
}

/**
 * The visible saved layers as the sources and layers the map draws: ONE source
 * per ladder rung (a resolution is a client-owned tile parameter, so two rungs
 * are two URLs and MapLibre loads only the band's), the fill painted from the
 * lab's own expression over the series grid, and — for a three-variable layer —
 * the third variable's dissolve outline above the fill.
 *
 * The fill is the lab's `bivariateFillColor` in all three cases: with one
 * variable it is issued as a spotlight over that one series, which is exactly
 * its single sequential ramp; with two or three it is the 3×3 grid. The layer's
 * own variables are never a fourth encoding — the count of variables IS the
 * encoding, which is why the composition lives here and not in the painting.
 */
const h3Render = computed<H3RenderLayer[]>(() => {
	const base = h3TileBase.value;
	const areaUnit = h3AreaUnit.value;
	const rungs = h3Rungs.value;
	return h3VisibleLayers.value.map((layer) => {
		const definition = layer.definition;
		const encode = h3Encode(definition, h3Domains.value[layer.id] ?? {});
		const outlineClass = h3OutlineClass(definition);
		return {
			id: layer.id,
			fillPaint: {
				"fill-color": bivariateFillColor(encode),
				// The 3×3 must read cell for cell against its own key, so a
				// two- or three-variable fill is opaque; the single ramp keeps the
				// lab's 0.8 wash so it stays comparable to the other hex views.
				"fill-opacity":
					definition.variables.length === 1 ? MULTI_FILL_OPACITY : 1,
			} as unknown as FillPaint,
			edgePaint: H3_CELL_EDGE_PAINT,
			outlinePaint: outlineClass
				? (outlineLinePaint(outlineClass) as unknown as LinePaint)
				: null,
			rungs: rungs.map((rung) => ({
				res: rung.res,
				min: rung.minZoom,
				max: rung.maxZoom,
				tiles: h3SeriesGridTiles(definition, {
					base,
					resolution: rung.res,
					areaUnit,
				}),
				outlineTiles: outlineClass
					? h3OutlineTiles(definition, {
							base,
							resolution: rung.res,
							areaUnit,
						})
					: null,
			})),
		};
	});
});

/**
 * Read the district's saved H3 layers and the quantity units its data carries.
 * The two are one pass: the editor must not offer a unit the data does not
 * have, and the layers must be on the map as soon as the read lands.
 */
async function loadH3Layers(): Promise<void> {
	h3Loading.value = true;
	try {
		const [layers, units] = await Promise.all([
			apiClient.JSONGet<MapReviewH3Layer[]>("/api/h3-layer"),
			apiClient.JSONGet<LabH3MultiResponse>("/api/lab/h3-multi?units=1"),
		]);
		h3Layers.value = layers;
		h3Units.value = units.units ?? [];
		h3Error.value = "";
		for (const layer of h3VisibleLayers.value) void loadH3Domains(layer);
	} catch (error) {
		h3Error.value = error instanceof Error ? error.message : String(error);
	} finally {
		h3Loading.value = false;
	}
}

/**
 * Count one visible layer's variables — each under ITS OWN metric and unit —
 * so the fill's classes and the ramp's maximum are the numbers the server
 * counted for the same population the tile draws. It is the lab's own count
 * endpoint, asked once per variable (a variable's metric is its own, which is
 * the extension this feature makes to the lab's single global measure).
 */
async function loadH3Domains(layer: MapReviewH3Layer): Promise<void> {
	const generation = (h3DomainGeneration.get(layer.id) ?? 0) + 1;
	h3DomainGeneration.set(layer.id, generation);
	const areaUnit = h3AreaUnit.value;
	const counted = await Promise.all(
		layer.definition.variables.map(async (variable, index) => {
			const query = new URLSearchParams({ series: String(variable.source) });
			if (variable.metric !== "records") {
				query.set("measure", String(h3MetricIndex(variable.metric)));
				if (variable.metric === "quantity" && variable.unit)
					query.set("unit", variable.unit);
				if (variable.metric === "area") query.set("area_unit", areaUnit);
			}
			try {
				const response = await apiClient.JSONGet<LabH3MultiResponse>(
					`/api/lab/h3-multi?${query}`,
				);
				const series = response.series[0];
				return series ? { index, series } : null;
			} catch {
				// The tile is drawn from the definition regardless; a failed count
				// only leaves the fill on the lab's empty-domain floors.
				return null;
			}
		}),
	);
	if (h3DomainGeneration.get(layer.id) !== generation) return;
	const domains: MultiDomains = {};
	for (const entry of counted) {
		if (!entry) continue;
		domains[`s${entry.index}`] = {
			total: entry.series.total,
			cells: entry.series.cells,
			maxCellCount: entry.series.maxCellCount,
			p33: entry.series.p33,
			p67: entry.series.p67,
		};
	}
	h3Domains.value = { ...h3Domains.value, [layer.id]: domains };
}

/** Switch one saved layer on or off for THIS operator: the choice is the
 * device's, so it rides the prefs and never the district's row. A layer coming
 * back on re-counts its domain, which is what the ramp needs to be meaningful
 * immediately. */
function toggleH3Layer(id: number): void {
	const hidden = h3Prefs.hidden.includes(id);
	h3Prefs.hidden = hidden
		? h3Prefs.hidden.filter((entry) => entry !== id)
		: [...h3Prefs.hidden, id];
	if (hidden) {
		const layer = h3Layers.value.find((entry) => entry.id === id);
		if (layer) void loadH3Domains(layer);
	}
}

function openH3Editor(layer: MapReviewH3Layer | null): void {
	h3EditorLayer.value = layer;
	h3EditorError.value = "";
	h3EditorOpen.value = true;
}

function closeH3Editor(): void {
	h3EditorOpen.value = false;
	h3EditorLayer.value = null;
	h3EditorError.value = "";
}

/** Write (or correct) one saved layer. The response is the stored row, so the
 * list is replaced from the server's own answer rather than from the draft. */
async function saveH3Layer(request: MapReviewH3LayerRequest): Promise<void> {
	h3EditorSaving.value = true;
	h3EditorError.value = "";
	try {
		const editing = h3EditorLayer.value;
		const saved = editing
			? await apiClient.JSONPut<MapReviewH3Layer>(
					`/api/h3-layer/${editing.id}`,
					request,
				)
			: await apiClient.JSONPost<MapReviewH3Layer>("/api/h3-layer", request);
		h3Layers.value = editing
			? h3Layers.value.map((layer) => (layer.id === saved.id ? saved : layer))
			: [...h3Layers.value, saved];
		closeH3Editor();
		void loadH3Domains(saved);
	} catch (error) {
		h3EditorError.value =
			error instanceof Error ? error.message : String(error);
	} finally {
		h3EditorSaving.value = false;
	}
}

/** Retire one saved layer for the whole district. The per-user visibility flag
 * is dropped with it, so a later layer reusing the id cannot arrive hidden. */
async function removeH3Layer(id: number): Promise<void> {
	try {
		await apiClient.JSONDelete(`/api/h3-layer/${id}`);
		h3Layers.value = h3Layers.value.filter((layer) => layer.id !== id);
		if (h3Prefs.hidden.includes(id))
			h3Prefs.hidden = h3Prefs.hidden.filter((entry) => entry !== id);
		const domains = { ...h3Domains.value };
		delete domains[id];
		h3Domains.value = domains;
		closeH3Editor();
	} catch (error) {
		showToast(
			"Could not delete the H3 layer",
			error instanceof Error ? error.message : String(error),
		);
	}
}

// Toast (error state)
const toastShow = ref(false);
const toastTitle = ref("");
const toastMessage = ref("");

// The layers, search, filters and tools panes: the tabs this view contributes
// to the shared right-edge tool rail, collapsed until the operator asks for one
// (the rail's icons are always visible). Which tab is open, if any, is a fact
// of this visit only; the layer choices in the layers tab are the saved ones
// (see layerVisibility), the search tab holds the finder, and the filters tab
// holds the address field and the applied clauses.
const rightTab = ref<string | null>(null);

// The details and analysis panels: the tabs this view contributes to the shared
// left-edge tool rail (the same element as the layers/search/filters/tools
// rail). Both start closed — the map is the page, and a panel that has nothing
// to say yet should not be the first thing over it — and the details open when
// the operator asks for them or when the map or the ledger selects a record
// (see the `nav` watcher below). The record shown is the selection graph the
// map and the ledger drive; this is only which of the two panels is expanded.
const leftTab = ref<string | null>(null);

// Both rails end above the ledger. The drawer, the rails and the map share one
// containing block (`.map-review-map`), so the drawer's fraction of that block is
// the same percentage each rail gives up at its bottom — and because the store
// owns the fraction, a drag moves the rail bottoms with the drawer live.
//
// The drawer never gets shorter than its own 140px minimum, and a rail shorter
// than its header cannot show it, so the offset is the taller of the drawer's
// real height and its floor, capped where the rail is down to that header
// alone. At a full-height ledger the rails stand as bare headers behind it
// rather than as a negative-height or scrolling box.
//
// The header is 6px of padding above and below its 30px tab strip, plus the
// hairline under it.
const RAIL_HEADER_HEIGHT = 43;
const DRAWER_MIN_HEIGHT = 140;

function railBottomOffset(closedOffset: string, topOffset: number): string {
	if (!store.drawerOpen) return closedOffset;
	const drawerTop = `max(calc(${store.drawerFraction * 100}% + 12px), ${
		DRAWER_MIN_HEIGHT + 12
	}px)`;
	const headerOnly = `calc(100% - ${topOffset + RAIL_HEADER_HEIGHT}px)`;
	return `min(${drawerTop}, ${headerOnly})`;
}

// Opening the Analysis pane IS the refresh: the report is not live, so an
// aggregate left over from an earlier visit would be read as current. Its two
// reads are issued together; they are independent.
watch(leftTab, (tab) => {
	if (tab !== "analysis") return;
	void store.loadDashboard();
	void store.loadSurveillance();
});

// The search pane's side effects, which stay here because the view owns the
// map. SearchPane reports the picked result; this view flies the map, opens the
// details node graph and drops the pin. Search is a find, never a filter —
// nothing here writes `store.spec.filters` or `spec.search`, so the tables'
// counts and rows never narrow while the operator searches. The one write is
// the address row's secondary action, which applies the same Site address
// clause the Filters pane applies.
const searchPin = ref<{ lng: number; lat: number } | null>(null);

const searchPinFC = computed<GeoJSON.FeatureCollection<GeoJSON.Point>>(() => ({
	type: "FeatureCollection",
	features: searchPin.value
		? [
				{
					type: "Feature",
					geometry: {
						type: "Point",
						coordinates: [searchPin.value.lng, searchPin.value.lat],
					},
					properties: {},
				},
			]
		: [],
}));

/** Fly to a picked search result and open it. A record flies to its own point
 * and opens its details through the same node graph a map click opens, so the
 * details panel and the selection halo both follow; an Observation travels with
 * its row because the find never narrowed the ledger page the popup would
 * otherwise read it from. A record without a point opens its details with the
 * map left where it is.
 *
 * An address flies to its point and opens its Site popup when the result
 * resolved to one; a result with no Site instead gets the search pin, so the
 * flown-to point is visible. */
function onSearchSelect(selection: SearchSelection) {
	if (selection.kind === "address") {
		const result = selection.result;
		const lng = result.lng ?? null;
		const lat = result.lat ?? null;
		const map = mapRef.value;
		if (map && lng != null && lat != null) {
			map.flyTo({ center: [lng, lat], zoom: 18, duration: 600 });
		}
		if (result.site_id != null) {
			searchPin.value = null;
			openRoot({
				kind: "site",
				id: result.site_id,
				anchor: lng != null && lat != null ? { lng, lat } : null,
			});
			return;
		}
		searchPin.value = lng != null && lat != null ? { lng, lat } : null;
		if (!searchPin.value) {
			showToast("No location", "That result has no map location.");
		}
		return;
	}
	const map = mapRef.value;
	if (map && selection.anchor) {
		map.flyTo({
			center: [selection.anchor.lng, selection.anchor.lat],
			zoom: 18,
			duration: 600,
		});
	} else {
		showToast("No location", "That record has no map location.");
	}
	openRoot({
		kind: selection.table,
		id: selection.id,
		anchor: selection.anchor,
		observationRow:
			selection.table === "observation"
				? (selection.row as MapReviewObservationRow)
				: undefined,
	});
}

/** The address row's secondary action: narrow Sites to that address and show
 * the Filters tab, where the clause is listed with its own remove action. It is
 * the same clause the Filters pane's field writes, so the two can never
 * disagree. */
function onFilterAddress(address: string) {
	store.setFilter(
		{
			table: "site",
			column: "address",
			op: "contains",
			text: address.trim(),
		},
		"address",
		"site",
	);
	rightTab.value = "filters";
}

/** A failed search is reported here. A single table's failure never reaches
 * this: it stays inline in the list as that group's own failure line. */
function onSearchError(error: { title: string; message: string }) {
	showToast(error.title, error.message);
}

/** Show the shared toast (defined above) for a one-off message: a failed
 * search, or a skip-trace outcome. */
function showToast(title: string, message: string) {
	toastTitle.value = title;
	toastMessage.value = message;
	toastShow.value = true;
}

// The popup's linked graph. The stack is the walkable path the links build
// (site → feature → treatment or observation, in any order the links allow):
// its last entry is the node shown, and Back pops it. The store's selection
// follows the current node, so the map highlights wherever the popup is. A
// popup names a ledger row, so its kind is exactly the ledger's tables.
//
// A contact is the one kind that is NOT a ledger row: it is reached from a
// Site's people list (or a skip-trace run), it has no map layer and no row
// page, and the shared selection never points at it.
//
// An observation's facts are its row, and that row travels with the node when
// the link that opened it already held one (the Feature popup's observation
// list, or a map click that resolved the dot); the ledger's own page is the
// fallback. Either way the popup renders the same record and links onward to
// the Feature, the Site and the Treatments recorded under it.
type PopupKind = MapReviewTable | "contact";
interface PopupNode {
	kind: PopupKind;
	id: number;
	anchor: { lng: number; lat: number } | null;
	// observationRow carries the observation's own row when the node was opened
	// from a link or a map click that already had it; absent when only its id
	// is known (a ledger row, or a URL's `item`), which loadCurrent resolves
	// from the page, else by id.
	observationRow?: MapReviewObservationRow;
}
const nav = ref<PopupNode[]>([]);
const current = computed<PopupNode | null>(
	() => nav.value[nav.value.length - 1] ?? null,
);
// A new selection opens the details panel, so the record the map or the ledger
// just picked is shown even if the panel had been collapsed. Collapsing it again
// is the operator's to do, and that choice holds until the next selection.
watch(
	() => nav.value.length,
	(length) => {
		if (length > 0) leftTab.value = "details";
	},
);
// One ticked row is that record's detail; the selection summary and its actions
// begin at two. Ticking a row therefore reads exactly like clicking it, while
// ticking a second turns the panel into the many-row view. A filter change
// clears the ticks, so this cannot reopen a record the table no longer shows.
watch(
	() => store.checkedSingleID,
	(id) => {
		if (id !== null) store.select(store.activeTable, id);
	},
);
watch(
	() => store.ledgerSelectionCount > 1,
	(multi) => {
		if (multi) leftTab.value = "details";
	},
);
// The popup's own root, the element whose labels the width measurement reads
// (see measurePopupColumns).
const popupEl = ref<HTMLElement | null>(null);
// The details panel, so a walk can start the new node at the top of its scroll:
// the node is replaced inside the panel body's one scroller, so without this a
// reader who had scrolled the Site would land in the middle of the Feature they
// walked to.
const detailRail = ref<InstanceType<typeof ToolRail> | null>(null);
// Which of a popup's long lists the reader has opened. Walking the graph starts
// the new node folded again, alongside the scroll-to-top the walk already did.
const openLists = ref<Record<string, boolean>>({});
watch(
	current,
	() => {
		openLists.value = {};
		detailRail.value?.bodyEl?.scrollTo({ top: 0 });
	},
	{ flush: "post" },
);

// Every fact row in the popup is its own two-column grid, and they all have to
// agree on one label width or their values land on different x — the rails
// drift and the panel reads as ragged. `subgrid` expresses that declaratively
// and this file used it; where subgrid is unsupported the declaration is
// dropped silently and every row collapses to a single column with its label
// stacked above its value, which is not a broken-looking popup, it is a
// destroyed one. So the width is measured from the popup's own labels instead:
// one variable, correct in every browser, paid for with a single post-render
// frame.
function measurePopupColumns() {
	const el = popupEl.value;
	if (!el) return;
	// Measuring the label element itself would measure the COLUMN, not the
	// text: a grid item is sized by its track, so once the track is narrow the
	// element's box is narrow however much its text overflows. A Range over the
	// node's contents measures the ink, which is the width the track has to be.
	const inkWidth = (node: HTMLElement) => {
		const range = document.createRange();
		range.selectNodeContents(node);
		return Math.ceil(range.getBoundingClientRect().width);
	};
	const widest = (nodes: HTMLElement[]) =>
		nodes.reduce((max, node) => Math.max(max, inkWidth(node)), 0);
	// A width of 0 means there was nothing to measure; leaving the property unset
	// lets the CSS `max-content` fallback size the track rather than collapse it.
	const setColumn = (node: HTMLElement, name: string, width: number) => {
		if (width > 0) node.style.setProperty(name, `${width}px`);
		else node.style.removeProperty(name);
	};
	// Fact labels set the popup-wide column; a list's own keys set that list's,
	// so every reading in a list starts on the same x.
	const labels = [...el.querySelectorAll<HTMLElement>(".popup-kv-key")].filter(
		(label) => label.closest(".popup-list") === null,
	);
	setColumn(el, "--pp-key-col", widest(labels));
	for (const list of el.querySelectorAll<HTMLElement>(".popup-list")) {
		const keys = [
			...list.querySelectorAll<HTMLElement>(".popup-link-row > :first-child"),
		];
		setColumn(list, "--pp-list-key-col", widest(keys));
	}
}

const popupSite = ref<Site | null>(null);
const popupFeature = ref<FeatureDetail | null>(null);
const popupTreatment = ref<Treatment | null>(null);
const popupObservation = ref<MapReviewObservationRow | null>(null);
const popupTrapInstance = ref<MapReviewTrapInstanceRow | null>(null);
const popupTrapStation = ref<MapReviewTrapStationRow | null>(null);
// The station popup's counts-over-time chart data: the station's Trap
// Instances, fetched by the station relation when its popup opens. Empty until
// the fetch lands, and empty for a station with no analyzed sample.
const popupTrapStationInstances = ref<MapReviewTrapInstanceRow[]>([]);
const popupSpeciesCount = ref<MapReviewSpeciesCountRow | null>(null);
// The zone the Zones grid's row opened. Its row carries every fact its detail
// shows, so the ledger's page is the fast path; a record the page does not carry
// (a link names one) is read back by id.
const popupZone = ref<MapReviewZoneRow | null>(null);
const popupContact = ref<ContactDetailDTO | null>(null);
// The Feature a treatment was applied to, fetched so the treatment popup can
// link to both that Feature and its Site (the Site id lives on the Feature).
const popupTreatmentFeatureDetail = ref<FeatureDetail | null>(null);
const popupLoading = ref(false);
const popupError = ref("");
const popupAnchor = shallowRef<{ lng: number; lat: number } | null>(null);
// Bumped on every navigation so a slow fetch for an earlier node cannot
// overwrite the content of a newer one.
let popupToken = 0;

// The selected record's own point, for the halo that marks it on the map. It is
// built from the popup's anchor — whatever the map click or the ledger row
// projected — so the halo sits on the record the details panel is describing
// and nowhere else, and it empties the moment the selection does.
const selectedHaloFC = computed<GeoJSON.FeatureCollection<GeoJSON.Point>>(
	() => ({
		type: "FeatureCollection",
		features: popupAnchor.value
			? [
					{
						type: "Feature",
						geometry: {
							type: "Point",
							coordinates: [popupAnchor.value.lng, popupAnchor.value.lat],
						},
						properties: {},
					},
				]
			: [],
	}),
);

// Re-measure whenever the record, its loaded detail, a folded list, or the
// panel's own open state changes: collapsing the panel unmounts the popup, so
// reopening it remounts unmeasured and would otherwise fall back to per-row
// `max-content` label columns.
watch(
	[
		current,
		popupSite,
		popupFeature,
		popupTreatment,
		popupObservation,
		popupContact,
		openLists,
		leftTab,
	],
	() => {
		void nextTick().then(measurePopupColumns);
	},
	{ flush: "post" },
);

// The panels' geometry lives in the shared tool rail's stylesheet: the body is
// placed by the `side` and by the caller's `topOffset`/`bottomOffset`. The
// details and analysis panels take the window's top and the left gutter (12px
// each), because nothing stands above that column; the right rail starts 60px
// down, which is the ledger's toggle above it, and its panes stop at 48px so
// they clear the map's own attribution in the corner. There is nothing to
// compute here — a panel at a constant share of the viewport has no placement
// to decide, which is why these replaced a balloon that had to be anchored at a
// click, flipped sides and clamped itself to the viewport.

// The Feature a treatment was applied to (for its type) and the Site that
// Feature belongs to. The Feature detail carries the Site id, so a treatment
// can link to both without the map holding the Site list any more; the address
// is fetched on demand from /api/site/{id} when the treatment popup opens, so
// the link never waits on the whole site fabric.
const popupTreatmentFeatureType = computed<string>(
	() => popupTreatmentFeatureDetail.value?.type ?? "",
);

const popupTreatmentSiteId = computed<number | null>(
	() => popupTreatmentFeatureDetail.value?.site_id ?? null,
);

/** The linked Site's address, resolved on demand when the treatment popup
 * loads. Empty until it lands, and left empty on a failure — the link's id is
 * still shown, exactly as an unresolved address read before. */
const popupTreatmentSiteAddress = ref<string>("");

/** Fetch the Site a treatment's Feature belongs to, for the popup's address
 * line. Token-guarded so a slow response for a treatment the user has navigated
 * away from cannot overwrite the newer one, and non-fatal: a failure leaves the
 * address blank rather than reporting an error over the treatment's own facts. */
async function loadTreatmentSiteAddress(
	siteID: number,
	token: number,
): Promise<void> {
	try {
		const site = await storeSite.fetchOne(siteID);
		if (token !== popupToken) return;
		popupTreatmentSiteAddress.value = hasAddress(site.address)
			? formatAddress(site.address)
			: "";
	} catch {
		if (token === popupToken) popupTreatmentSiteAddress.value = "";
	}
}

const popupFeatureLocation = computed<string>(() => {
	const location = popupFeature.value?.location;
	if (!location || !Number.isFinite(location.latitude)) return "—";
	return `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`;
});

// The amount applied as recorded, or in the district's preferred units when the
// platform resolves a conversion (quantity_preferred/quantity_unit_preferred).
// This is the seam the unit-preference work fills. The amount is never divided
// by the treated area: the stored units are heterogeneous (packet, fl oz, lb,
// …), so an applied rate has no common basis yet.
function formatAmount(treatment: Treatment): string {
	if (treatment.quantity_preferred != null) {
		const value = treatment.quantity_preferred.toLocaleString();
		return treatment.quantity_unit_preferred
			? `${value} ${treatment.quantity_unit_preferred}`
			: value;
	}
	if (treatment.quantity == null) return "—";
	const value = treatment.quantity.toLocaleString();
	return treatment.quantity_unit
		? `${value} ${treatment.quantity_unit}`
		: value;
}

function formatArea(treatment: Treatment): string {
	return treatment.area_treated != null
		? `${treatment.area_treated.toLocaleString()} m²`
		: "—";
}

function geometryLabel(treatment: Treatment): string {
	return treatment.geometry ? treatment.geometry.type : "None";
}

/**
 * A list row's key: one instant, in the shortest form that still tells two rows
 * of one list apart — a two-digit year, no seconds. A list row has only the
 * value column to work with, and the full timestamp at 12px would push the
 * reading beside it onto a second line in the busiest Feature popup. Nothing is
 * lost: the element keeps the ISO instant as its title, and the record's own
 * popup shows the full localized time.
 */
function compactStamp(datetime: string): string {
	return new Date(datetime).toLocaleString(undefined, {
		year: "2-digit",
		month: "numeric",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

// Human labels for the graph's vocabularies: the API carries the machine
// values, the popup shows what they mean.

const SITE_KIND_LABELS: Record<string, string> = {
	parcel: "Parcel",
	street: "Street (right-of-way)",
	other: "Other",
};

function siteKindLabel(kind: string): string {
	if (!kind) return "Unknown";
	return SITE_KIND_LABELS[kind] ?? kind.replace(/_/g, " ");
}

const OWNER_KIND_LABELS: Record<string, string> = {
	private: "Private",
	city: "City",
	county: "County",
	state: "State",
	federal: "Federal",
	district: "District",
	unknown: "Unknown",
};

function ownerKindLabel(kind: string): string {
	if (!kind) return "Unknown";
	return OWNER_KIND_LABELS[kind] ?? kind.replace(/_/g, " ");
}

function productionStateLabel(state: string): string {
	if (!state) return "Unknown";
	return state.replace(/_/g, " ");
}

/** True when an address has a street number or name to show. */
function hasAddress(address?: Address): boolean {
	return !!address && (!!address.number || !!address.street);
}

// --- The Site popup's title and roll-ups -----------------------------------

// WHERE: what the title does not already say. The title IS the address whenever
// the site has no name of its own, so the address is printed once and Where
// starts at what it adds — which for those sites is the owner, and sometimes
// nothing at all.
const siteWhereRows = computed<{ label: string; value: string }[]>(() => {
	const site = popupSite.value;
	if (!site) return [];
	const rows: { label: string; value: string }[] = [];
	const address = hasAddress(site.address) ? formatAddress(site.address) : "";
	if (address === "") {
		rows.push({ label: "Address", value: "None recorded" });
	} else if (address !== popupSiteTitle.value) {
		rows.push({ label: "Address", value: address });
	}
	const kind =
		site.owner_kind && site.owner_kind !== "unknown"
			? ` (${ownerKindLabel(site.owner_kind)})`
			: "";
	if (site.owner_name || kind) {
		rows.push({
			label: "Owner",
			value: `${site.owner_name || "Owner unknown"}${kind}`,
		});
	}
	return rows;
});

// The site's name: its label when it has one (5% of sites), else the address
// (71%), else its id. The address is what most reviews will read as the name.
const popupSiteTitle = computed<string>(() => {
	const site = popupSite.value;
	if (!site) return "";
	// The fabric labels a parcel Site with its APN ("1925041109"), which is an
	// identifier rather than a name, so a label stands as the title only when it
	// reads as one — 936 org-1 Sites are labelled that way today. The address is
	// the better name for those, and for the 69,306 that carry no label at all.
	if (site.label && /[A-Za-z]/.test(site.label)) return site.label;
	if (hasAddress(site.address)) return formatAddress(site.address);
	return `Site #${site.id}`;
});

// ON THIS SITE: the site's Features grouped by habitat class, with counts. A
// Feature with no current habitat class counts as unclassified rather than
// vanishing from the list.
function habitatGroups(
	features: Feature[],
): { label: string; count: number }[] {
	const counts = new Map<string, number>();
	for (const feature of features) {
		const key = feature.type || "";
		counts.set(key, (counts.get(key) ?? 0) + 1);
	}
	return [...counts.entries()]
		.map(([type, count]) => ({
			label: type ? featureTypeLabel(type) : "Unclassified",
			count,
		}))
		.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

// The latest control window end across the site's Features — when the site's
// suppression lapses. Null when nothing contributes a date.
const popupSiteSuppressionEnd = computed<string | null>(() => {
	let latest: string | null = null;
	for (const feature of popupSite.value?.features ?? []) {
		if (!feature.window_end) continue;
		if (latest === null || feature.window_end > latest) {
			latest = feature.window_end;
		}
	}
	return latest;
});

// The site's own distribution over habitat classes, as one line beneath the
// Features label: the names are the ontology's, so the roll-up and the list it
// summarises cannot disagree.
const siteFeatureRollup = computed<string>(() =>
	habitatGroups(popupSite.value?.features ?? [])
		.map((group) => `${group.label} ${group.count}`)
		.join(" · "),
);

// --- Folding a long list ----------------------------------------------------

// A record's fields are never folded. A list is, once it is long enough to push
// the record past its screenful, and then only behind the count its own group
// label already carries.
const POPUP_FOLD_AT = 5;

function listOpen(key: string): boolean {
	return openLists.value[key] === true;
}

function toggleList(key: string): void {
	openLists.value = { ...openLists.value, [key]: !openLists.value[key] };
}

/** The head of a list, or all of it once the reader has opened it. */
function folded<T>(key: string, items: T[] | undefined): T[] {
	const list = items ?? [];
	return listOpen(key) || list.length <= POPUP_FOLD_AT
		? list
		: list.slice(0, POPUP_FOLD_AT);
}

const siteVisibleFeatures = computed(() =>
	folded("site-features", popupSite.value?.features),
);

const popupFeatureVisibleObservations = computed(() =>
	folded("feature-observations", popupFeature.value?.observations),
);

const popupFeatureVisibleTreatments = computed(() =>
	folded("feature-treatments", popupFeature.value?.treatments),
);

const popupObservationVisibleTreatments = computed(() =>
	folded("observation-treatments", popupObservation.value?.treatments),
);

const popupContactVisibleHistory = computed(() =>
	folded("contact-history", popupContact.value?.history),
);

// A Feature's dimensions split in two: the ones carrying a value, which render
// with the Observation that set them, and the ones carrying none, which are
// named in a single line. A blank dimension is not a fact; which dimensions are
// blank is, and five blank rows say it more slowly than one line does.
const popupFeatureValued = computed(() =>
	(popupFeature.value?.characteristics ?? []).filter(
		(characteristic) => !!characteristic.value,
	),
);

const popupFeatureUnrecorded = computed<string>(() =>
	(popupFeature.value?.characteristics ?? [])
		.filter((characteristic) => !characteristic.value)
		.map((characteristic) => dimensionLabel(characteristic.dimension))
		.join(" · "),
);

// The Sites a contact is linked to, one row per Site however many times it is
// linked — the mirror of `siteContactRows`, and the same two-role record read
// from the other end.
const popupContactSiteRows = computed(() => {
	type SiteRow = {
		site_id: number;
		roles: { role: string; is_primary: boolean }[];
		sources: string[];
		last_traced_at?: string | null;
	};
	const rows: SiteRow[] = [];
	const byID = new Map<number, SiteRow>();
	for (const link of popupContact.value?.sites ?? []) {
		const existing = byID.get(link.site_id);
		if (existing) {
			existing.roles.push({ role: link.role, is_primary: link.is_primary });
			if (!existing.sources.includes(link.source)) {
				existing.sources.push(link.source);
			}
			existing.last_traced_at = existing.last_traced_at ?? link.last_traced_at;
			continue;
		}
		const row: SiteRow = {
			site_id: link.site_id,
			roles: [{ role: link.role, is_primary: link.is_primary }],
			sources: [link.source],
			last_traced_at: link.last_traced_at,
		};
		byID.set(link.site_id, row);
		rows.push(row);
	}
	return rows;
});

// --- The Site popup's people and skip trace --------------------------------

// A contact link's source vocabulary: where the person came from.
const SOURCE_LABELS: Record<string, string> = {
	skip_trace: "Skip trace",
	county_assessor: "Assessor",
	direct: "Direct",
	rmo: "RMO",
	virtual_inspection: "Virtual inspection",
};

function sourceLabel(source: string): string {
	return source
		? (SOURCE_LABELS[source] ?? source.replace(/_/g, " "))
		: "Unknown";
}

/** The Site's people, as carried on the detail payload. */
const siteContacts = computed<SiteContactSummaryDTO[]>(
	() => popupSite.value?.contacts ?? [],
);

// A contact link's role on the Site. Both roles the data carries are the
// ontology's own terms (Owner, Resident); a role the ontology has not named
// keeps its raw token rather than being given a coined label.
const SITE_CONTACT_ROLE_LABELS: Record<string, string> = {
	owner: "Owner",
	resident: "Resident",
};

function siteContactRoleLabel(role: string): string {
	return SITE_CONTACT_ROLE_LABELS[role] ?? role;
}

/** One person a Site links to, with every role and source the Site links them by. */
interface SiteContactRow {
	contact_id: number;
	name: string;
	roles: { role: string; is_primary: boolean }[];
	sources: string[];
}

/**
 * The Site's people, one row per contact however many times the Site links
 * them. One county assessor record for a jointly-owned parcel is a single
 * contact the Site links twice — owner and resident — and reading that as two
 * identical rows hides the fact that it is one person. The row holding the
 * owner role leads, its primary owner first, and the rest keep payload order.
 */
const siteContactRows = computed<SiteContactRow[]>(() => {
	const rows: SiteContactRow[] = [];
	const byID = new Map<number, SiteContactRow>();
	for (const contact of siteContacts.value) {
		const existing = byID.get(contact.contact_id);
		if (existing) {
			existing.roles.push({
				role: contact.role,
				is_primary: contact.is_primary,
			});
			if (!existing.sources.includes(contact.source)) {
				existing.sources.push(contact.source);
			}
			continue;
		}
		const row: SiteContactRow = {
			contact_id: contact.contact_id,
			name: contact.name,
			roles: [{ role: contact.role, is_primary: contact.is_primary }],
			sources: [contact.source],
		};
		byID.set(contact.contact_id, row);
		rows.push(row);
	}
	const rank = (row: SiteContactRow): number => {
		const owner = row.roles.find((role) => role.role === "owner");
		if (!owner) return 2;
		return owner.is_primary ? 0 : 1;
	};
	return rows
		.map((row, index) => ({ row, index }))
		.sort((a, b) => rank(a.row) - rank(b.row) || a.index - b.index)
		.map((entry) => entry.row);
});

// The configured provider's real per-hit cost, fetched with the Site popup so
// the confirm names what a lookup actually charges — whatever the org has
// configured, and whichever vendor answers. Null means unknown: still loading,
// or the GET failed. An unknown config must not invent a figure or block the
// action, so the confirm falls back to a generic credit warning and the button
// stays enabled.
const skipTraceConfig = ref<SkipTraceConfigDTO | null>(null);

// A configured-but-disabled provider disables the action; unknown stays
// enabled.
const skipTraceUnconfigured = computed(
	() => skipTraceConfig.value?.configured === false,
);

const skipTraceUnconfiguredTitle = computed(() =>
	skipTraceConfig.value ? "Skip trace is not configured" : "",
);

/** The confirm's wording: the configured provider's own cost when it is known,
 * else the generic credit warning. */
function skipTraceConfirmText(): string {
	const display = skipTraceConfig.value?.cost_display;
	return display
		? `Run a skip trace for this site? ${display}.`
		: "Run a skip trace for this site? This spends skip-trace credits.";
}

// The skip-trace run's own in-flight state, so the button cannot double-fire
// and reads as busy while the lookup runs.
const skipTraceBusy = ref(false);

/**
 * Fetch the configured skip-trace provider's cost for a Site. A failure is not
 * the popup's failure: the config simply stays unknown, which the action and
 * the confirm both tolerate.
 */
async function loadSkipTraceConfig(siteID: number, token: number) {
	try {
		const config = await apiClient.JSONGet<SkipTraceConfigDTO>(
			`/api/site/${siteID}/skip-trace`,
		);
		if (token !== popupToken) return;
		skipTraceConfig.value = config;
	} catch {
		if (token !== popupToken) return;
		skipTraceConfig.value = null;
	}
}

function openContactFromSite(contact: { contact_id: number }) {
	pushNode({
		kind: "contact",
		id: contact.contact_id,
		anchor: popupAnchor.value,
	});
}

function openSiteFromContact(siteID: number) {
	pushNode({ kind: "site", id: siteID, anchor: popupAnchor.value });
}

/** A skip-trace person's mailing address, one readable line or empty. */
function historyMailingAddress(person: SkipTracePersonDTO): string {
	const mailing = person.mailing_address;
	if (!mailing) return "";
	const cityState = [mailing.city, mailing.state].filter(Boolean).join(", ");
	return [mailing.street, cityState, mailing.zip].filter(Boolean).join(", ");
}

/** A skip-trace person's phone numbers, in returned order. */
function historyPhones(person: SkipTracePersonDTO): string[] {
	return (person.phones ?? []).map((phone) => phone.number).filter(Boolean);
}

/** A skip-trace person's email addresses, in returned order. */
function historyEmails(person: SkipTracePersonDTO): string[] {
	return (person.emails ?? []).map((email) => email.email).filter(Boolean);
}

/**
 * Run a skip trace for the Site the popup shows. The lookup spends the
 * configured provider's credits, so it asks first with that provider's own
 * cost; on success the Site's contacts are refetched and the run's outcome is
 * toasted. A server whose provider is not configured says so plainly — that is
 * a configuration fact, not a failure.
 */
async function runSkipTrace() {
	const site = popupSite.value;
	if (!site || skipTraceBusy.value) return;
	if (!confirm(skipTraceConfirmText())) {
		return;
	}
	skipTraceBusy.value = true;
	try {
		const result = await apiClient.JSONPost<SkipTraceResultDTO>(
			`/api/site/${site.id}/skip-trace`,
			{},
		);
		if (!result.configured) {
			showToast(
				"Skip trace",
				result.message ||
					skipTraceUnconfiguredTitle.value ||
					"Skip trace is not configured",
			);
			return;
		}
		// The run refreshed (or created) the site's skip-trace contact links, so
		// the popup's people list is now stale.
		popupSite.value = await storeSite.fetchOne(site.id);
		const persons = result.persons_count.toLocaleString();
		showToast(
			result.hit ? "Skip trace hit" : "Skip trace — no match",
			`${persons} person${result.persons_count === 1 ? "" : "s"} · ${result.credits_deducted.toLocaleString()} credit${result.credits_deducted === 1 ? "" : "s"} deducted`,
		);
	} catch (err) {
		showToast(
			"Skip trace failed",
			err instanceof Error ? err.message : "Could not run skip trace.",
		);
	} finally {
		skipTraceBusy.value = false;
	}
}

// --- The Observation popup's person line and Condition block ----------------

// A zone is a district's routing label ("02"), not a person. It stands in for
// the person line only where the source named no technician.
function zoneLabel(zone?: string | null): string {
	const value = zone?.trim();
	return value ? `Zone ${value}` : "";
}

function observationPersonLine(row: {
	creator?: string | null;
	zone?: string | null;
}): string {
	return row.creator || zoneLabel(row.zone) || "—";
}

// The condition vocabulary's readable lines. Every token the source states is
// shown: a token no group names still renders, under Other.
const CONDITION_GROUPS: { label: string; tokens: string[] }[] = [
	{ label: "Water level", tokens: ["low", "full"] },
	{
		label: "Flow",
		tokens: ["stagnant", "stagnant (water blocked)", "flowing"],
	},
	{
		label: "Quality",
		tokens: ["high organic", "high organic (dirty water)", "clean"],
	},
	{ label: "State", tokens: ["dry", "drying out"] },
	{
		label: "Flags",
		tokens: [
			"needs monitoring",
			"unmaintained",
			"unmaintained (pool only)",
			"maintained",
			"maintained (pool only)",
			"fish present",
			"appears vacant",
			"unknown",
		],
	},
];

function conditionToken(raw: string): string {
	return raw
		.trim()
		.toLowerCase()
		.replace(/[\s.;,]+$/, "");
}

// The Condition block's lines: the source's pipe-delimited tokens, deduplicated
// and grouped into the vocabulary's readable groups.
function conditionLines(
	condition?: string | null,
): { label: string; text: string }[] {
	if (!condition) return [];
	const seen = new Set<string>();
	const tokens: string[] = [];
	for (const raw of condition.split("|")) {
		const text = raw.trim();
		if (!text) continue;
		const key = conditionToken(text);
		if (seen.has(key)) continue;
		seen.add(key);
		tokens.push(text);
	}
	const grouped = new Set<string>();
	const lines: { label: string; text: string }[] = [];
	for (const group of CONDITION_GROUPS) {
		const matched = tokens.filter((t) =>
			group.tokens.includes(conditionToken(t)),
		);
		for (const token of matched) grouped.add(conditionToken(token));
		if (matched.length > 0)
			lines.push({ label: group.label, text: matched.join(", ") });
	}
	const other = tokens.filter((t) => !grouped.has(conditionToken(t)));
	if (other.length > 0) lines.push({ label: "Other", text: other.join(", ") });
	return lines;
}

// The Observation popup's link to the observation that set a Feature value.
// The row is normally already in the Feature detail's list; absent it, the node
// still carries the id and loadCurrent falls back to the ledger's page.
function openConditionObservation(observationID: number) {
	const observation = popupFeature.value?.observations?.find(
		(candidate) => candidate.id === observationID,
	);
	pushNode({
		kind: "observation",
		id: observationID,
		anchor: popupAnchor.value,
		observationRow: observation,
	});
}

// The site tiles carry a refresh token, in the same idiom as the treatment and
// observation maps: bulk assigning Sites (or clearing their assignments) in the
// ledger changes who the fabric belongs to, and bulk tagging Sites changes the
// ranked tag its symbol is painted from — a tile the browser has already cached
// would keep painting the old plan or the old style until the cache expired. The
// token steps when the ledger reports that the paint inputs of the Sites it is
// showing have moved, which changes the tile URL and so makes MapLibre
// re-request the tiles. Nothing else about a reload — paging, sorting, filtering
// — moves those columns, so no other ledger change refetches the fabric. The
// token filters nothing and no layer SQL refers to it, so tegola ignores it;
// changing the URL is the whole of its job.
const siteParams = computed<Record<string, string | number>>(() => ({
	site_fabric_token: siteFabricToken.value,
}));

const siteFabricToken = ref(0);

/** The paint inputs of every Site row the ledger is showing, as one string per
 * Site: the assignment (the fabric's base colour) and the carried tag ids (the
 * ranked one selects the symbol, §4.4). Both are tile properties, so both belong
 * in the comparison. The tag ids are sorted because their server order carries
 * no new paint when the set is unchanged. */
const siteRowPaintInputs = computed(() => {
	const inputs = new Map<number, string>();
	if (store.activeTable !== "site") return inputs;
	for (const row of store.rows as MapReviewSiteRow[]) {
		const tags = Array.isArray(row.tag_ids)
			? [...row.tag_ids].sort((a, b) => a - b).join(",")
			: "";
		inputs.set(row.id, `${row.assigned_to}|${row.assigned_on ?? ""}|${tags}`);
	}
	return inputs;
});

// Compare the paint inputs of the Sites present in BOTH this reload and the last
// one — the same page's rows — so a page turn (different ids) or a fresh filter
// cannot be mistaken for a change, and a bulk write, which leaves the ids alone
// and moves only the columns, cannot be missed.
let lastSitePaintInputs = new Map<number, string>();
watch(siteRowPaintInputs, (inputs) => {
	let moved = false;
	for (const [id, value] of inputs) {
		const previous = lastSitePaintInputs.get(id);
		if (previous !== undefined && previous !== value) moved = true;
	}
	lastSitePaintInputs = inputs;
	if (moved) siteFabricToken.value++;
});

// The treatment tile map carries a selection token. When the server resolved a
// selection too large to ship as membership it returns an opaque selection token
// instead; putting it in the URL makes tegola filter the tiles server-side and —
// because the URL changes — makes MapLibre re-request them.
const treatmentParams = computed<Record<string, string | number>>(() => {
	const params: Record<string, string | number> = {};
	const token = store.mapSelection.treatment?.selection_token;
	if (token) params.treatment_selection_token = token;
	return params;
});

// The observation tile map, with its own param token. The observation layer
// never ships an id set — the tile IS the resolved set — so a filtered response
// always carries a selection token; the token in the URL makes tegola apply the
// spec server-side, and an unknown or expired token draws empty rather than the
// last unfiltered set.
const observationParams = computed<Record<string, string | number>>(() => {
	const params: Record<string, string | number> = {};
	const token = store.mapSelection.observation?.selection_token;
	if (token) params.observation_selection_token = token;
	return params;
});

// The Feature tile map carries a selection token exactly as the treatment map
// does: a set too large to ship as membership returns an opaque token that makes
// tegola filter the tiles server-side (and, because the URL changes, makes
// MapLibre re-request them). Within the cap the selection arrives as ids and the
// layer narrows itself by the MVT feature id; either way the site list is never
// on the map's critical path.
const featureParams = computed<Record<string, string | number>>(() => {
	const params: Record<string, string | number> = {};
	const token = store.mapSelection.feature?.selection_token;
	if (token) params.feature_selection_token = token;
	return params;
});

// The two trap tile maps: the standing Stations and the off-station Instances.
// Neither layer has a client-side coarse rendering, so the server ships the kept
// set as membership ids and the layer narrows by the row's own id; a set too
// large to ship falls back to the org scope.
const trapStationParams = computed<Record<string, string | number>>(() => ({}));

const trapInstanceParams = computed<Record<string, string | number>>(
	() => ({}),
);

/**
 * A geometry's first coordinate. Walks down the nested coordinate arrays
 * (Point, MultiPoint, LineString, Polygon, MultiPolygon) to the first
 * [lng, lat] pair.
 */
function firstCoordinate(geometry: GeoJSON.Geometry): number[] | null {
	let node: unknown = (geometry as { coordinates?: unknown }).coordinates;
	while (Array.isArray(node) && Array.isArray(node[0])) {
		node = node[0];
	}
	return Array.isArray(node) && typeof node[0] === "number" ? node : null;
}

/**
 * Sites and features carry the occasional (0, 0) placeholder row (an empty
 * geometry, or a location that was never set). Taken at face value it plots in
 * the Gulf of Guinea and drags the initial viewport out to the whole world, so
 * the map treats the exact origin as no location at all. (Treatments are
 * filtered tile-side, so they never reach here.)
 */
function isNullIsland(location: Location): boolean {
	return location.longitude === 0 && location.latitude === 0;
}

function hasLocation(location?: Location | null): location is Location {
	return (
		location != null &&
		Number.isFinite(location.longitude) &&
		Number.isFinite(location.latitude) &&
		!isNullIsland(location)
	);
}

// The empty state keys off the whole Feature set and the treatments, not the
// map's own filter: the control pane's band rows narrow the tile layers, never
// the set the server resolved, so hiding a band never turns "there is data"
// into the no-data overlay. `store.counts` is the server's exact keep count for
// each table under the shared spec — the same set the tiles paint. The template
// also requires `store.loadState === "resolved"`: a round that failed leaves
// those counts stale (or at their initial zero), and the map must not report a
// failed read as an empty district.
const isEmpty = computed(
	() => store.counts.feature === 0 && store.counts.treatment === 0,
);

// The control pane's Feature counts, from the server (POST
// /api/feature-counts). They band every Feature in the district by its
// effective window_end against the server's clock and the due-soon horizon; the
// server leaves inactive Features out unless "Show inactive" is on, so the
// legend matches what the map draws. The count is taken over the whole district
// — the same whole-set tally the pane used to compute over the loaded Site list
// — and is refetched when the horizon or the inactive row moves, never on every
// filter.
const bandCounts = computed<Record<ControlBand, number>>(() => {
	const counts = store.featureCounts;
	return {
		expired: counts.expired,
		due_soon: counts.due_soon,
		controlled: counts.controlled,
		unknown: counts.unknown,
	};
});

// The district's deactivated Features ("Show inactive"). Deactivation is
// retention, not deletion: these Features stay in the ledger, and only their map
// visibility is toggled. Counted server-side over the whole district.
const inactiveCount = computed(() => store.featureCounts.inactive);

// The Features the server reports an active manual revisit date for ("pinned").
// The pinned overlay's control pane row carries this count; it is the same
// active-plan rule the tile's `revisit_ms` carries, so the legend and the ring
// cannot disagree.
const pinnedCount = computed(() => store.featureCounts.pinned);

// --- Map selections --------------------------------------------------------
//
// These filters only translate the selection the server resolved; the
// composition lives in lib/mapReviewLayers so it is testable without a browser.
// No clause of the filter spec is interpreted here. A set too large to ship as
// membership arrives as an opaque `selection_token` instead: it rides in the
// tile URL, Postgres has already filtered the tiles, and the layer carries no
// filter. The Feature tile works the same way; a set within the cap reaches the
// layer as id membership. The product/period echo is the coarse superset
// the server ships when it could not make a set exact, which the control pane
// flags as approximate. See local://map-review-contract.md §2.

const mapSelection = computed(() => store.mapSelection);

const treatmentMapFilter = computed<any>(() => {
	const sel = mapSelection.value.treatment;
	return treatmentSelectionFilter(sel);
});

/** The trap layers are MVT layers whose feature id is the row's own id, so each
 * selection is membership of the shipped ids (or nothing to narrow when the
 * server did not ship a set). */
const trapStationMapFilter = computed<any>(() =>
	trapSelectionFilter(mapSelection.value.trap_station),
);
const trapInstanceMapFilter = computed<any>(() =>
	trapSelectionFilter(mapSelection.value.trap_instance),
);

/** The Feature tile layer's selection: an exact membership set narrows by the
 * MVT feature id, a selection token means the tiles are already the filtered set
 * (so no filter is added), and a coarse selection the server could not make
 * exact draws unnarrowed (the Feature tile has no coarse clause). */
const featureHighlightFilter = computed<any>(() => {
	const sel = mapSelection.value.feature;
	return featureTileSelectionFilter(sel);
});

/** The pinned Features — the tile's `revisit_ms`, present only while a manual
 * revisit date is active. `!=` null also drops a point whose property is absent
 * (MapLibre reads it as null). */
const pinnedFilter = computed<any>(() => ["!=", ["get", "revisit_ms"], null]);

/** The Feature layer's filter: the shared selection narrowed by the control
 * pane's visibility, all in one `["all", …]`. This is the ONLY Feature point
 * layer, so a point the filter excludes is not drawn at any opacity. The
 * visibility clause bands the tile's `window_end_ms` against the live clock and
 * horizon, exactly as the fill does. */
const featureHighlightFilterComposed = computed(() =>
	asLayerFilter(
		andExpr(
			featureHighlightFilter.value,
			featureVisibilityFilter(layerVisibility, nowMs.value, dueSoonDays.value),
		),
	),
);

/** The selected Feature point, drawn above every other point layer. The Feature
 * tile carries its id as the MVT feature id (tegola's id_fieldname), so it is
 * matched with ["id"]; -1 (never a real id) hides it when nothing is selected. */
const selectedFeatureFilter = computed<any>(() => [
	"==",
	["id"],
	store.selection?.table === "feature" ? store.selection.id : -1,
]);

// The selected Site's polygon, drawn above the base fill/outline. The site tile
// carries its id as the MVT feature id (tegola's id_fieldname), so it is
// matched with ["id"]; -1 (never a real id) hides it when nothing is selected.
const selectedSiteFilter = computed<any>(() => [
	"==",
	["id"],
	store.selection?.table === "site" ? store.selection.id : -1,
]);

// The selected treatment layer filters to one id; -1 (never a real id) hides it
// when nothing is selected. The treatment tile carries its id as the MVT
// feature id (tegola's id_fieldname), so it is matched with ["id"].
const selectedTreatmentFilter = computed<any>(() => [
	"==",
	["id"],
	store.selection?.table === "treatment" ? store.selection.id : -1,
]);

/**
 * True when any table's map highlight is a superset of its table rows — the
 * server omitted an id set that exceeded the shipping cap, so the map draws the
 * coarse clauses alone. Surfacing it keeps the map from silently contradicting
 * the table.
 */
const highlightApproximate = computed(() => {
	const selections = store.mapSelection;
	return (["site", "feature", "treatment"] as MapReviewTable[]).some(
		(table) => {
			const sel = selections[table];
			return sel != null && sel.ids === null && !sel.exact;
		},
	);
});

// --- Zones ----------------------------------------------------------------
//
// A Zone is the district's own named unit — drawn or imported, versioned and
// retired rather than deleted — and it narrows the ledger exactly as the
// standing zones and buffers do: by ids in the shared spec, never through the
// relation graph. The view's part in that is three things, all of them the
// server's to resolve: the polygon layer, the chips that write `spec.zones`,
// and the writes that make, correct and retire a unit. Nothing here computes
// containment, and nothing here re-reads a zone per keystroke: the polygon
// layer is the `zone` tile map (re-requested after a write, see zoneParams)
// and the chips read the zone list.

// The live zones as GET /api/zone returns them — the one list the
// chips read, so a unit can only be toggled while it is drawn. The polygon
// layer itself is the `zone` tile map and holds no client-side geometry.
const zoneList = ref<MapReviewZoneListRow[]>([]);

// The zone draw's own source and layers, so drawing a zone cannot collide
// with any other overlay.
const ZONE_DRAW_SOURCE = "map-review-zone-draw";
const ZONE_DRAW_FILL = "map-review-zone-draw-fill";
const ZONE_DRAW_LINE = "map-review-zone-draw-line";

// The draw and the form it commits into: the ring while the form is open, the
// name and comment it will be written with, and one flag per write so neither
// the button nor the form can double-fire.
const zoneArmed = ref(false);
const zoneDraftGeometry = ref<GeoJSON.Polygon | null>(null);
const zoneDraftName = ref("");
const zoneDraftComment = ref("");
const zoneSaving = ref(false);
const zoneImporting = ref(false);
const zoneRetiring = ref(false);

// The zone being corrected, if any. A correction supersedes that zone's active
// version instead of making a unit, so this is the mode the save reads to choose
// its endpoint. Null means the form is making a new zone, and it is cleared only
// by saving or discarding the form — never by cancelling the draw — so a
// cancelled correction cannot leak into the next new zone.
const zoneCorrecting = ref<MapReviewZoneRow | null>(null);

/** The zones the shared spec selects. A spec written before zones existed
 * carries none, which reads as no narrowing. */
const zoneSelection = computed<number[]>(() => store.spec.zones ?? []);

/** The zone chips, in the order the district's units come back. */
const zoneOptions = computed(() =>
	zoneList.value.map((zone) => ({ id: zone.id, name: zone.name })),
);

/** The geometry the form would write: the ring the operator drew, or null. A
 * correction that only renames sends no geometry at all — the server re-states
 * the active version's own extent — so the shape moves only when the operator
 * draws a new ring. */
const zoneWriteGeometry = computed<GeoJSON.Geometry | null>(() =>
	zoneDraftGeometry.value ? toRaw(zoneDraftGeometry.value) : null,
);

/** Whether the form is complete enough to write: a name, and a ring unless it is
 * a correction (whose geometry is optional). What keeps the save button inert
 * until the write can succeed. */
const zoneSaveable = computed(() => {
	if (zoneDraftName.value.trim() === "") return false;
	return zoneCorrecting.value !== null || zoneDraftGeometry.value !== null;
});

/** The selected zones, redrawn above the rest. The ids are the spec's own — the
 * same ones the server narrows by — and the tile carries a zone's id as its MVT
 * feature id, so the highlight cannot disagree with the ledger. */
const selectedZoneFilter = computed<any>(() =>
	zoneSelectionFilter(zoneSelection.value),
);

function zoneSelected(id: number): boolean {
	return zoneSelection.value.includes(id);
}

/** Read the district's live zones. It runs with the page's other loads and after
 * every zone write; a failure is the caller's to report. */
async function loadZoneList(): Promise<void> {
	zoneList.value = await apiClient.JSONGet<MapReviewZoneListRow[]>("/api/zone");
}

// The zone tiles carry a refresh token, in the same idiom as the site fabric:
// any zone write — create, correct, import or retire — changes what the layer
// draws, and a tile the browser has already cached would keep painting the old
// units until the cache expired. The token steps on every write, which changes
// the tile URL and so makes MapLibre re-request the tiles. It filters nothing
// and no layer SQL refers to it, so tegola ignores it; changing the URL is the
// whole of its job. The org the tile is scoped to is resolved server-side from
// the session, so the URL carries no organization.
const zoneTileToken = ref(0);

const zoneParams = computed<Record<string, string | number>>(() => ({
	zone_tiles_token: zoneTileToken.value,
}));

/** Toggle one zone in the shared spec. setZones owns the page reset and the
 * reload, so the grid, the counts and the map narrow together — and selecting
 * several narrows to their union, which is what lets overlapping units compose. */
function toggleZone(id: number) {
	store.setZones(
		zoneSelected(id)
			? zoneSelection.value.filter((zoneID) => zoneID !== id)
			: [...zoneSelection.value, id],
	);
}

/** Drop the zone narrowing, and with it every zone it selected. */
function clearZoneFilter() {
	store.setZones([]);
}

// The zone draw, reusing the shared spatial tool with its own source and
// layers. The committed ring stays on the map while the form names it; the
// zone itself is created only when that form is saved. It draws in the same
// shared mode every armed draw uses, so the zone draws under the selector
// exactly as the operator set it.
const {
	points: zoneDrawPoints,
	updateOverlay: updateZoneDraw,
	cancel: cancelZoneDraw,
} = usePolygonDraw({
	sourceId: ZONE_DRAW_SOURCE,
	fillId: ZONE_DRAW_FILL,
	lineId: ZONE_DRAW_LINE,
	fillColor: ZONE_COLOR,
	fillOpacity: 0.2,
	lineColor: ZONE_COLOR,
	lineWidth: 2,
	committed: () =>
		zoneDraftGeometry.value ? [toRaw(zoneDraftGeometry.value)] : [],
	armed: zoneArmed,
	mode: drawMode,
	radiusMeters: drawRadiusMeters,
	onRadius: onDrawRadius,
	onCommit: (ring) => {
		zoneDraftGeometry.value = { type: "Polygon", coordinates: [ring] };
		zoneArmed.value = false;
	},
	onCancel: () => {
		// Cancelling the tool drops the draw, not the zone being made: a ring
		// the form is already naming stays on the map.
		zoneArmed.value = false;
	},
});

// The composable renders its overlay from `committed()`; nudge it whenever the
// ring changes from outside (a discard, or a redraw replacing it).
watch(
	() => zoneDraftGeometry.value,
	() => {
		zoneDrawPoints.length = 0;
		updateZoneDraw();
	},
);

function toggleZoneDraw() {
	if (zoneArmed.value) {
		cancelZoneDraw();
		return;
	}
	zoneArmed.value = true;
}

/** Start a correction of the zone the detail shows: the form opens on the zone's
 * own name and comment, the draw arms so a ring can replace the unit's shape,
 * and the mode it sets is what makes the save write a version. The detail closes
 * — what the operator needs next is the map — and only saving or discarding the
 * form ends the mode. */
function correctZone(zone: MapReviewZoneRow) {
	zoneCorrecting.value = zone;
	zoneDraftGeometry.value = null;
	zoneDraftName.value = zone.name;
	zoneDraftComment.value = zone.comment;
	clearSelection();
	zoneArmed.value = true;
}

/** Drop the form: the drawn ring and the names, the correction mode with them,
 * so a cancelled correction cannot leak into the next new zone. */
function clearZoneDraft() {
	if (zoneArmed.value) cancelZoneDraw();
	zoneDraftGeometry.value = null;
	zoneCorrecting.value = null;
	zoneDraftName.value = "";
	zoneDraftComment.value = "";
}

/** Write the form. A new zone becomes the ring's first version; a correction
 * supersedes the active version of the zone the mode names, and both versions
 * stand. The ring travels as the Polygon it was drawn as — the server holds
 * every zone as a MultiPolygon, hand-drawn and imported alike — and a
 * correction that only renames sends no geometry at all: the server re-states
 * the unit's own active extent, so a rename can never move the unit. */
async function saveZoneDraft() {
	const geometry = zoneWriteGeometry.value;
	const correcting = zoneCorrecting.value;
	const name = zoneDraftName.value.trim();
	if (!zoneSaveable.value || zoneSaving.value) return;
	zoneSaving.value = true;
	try {
		const body: {
			name: string;
			comment: string;
			geometry?: GeoJSON.Geometry;
		} = {
			name,
			comment: zoneDraftComment.value.trim(),
		};
		if (geometry) body.geometry = geometry;
		await apiClient.JSONPost<MapReviewZoneRow>(
			correcting ? `/api/zone/${correcting.id}/version` : "/api/zone",
			body,
		);
		clearZoneDraft();
		showToast(
			correcting ? "Zone corrected" : "Zone created",
			correcting
				? `"${name}" is now the zone's active version.`
				: `"${name}" is now a zone of the district.`,
		);
		await refreshZones();
	} catch (err) {
		showToast(
			correcting ? "Could not correct zone" : "Could not create zone",
			errorMessage(err),
		);
	} finally {
		zoneSaving.value = false;
	}
}

/**
 * Upload one KML, KMZ or zipped Shapefile the tools pane handed over. The
 * server makes one zone per polygon-bearing feature and answers with what it
 * made and what it skipped; both are reported, because a skipped feature is the
 * source's own grain and not a failure, but it must not pass in silence either.
 */
async function importZoneFile(file: File) {
	if (zoneImporting.value) return;
	zoneImporting.value = true;
	const body = new FormData();
	body.append("file", file);
	try {
		const result = await apiClient.JSONPost<MapReviewZoneImportResult>(
			"/api/zone/upload",
			body,
		);
		showToast(
			"Zones imported",
			`Created ${result.created.toLocaleString()} zone${result.created === 1 ? "" : "s"}, skipped ${result.skipped.toLocaleString()} feature${result.skipped === 1 ? "" : "s"}.`,
		);
		if (result.created > 0) await refreshZones();
	} catch (err) {
		showToast("Zone import failed", errorMessage(err));
	} finally {
		zoneImporting.value = false;
	}
}

/**
 * Retire a zone. The write is irreversible through this UI — the zone keeps
 * every version but stops drawing and stops narrowing the ledger — so it asks
 * first, in the district's own words.
 */
async function retireZone(zone: MapReviewZoneRow) {
	if (zoneRetiring.value) return;
	if (
		!confirm(
			`Retire the zone "${zone.name}"? Its versions are kept, but it stops drawing and stops narrowing the ledger.`,
		)
	) {
		return;
	}
	zoneRetiring.value = true;
	try {
		// The JSON POST wrapper reads a body, so the request carries the empty
		// object; the zone it retires is named in the path.
		await apiClient.JSONPost<MapReviewZoneRow>(
			`/api/zone/${zone.id}/retire`,
			{},
		);
		showToast("Zone retired", `"${zone.name}" is retired.`);
		clearSelection();
		// A retired zone stops narrowing, so a selection that named it is
		// dropped with it: the filter summary must not keep claiming a narrowing
		// that no chip can clear.
		if (zoneSelected(zone.id)) {
			store.setZones(
				zoneSelection.value.filter((zoneID) => zoneID !== zone.id),
			);
		}
		await refreshZones();
	} catch (err) {
		showToast("Could not retire zone", errorMessage(err));
	} finally {
		zoneRetiring.value = false;
	}
}

/** Re-read what a zone write changed: the map layer's tiles, the chips' list and
 * the ledger. Everything is re-read from the server — a zone's active version,
 * its record count and every narrowing it drives are the server's to resolve,
 * never the client's to patch. Bumping the tile token changes the tile URL,
 * which is what makes MapLibre re-request the polygon layer. */
async function refreshZones(): Promise<void> {
	zoneTileToken.value++;
	await Promise.all([
		loadZoneList().catch((err: unknown) => {
			showToast("Could not load zones", errorMessage(err));
		}),
		store.load(),
	]);
}

// --- Temporary zones ------------------------------------------------------
//
// A temporary zone is a buffer: the places within a stated distance of the kept
// rows of one surface. It is the throwaway twin of a standing zone — no id, no
// version, session-scoped — and it narrows the ledger the same way, through the
// shared spec, so the same seven predicates and the same grid, counts and map
// follow from it with nothing built here. The view's part is to state the
// buffer (the Tools pane owns that form and hands the statement over), hold
// what the server materialised, draw it, and offer to keep it. Nothing here
// computes geometry or containment, and nothing here narrows a layer: the
// server resolves the rows, the union and the ground distance.

// The two writes are guarded against a double-fire, exactly as the zone writes
// are; the form's own fields and their validity live with the form.
const bufferBusy = ref(false);
const tempZoneSaving = ref(false);

/** The temporary zones the shared spec holds — the buffers in force. A spec
 * from a server that predates them, or restored from a visit that carried
 * none, reads as no buffer. */
const tempZones = computed<MapReviewTempZone[]>(
	() => store.spec.temp_zones ?? [],
);

/** The temp zones as the map draws them. The geometries reach MapLibre as plain
 * objects — a reactive proxy must not be handed into a source — and each
 * feature carries the server's label, which is what the overlay's name and the
 * save action read. The source is the spec itself: the layer draws exactly the
 * buffers the ledger is narrowed to, with no second list to keep in step. */
const tempZonesFC = computed<GeoJSON.FeatureCollection>(() => ({
	type: "FeatureCollection",
	features: tempZones.value.map((zone) => ({
		type: "Feature",
		properties: { name: zone.name },
		geometry: toRaw(zone.geometry),
	})),
}));

/** The population view's cells as the map draws them — empty until the first
 * payload arrives, so the layer can be sent to the map before the read
 * resolves and fills in when it does. The cells reach the client already
 * dissolved to the service area, with closed rings, so each is a Polygon as it
 * stands — nothing is clipped or rebuilt here, and no class shape is assembled
 * on the client. A cell the estimator could not class arrives null and is drawn
 * neutral, which is what the legend's unclassified swatch names. */
const populationFC = computed<GeoJSON.FeatureCollection>(() => {
	const view = store.population;
	return {
		type: "FeatureCollection",
		features: (view?.cells ?? []).map((cell) => ({
			type: "Feature",
			properties: { h3: cell.h3, class: cell.class ?? -1 },
			geometry: {
				type: "Polygon",
				// A reactive proxy must not be handed into a source.
				coordinates: [toRaw(cell.boundary)],
			},
		})),
	};
});

/** The population cells' fill: the contract's nine-class palette over the
 * `class` property, drawn as a wash because the layer sits under the record
 * layers on the workbench map. */
const populationFillPaint = computed<FillPaint>(
	() =>
		({
			"fill-color": populationFillColorExpr(),
			"fill-opacity": POPULATION_FILL_OPACITY,
		}) as unknown as FillPaint,
);

/** The waste view's cells as the map draws them — the filtered payload's cells
 * and nothing else. While a read is in flight the source is empty, so a filter
 * change never leaves the previous fill standing over the map; when the new
 * payload lands the layer repaints from it. A thin cell arrives classless and
 * is drawn pale, which is the five-visit floor stated rather than faked. */
const wasteFC = computed<GeoJSON.FeatureCollection>(() => {
	const view = store.wasteOnMap && !store.wasteLoading ? store.waste : null;
	return {
		type: "FeatureCollection",
		features: (view?.h3.cells ?? []).map((cell) => ({
			type: "Feature",
			properties: { h3: cell.h, class: cell.class ?? -1 },
			geometry: {
				type: "Polygon",
				// A reactive proxy must not be handed into a source.
				coordinates: [toRaw(cell.boundary)],
			},
		})),
	};
});

/** The waste cells' fill: the nine-class palette over `class`, pale on the
 * thin-cell marker, drawn as a wash under the record layers. */
const wasteFillPaint = computed<FillPaint>(
	() =>
		({
			"fill-color": wasteFillColorExpr(),
			"fill-opacity": WASTE_FILL_OPACITY,
		}) as unknown as FillPaint,
);

/** Materialise a buffer from the kept rows of the surface the Tools pane
 * stated, and hold it in the shared spec. addTempZone owns the page reset and
 * the reload, so the grid, the counts and the map narrow to the buffer
 * together. A refusal — nothing kept, a zone source, a distance the server will
 * not take — and a transport fault both reach the toast in the server's own
 * words, exactly as the other zone writes do. The distance arrives validated:
 * the form refuses to send zero, a negative or a non-number. */
async function materializeBuffer(request: {
	distance: number;
	unit: MapReviewLengthUnit;
	source: MapReviewTable;
}) {
	if (bufferBusy.value) return;
	bufferBusy.value = true;
	try {
		const reply = await apiClient.JSONPost<MapReviewBufferResponse>(
			"/api/zone/buffer",
			{
				source_table: request.source,
				spec: store.spec,
				distance: request.distance,
				unit: request.unit,
			} satisfies MapReviewBufferRequest,
		);
		store.addTempZone({ name: reply.name, geometry: reply.geometry });
		showToast("Buffer created", `"${reply.name}" now narrows the ledger.`);
	} catch (err) {
		showToast("Could not buffer", errorMessage(err));
	} finally {
		bufferBusy.value = false;
	}
}

/** Save one temporary zone as a standing zone: the buffer's name becomes the
 * zone's name and its geometry the zone's first version, which is the doc's
 * bridge between the throwaway unit and the kept one. The write goes to the
 * same endpoint the draw form writes to, and refreshZones reads the new unit
 * back into the layer and the chips — the client never patches either. The
 * buffer itself stays in the spec: saving keeps a copy, it does not consume
 * what it copies, and the operator clears it like any other clause. */
async function saveTempZoneAsZone(zone: MapReviewTempZone) {
	if (tempZoneSaving.value) return;
	tempZoneSaving.value = true;
	try {
		await apiClient.JSONPost<MapReviewZoneRow>("/api/zone", {
			name: zone.name,
			// A buffer carries no comment of its own; the district comments the
			// unit when it corrects it.
			comment: "",
			geometry: zone.geometry,
		});
		showToast("Zone created", `"${zone.name}" is now a zone of the district.`);
		await refreshZones();
	} catch (err) {
		showToast("Could not create zone", errorMessage(err));
	} finally {
		tempZoneSaving.value = false;
	}
}

/** Dismiss the details and clear the shared selection. */
function clearSelection() {
	popupToken++;
	nav.value = [];
	popupSite.value = null;
	popupFeature.value = null;
	popupTreatment.value = null;
	popupObservation.value = null;
	popupTrapInstance.value = null;
	popupTrapStation.value = null;
	popupTrapStationInstances.value = [];
	popupSpeciesCount.value = null;
	popupZone.value = null;
	popupContact.value = null;
	popupTreatmentFeatureDetail.value = null;
	popupError.value = "";
	popupLoading.value = false;
	popupAnchor.value = null;
	store.clearSelection();
}

/** The row on the current table page, when the selection is one of its rows. */
function selectedRow(): {
	latitude: number | null;
	longitude: number | null;
} | null {
	const selection = store.selection;
	if (!selection || selection.table !== store.activeTable) return null;
	const row = store.rows.find((r) => r.id === selection.id);
	return row ? { latitude: row.latitude, longitude: row.longitude } : null;
}

/**
 * Where to anchor a popup for a selection that did not come from a map click.
 * The row's own geometry is the first choice (the table returns it). A selection
 * whose row is not on the current page — a ledger paged elsewhere, or a record a
 * URL named — falls back to an on-demand read of the record itself, by id. Every
 * ledger resource carries `id`, `latitude` and `longitude`, so one read serves
 * every table; the map no longer holds a client-side fabric to search. A kind
 * with no point (a zone) and a failed or empty read both open the popup without
 * panning, exactly as an unresolved anchor did before.
 */
async function selectionAnchor(
	table: MapReviewTable,
	id: number,
): Promise<{ lng: number; lat: number } | null> {
	const row = selectedRow();
	if (row && row.latitude != null && row.longitude != null) {
		return { lng: row.longitude, lat: row.latitude };
	}
	try {
		const fetched = await store.fetchRow(table, id);
		if (fetched && fetched.latitude != null && fetched.longitude != null) {
			return { lng: fetched.longitude, lat: fetched.latitude };
		}
	} catch {
		// The popup reports its own failure; the anchor is best-effort.
	}
	return null;
}

/**
 * Point the store's selection at the node being shown, so the map highlights
 * where the popup is. Skipped when it already points there, which also stops
 * the selection watcher from treating our own navigation as an outside click.
 */
function followSelection(node: PopupNode) {
	// A contact is not a ledger row: the map has no contact layer and the
	// shared selection cannot name one, so the popup shows it without moving
	// the highlight.
	if (node.kind === "contact") return;
	const selection = store.selection;
	if (selection && selection.table === node.kind && selection.id === node.id) {
		return;
	}
	store.select(node.kind, node.id);
}

/**
 * The row a popup renders, for the kinds whose detail IS their ledger row: the
 * page's row when it carries one, else a read by id. The fallback is what makes
 * a record addressable — a map click can pick a dot the ledger's page does not
 * hold, and a URL's `item` names a record whose page may not even be the one the
 * link states. Every ledger row carries `id`, so one read serves every table.
 */
async function popupRow<T extends MapReviewRow>(
	table: MapReviewTable,
	id: number,
): Promise<T | null> {
	// The page holds ONE table's rows, so it can only answer for its own table:
	// an id match against another table's page would be a different record that
	// happens to share the number.
	const pageRow =
		store.activeTable === table
			? store.rows.find((row) => row.id === id)
			: undefined;
	if (pageRow) return pageRow as T;
	return (await store.fetchRow(table, id)) as T | null;
}

/**
 * Load the node on top of the stack and show it. The fetch is token-guarded so
 * a slow response for a node the user already navigated away from cannot
 * overwrite the newer one.
 */
async function loadCurrent() {
	const node = current.value;
	popupToken++;
	const token = popupToken;
	popupSite.value = null;
	popupFeature.value = null;
	popupTreatment.value = null;
	popupObservation.value = null;
	popupTrapInstance.value = null;
	popupTrapStation.value = null;
	popupTrapStationInstances.value = [];
	popupSpeciesCount.value = null;
	popupZone.value = null;
	popupContact.value = null;
	popupTreatmentFeatureDetail.value = null;
	popupTreatmentSiteAddress.value = "";
	skipTraceConfig.value = null;
	popupError.value = "";
	popupLoading.value = false;

	if (!node) {
		popupAnchor.value = null;
		return;
	}

	popupAnchor.value = node.anchor;
	if (node.anchor) {
		mapRef.value?.easeTo({
			center: [node.anchor.lng, node.anchor.lat],
			duration: 500,
		});
	}

	popupLoading.value = true;
	try {
		if (node.kind === "site") {
			const [site] = await Promise.all([
				storeSite.fetchOne(node.id),
				// The provider's cost rides along with the site load; a failure
				// is swallowed there so it never becomes a popup error.
				loadSkipTraceConfig(node.id, token),
			]);
			if (token !== popupToken) return;
			popupSite.value = site;
		} else if (node.kind === "feature") {
			popupFeature.value = await storeFeature.fetchByID(node.id);
		} else if (node.kind === "contact") {
			popupContact.value = await apiClient.JSONGet<ContactDetailDTO>(
				`/api/contact/${node.id}`,
			);
		} else if (node.kind === "observation") {
			// The row travels with the node when the link or the map click that
			// opened it already held one; otherwise it is the page's row, else a
			// read by id, so a linked observation the page does not carry still
			// opens.
			const row =
				node.observationRow ??
				(await popupRow<MapReviewObservationRow>(node.kind, node.id));
			if (token !== popupToken) return;
			if (!row) {
				popupError.value = "This observation no longer exists.";
				return;
			}
			popupObservation.value = row;
		} else if (node.kind === "trap_instance") {
			// The row carries everything the detail shows, the full species × sex
			// matrix included. It is normally the ledger row the page is showing;
			// a map click (or a link) can select an instance the page does not
			// carry, so the row is read by id when the page misses it.
			const row = await popupRow<MapReviewTrapInstanceRow>(node.kind, node.id);
			if (token !== popupToken) return;
			if (!row) {
				popupError.value = "This trap instance no longer exists.";
				return;
			}
			popupTrapInstance.value = row;
		} else if (node.kind === "trap_station") {
			// Same fast path as a trap instance: the page row when present, else
			// a by-id read so an off-page station dot still opens its detail.
			const row = await popupRow<MapReviewTrapStationRow>(node.kind, node.id);
			if (token !== popupToken) return;
			if (!row) {
				popupError.value = "This trap station no longer exists.";
				return;
			}
			popupTrapStation.value = row;
			// The counts-over-time chart reads the station's whole series, not
			// the ledger's page, so its instances are fetched by the station
			// relation and token-guarded like the row above. The chart is
			// supplementary: a failed series load leaves it empty rather than
			// replacing the station's own detail with an error.
			try {
				const instances = await store.fetchStationInstances(node.id);
				if (token !== popupToken) return;
				popupTrapStationInstances.value = instances;
			} catch {
				popupTrapStationInstances.value = [];
			}
		} else if (node.kind === "species_count") {
			// The row is the ledger row itself: a Species Count carries everything
			// its detail shows. The page's row is the fast path; a linked record
			// the page does not carry is read by id.
			const row = await popupRow<MapReviewSpeciesCountRow>(node.kind, node.id);
			if (token !== popupToken) return;
			if (!row) {
				popupError.value = "This species count no longer exists.";
				return;
			}
			popupSpeciesCount.value = row;
		} else if (node.kind === "zone") {
			// A zone travels like a species count: the ledger row is the whole
			// detail. Zones are served by the same generic QUERY as every other
			// ledger, so a link naming a zone the current page does not hold still
			// opens it.
			const row = await popupRow<MapReviewZoneRow>(node.kind, node.id);
			if (token !== popupToken) return;
			if (!row) {
				popupError.value = "This zone no longer exists.";
				return;
			}
			popupZone.value = row;
		} else {
			const treatment = await storeTreatment.fetchByID(node.id);
			if (token !== popupToken) return;
			popupTreatment.value = treatment;
			// The Feature detail carries the Site id the Place links need; a
			// failure to load it only costs those two links, so it is not fatal.
			try {
				const feature = await storeFeature.fetchByID(treatment.feature_id);
				if (token !== popupToken) return;
				popupTreatmentFeatureDetail.value = feature;
				// The Site the Feature belongs to is fetched on demand for the
				// address line; it never blocks the popup and a failure leaves
				// the address blank.
				void loadTreatmentSiteAddress(feature.site_id, token);
			} catch {
				popupTreatmentFeatureDetail.value = null;
			}
		}
	} catch (err) {
		if (token !== popupToken) return;
		popupError.value = err instanceof Error ? err.message : "Could not load.";
	} finally {
		if (token === popupToken) popupLoading.value = false;
	}
}

/** Open a node as a new root of the graph (a map click or a table row). */
function openRoot(node: PopupNode) {
	nav.value = [node];
	followSelection(node);
	void loadCurrent();
}

/**
 * Open the record a URL names. A ledger record goes through the shared
 * selection, so the popup's node graph, the map halo and the details rail follow
 * exactly as they would a click's, and the anchor fallback finds a row the page
 * does not carry. A Contact is the one kind that is not a ledger row (it has no
 * layer and the selection never points at it), so it is seeded into the graph
 * directly.
 */
function openUrlRecord(item: MapReviewUrlItem) {
	if (item.kind === "contact") {
		nav.value = [{ kind: "contact", id: item.id, anchor: null }];
		void loadCurrent();
		return;
	}
	store.select(item.kind, item.id);
}

/** Walk one link deeper: append the related node and show it. */
function pushNode(node: PopupNode) {
	nav.value = [...nav.value, node];
	followSelection(node);
	void loadCurrent();
}

/** Walk back up one node, landing on the node shown before it. */
function goBack() {
	if (nav.value.length <= 1) {
		clearSelection();
		return;
	}
	nav.value = nav.value.slice(0, -1);
	const node = current.value;
	if (node) followSelection(node);
	void loadCurrent();
}

// The links between nodes. Each carries the anchor to pan to: the related
// record's own geometry when it has one, else the current popup's anchor, so
// walking a link never throws the viewport around.
function anchorFor(
	location?: Location | null,
): { lng: number; lat: number } | null {
	return hasLocation(location)
		? { lng: location.longitude, lat: location.latitude }
		: popupAnchor.value;
}

function openSiteFromFeature(siteID: number) {
	pushNode({ kind: "site", id: siteID, anchor: popupAnchor.value });
}

function openFeatureFromSite(feature: Feature) {
	pushNode({
		kind: "feature",
		id: feature.id,
		anchor: anchorFor(feature.location),
	});
}

function openFeatureFromTreatment(featureID: number) {
	pushNode({ kind: "feature", id: featureID, anchor: popupAnchor.value });
}

function openSiteFromTreatment(siteID: number) {
	pushNode({ kind: "site", id: siteID, anchor: popupAnchor.value });
}

function openTreatmentFromFeature(treatmentID: number) {
	pushNode({ kind: "treatment", id: treatmentID, anchor: popupAnchor.value });
}

function openFeatureFromObservation(featureID: number) {
	pushNode({ kind: "feature", id: featureID, anchor: popupAnchor.value });
}

function openSiteFromObservation(siteID: number) {
	pushNode({ kind: "site", id: siteID, anchor: popupAnchor.value });
}

// The station popup's Instances figure is actionable: it narrows the Mosquito
// counts surface to exactly this station's instances — the same relation clause
// the Traps grid's Instances cell emits — and closes the detail so the ledger is
// in view.
function openInstancesOfStation(stationID: number) {
	store.followRelation({
		table: "trap_instance",
		column: RELATION_COLUMN,
		op: "relation",
		source: "trap_station",
		source_id: stationID,
	});
	clearSelection();
}

// The Feature popup's observation list walks to the Observation popup. The row
// it names is already in hand, so it travels with the node and the popup needs
// no second fetch.
function openObservationFromFeature(observation: MapReviewObservationRow) {
	pushNode({
		kind: "observation",
		id: observation.id,
		anchor: popupAnchor.value,
		observationRow: observation,
	});
}

function openTreatmentFromObservation(treatmentID: number) {
	pushNode({ kind: "treatment", id: treatmentID, anchor: popupAnchor.value });
}

// The Treatment popup's link to the observation that led to it. The row is the
// one the Feature detail carries — already fetched for the Place links — and
// the linked observation is recorded at the treatment's own Feature, so it is
// in that list. Absent it, the node still carries the id and loadCurrent falls
// back to the ledger's page.
function openObservationFromTreatment(observationID: number) {
	const observation = popupTreatmentFeatureDetail.value?.observations?.find(
		(candidate) => candidate.id === observationID,
	);
	pushNode({
		kind: "observation",
		id: observationID,
		anchor: popupAnchor.value,
		observationRow: observation,
	});
}

// A map click carries its own anchor; a table selection has to find one, and
// the treatment detail's geometry is the last resort.
let anchorFromClick: { lng: number; lat: number } | null = null;

watch(
	() => store.selection,
	async (selection, previous) => {
		if (
			selection &&
			previous &&
			selection.table === previous.table &&
			selection.id === previous.id
		) {
			return;
		}
		// The popup's own links move the selection to follow the node they
		// show; only a selection from outside (a map click or a table row)
		// starts a new graph, so it must not clobber the walk in progress.
		const shown = current.value;
		if (
			selection &&
			shown &&
			selection.table === shown.kind &&
			selection.id === shown.id
		) {
			return;
		}
		if (!selection) {
			if (nav.value.length > 0) clearSelection();
			return;
		}
		let anchor = anchorFromClick;
		anchorFromClick = null;
		if (!anchor) {
			anchor = await selectionAnchor(selection.table, selection.id);
		}
		if (!anchor && selection.table === "treatment") {
			// The row is not on the current page (the drawer switched tables, or
			// the row is paged out); the detail carries the treatment's geometry.
			try {
				const treatment = await storeTreatment.fetchByID(selection.id);
				const coordinate = treatment.geometry
					? firstCoordinate(treatment.geometry)
					: null;
				if (coordinate) {
					anchor = { lng: coordinate[0], lat: coordinate[1] };
				}
			} catch {
				// The popup below reports the failure itself.
			}
		}
		openRoot({ kind: selection.table, id: selection.id, anchor });
	},
);

/**
 * Select a record on the map: the popup graph opens on it. The ledger drawer is
 * deliberately NOT touched here — it is the map's reading surface, opened only
 * from its own control, never as a side effect of a click.
 */
function selectFromMap(
	table: MapReviewTable,
	id: number,
	event: MapMouseEvent,
) {
	anchorFromClick = { lng: event.lngLat.lng, lat: event.lngLat.lat };
	store.select(table, id);
}

/**
 * A click on the observation layer. The tile groups observations by Feature and
 * its row id is that Feature's id, so the dot stands for one of the Feature's
 * observations; the Feature detail (which carries the Feature's observations,
 * newest first) supplies the row the Observation popup renders, sparing a
 * second fetch. The tile also carries `observation_id` — the most recent
 * observation *the filter selected* — which under an active filter is not
 * necessarily the Feature's newest overall, so the popup opens that record by id
 * rather than defaulting to the head of the list. An older cached tile without
 * the property, or a property naming a record the detail no longer carries,
 * falls back to the newest observation; a Feature with no observation to show
 * falls back to its own popup.
 */
async function openObservationForFeature(
	featureID: number,
	observationID: number | undefined,
	anchor: { lng: number; lat: number } | null,
) {
	try {
		const detail = await storeFeature.fetchByID(featureID);
		const observations = detail.observations ?? [];
		const observation =
			observations.find((candidate) => candidate.id === observationID) ??
			observations[0];
		if (!observation) {
			openRoot({ kind: "feature", id: featureID, anchor });
			return;
		}
		openRoot({
			kind: "observation",
			id: observation.id,
			anchor,
			observationRow: observation,
		});
	} catch {
		// The Feature popup below reports the failure itself.
		openRoot({ kind: "feature", id: featureID, anchor });
	}
}

/**
	}
}

/**
 * Map click: a small box around the pointer is queried against the drawn layers
 * in the order the operator reads them, so the 3–5px dots are clickable without
 * pixel-perfect aim.
 *
 * Priority: the trap layers win when visible — they draw last, above every other
 * data layer, so their dot is the mark actually under the pointer, and the
 * off-station instances layer is checked before the stations layer because it
 * renders later; then the Observation layer, whose popup links on to the
 * Feature and the Site; then the Treatment layer, whose popup is the richest
 * leaf; then the Feature points, more specific than the Site that holds them;
 * then the Site fabric, whose click uses the pointer's own vertex. A click on
 * empty map dismisses the popup.
 */
function onMapClick(event: MapMouseEvent) {
	const map = mapRef.value;
	if (!map) return;
	// A click while the zone draw is armed belongs to the draw, not to the records
	// under it: the clicked-vertex mode adds a vertex per click, and a click is
	// also how the circle takes its centre. Selecting whatever Site or Feature
	// sat under each of those clicks would open and close details under the
	// operator's hand while they are drawing. The draw's own handlers are
	// separate map listeners, so they still see every click.
	if (zoneArmed.value) return;
	const box: [[number, number], [number, number]] = [
		[event.point.x - 6, event.point.y - 6],
		[event.point.x + 6, event.point.y + 6],
	];
	// The trap layers draw above every other data layer, so their dot is the
	// mark actually under the pointer and they are queried first. The
	// off-station instances layer renders after the stations layer, so it wins
	// when both dots overlap. Each is a whole-layer toggle, so a hidden layer
	// (the group row or its own row off) is skipped and never answers a click.
	const trapInstanceLayers = ["map-review-trap-instances"].filter((layer) =>
		map.getLayer(layer),
	);
	if (
		layerVisibility.traps &&
		layerVisibility.trapInstances &&
		trapInstanceLayers.length > 0
	) {
		const hit = map
			.queryRenderedFeatures(box, { layers: trapInstanceLayers })
			.find(
				(feature) => feature.id != null && Number.isFinite(Number(feature.id)),
			);
		if (hit) {
			selectFromMap("trap_instance", Number(hit.id), event);
			return;
		}
	}
	const trapStationLayers = ["map-review-trap-stations"].filter((layer) =>
		map.getLayer(layer),
	);
	if (
		layerVisibility.traps &&
		layerVisibility.trapStations &&
		trapStationLayers.length > 0
	) {
		const hit = map
			.queryRenderedFeatures(box, { layers: trapStationLayers })
			.find(
				(feature) => feature.id != null && Number.isFinite(Number(feature.id)),
			);
		if (hit) {
			selectFromMap("trap_station", Number(hit.id), event);
			return;
		}
	}
	const observationLayers = ["map-review-observations"].filter((layer) =>
		map.getLayer(layer),
	);
	if (layerVisibility.observations && observationLayers.length > 0) {
		const hit = map
			.queryRenderedFeatures(box, { layers: observationLayers })
			.find(
				(feature) => feature.id != null && Number.isFinite(Number(feature.id)),
			);
		if (hit) {
			// The tile carries the id of the observation the dot stands for
			// (the most recent one the filter selected), so the popup opens that
			// exact record rather than the Feature's newest overall. Absent it —
			// an older cached tile — the handler falls back to the newest.
			const observationID = Number(hit.properties?.observation_id);
			void openObservationForFeature(
				Number(hit.id),
				Number.isFinite(observationID) ? observationID : undefined,
				{
					lng: event.lngLat.lng,
					lat: event.lngLat.lat,
				},
			);
			return;
		}
	}
	const treatmentLayers = ["map-review-treatments-highlight"].filter((layer) =>
		map.getLayer(layer),
	);
	if (treatmentLayers.length > 0) {
		const hit = map
			.queryRenderedFeatures(box, { layers: treatmentLayers })
			.find(
				(feature) => feature.id != null && Number.isFinite(Number(feature.id)),
			);
		if (hit) {
			selectFromMap("treatment", Number(hit.id), event);
			return;
		}
	}
	const featureLayers = [
		"map-review-point-selected",
		"map-review-features-highlight",
	].filter((layer) => map.getLayer(layer));
	// The Feature tile carries its id as the MVT feature id (tegola's
	// id_fieldname), like the treatment and observation layers.
	const featureHit = map
		.queryRenderedFeatures(box, { layers: featureLayers })
		.find(
			(feature) => feature.id != null && Number.isFinite(Number(feature.id)),
		);
	// A Feature is more specific than the Site that holds it, so it wins.
	if (featureHit) {
		selectFromMap("feature", Number(featureHit.id), event);
		return;
	}
	// The Site fabric is polygons, so the click's own point picks the Site
	// boundary under it.
	const siteLayers = [
		"map-review-sites-selected-fill",
		"map-review-sites-fill",
	].filter((layer) => map.getLayer(layer));
	const siteHit = map
		.queryRenderedFeatures(event.point, { layers: siteLayers })
		.find(
			(feature) => feature.id != null && Number.isFinite(Number(feature.id)),
		);
	if (siteHit) {
		selectFromMap("site", Number(siteHit.id), event);
		return;
	}
	clearSelection();
}

function onMapLoad(event: MglEvent<"load">) {
	mapRef.value = event.map;
	event.map.on("click", onMapClick);
	mapZoom.value = event.map.getZoom();
	// A link that names a camera opens where it was taken, so the district frame
	// is skipped entirely rather than corrected afterwards: the camera is jumped
	// to once, here or in the mount below, wherever the map becomes available
	// first.
	if (suppliedCamera) {
		jumpToSuppliedCamera(event.map);
		return;
	}
	frameDistrict();
	fitMap();
	noteCamera(event.map);
}

/** Place the map at the camera a link supplied. It is a jump, not a flight: the
 * saved camera is where the link was taken, so it is the opening frame. */
function jumpToSuppliedCamera(map: MaplibreMap) {
	if (!suppliedCamera) return;
	map.jumpTo({
		center: [suppliedCamera.lng, suppliedCamera.lat],
		zoom: suppliedCamera.zoom,
	});
	mapCamera.value = suppliedCamera;
}

/** Note the camera the map is showing, so the URL can name it. The codec rounds
 * to 5 decimals of position and 2 of zoom, so a pan that lands within that of
 * the written frame writes nothing (see the writer's no-op guard). */
function noteCamera(map: MaplibreMap) {
	const center = map.getCenter();
	mapCamera.value = { lng: center.lng, lat: center.lat, zoom: map.getZoom() };
}

/** Note the zoom the ladder control reads. The resolution a saved H3 layer is
 * cut at follows the zoom (one rung per band), so the control can only name the
 * grid on screen if it is told where the camera is. The same event is where the
 * URL learns the camera: `moveend` is the only thing that moves it, so a written
 * camera can never fight a moving map. */
function onMapMove(event: MglEvent<"moveend">) {
	mapZoom.value = event.map.getZoom();
	noteCamera(event.map);
}

/** The camera's opening centre: the district's own service area, read off the
 * session so it is in hand before any map layer loads. Without it the map would
 * open on [0, 0] — the Gulf of Guinea, the wrong hemisphere for a
 * mosquito-control district — and this re-centres the moment the session lands
 * if it was still in flight at first paint. A district with no service area on
 * file falls back to the continental-US default, the same framing the no-data
 * map uses. Mirrors review/Site.vue. */
const initialCenter = computed<[number, number]>(() => {
	const sa = session.organization?.service_area;
	if (sa && sa.min) {
		return [
			(sa.min.longitude + sa.max.longitude) / 2,
			(sa.min.latitude + sa.max.latitude) / 2,
		];
	}
	const center = boundsDefault().getCenter();
	return [center.lng, center.lat];
});

/** Pull the camera onto the district the instant the map is ready, before any
 * data layer loads, so the opening frame is the district rather than [0, 0].
 * `boundsForServiceArea` is the continental-US default when the district has no
 * service area, and the jump is immediate (duration 0) because this is the
 * opening frame, not a transition. */
function frameDistrict() {
	const map = mapRef.value;
	if (!map) return;
	map.fitBounds(boundsForServiceArea(session.organization?.service_area), {
		padding: 40,
		duration: 0,
	});
}

/** Frame the viewport on the district's service area (or the continental-US
 * default when it has none). The Site and Feature points now stream from
 * vector tiles, so there is no client-side collection to bound; the district is
 * the frame the map opens on, and every point lies within it. */
function fitMap() {
	const map = mapRef.value;
	if (!map) return;
	map.fitBounds(boundsForServiceArea(session.organization?.service_area), {
		padding: 60,
		duration: 800,
	});
}

function toggleDrawer() {
	if (store.drawerOpen) {
		store.closeDrawer();
	} else {
		store.openDrawer(store.activeTable);
	}
}

/** A failure as the operator needs it: the API's own message when the response
 * carried one — the platform's refusals name what to fix, where axios's
 * "Request failed with status code 400" names nothing — else the axios/Error
 * message, else the generic. The body is read by narrowing rather than by
 * asserting a shape onto it, so a body that is not the expected object cannot
 * make the toast lie. */
function errorMessage(
	err: unknown,
	fallback = "Could not load Map Review data.",
): string {
	if (err instanceof Error && "response" in err) {
		const response: unknown = err.response;
		if (response && typeof response === "object" && "data" in response) {
			const { data } = response;
			if (data && typeof data === "object" && "message" in data) {
				const { message } = data;
				if (typeof message === "string" && message !== "") return message;
			}
		}
	}
	if (err instanceof Error && err.message) return err.message;
	return fallback;
}

/**
 * The page's data, loaded after the map is already up. The ledger's rows and
 * counts, the control pane's legend and zone chips, the Treatment filters'
 * product and intervention options, and the site fabric's technician palette
 * each load independently and report their own failure, so none of them holds
 * the map's first paint — they stream in behind it. The ledger's own round
 * records its outcome on the store (`store.loadState`), which is what lets the
 * map tell an empty district from one that has not answered yet — or one whose
 * answer failed.
 */
async function loadPageData(): Promise<void> {
	// A failure fetching one resource must not discard the others that did load,
	// or the reverse. The table's own rows and counts come from the shared query
	// endpoint, and the map's control-pane legend counts come from their own
	// whole-district endpoint — the Feature points are drawn from tiles, so the
	// pane no longer counts a loaded Site list. The product list is the
	// Treatment table's product-filter options, and this page owns the only load
	// of it (the map's toast reports a failure). The intervention list is the
	// Treatment table's other id-valued filter and had no loader here at all, so
	// its panel offered no options and its summary named the id. The zone list
	// names the control pane's zone chips; the polygon layer itself is the
	// `zone` tile map, and the list is re-read after every zone write rather
	// than with every filter. The member list is the site fabric's palette: the
	// tile names the assigned technician's id and only the members can be given
	// a colour, so the list is read before the fabric is painted by it. It is
	// memoized in the store, so the ledger's own use of the same list is one
	// request, not two. The symbology catalog is the Site layers' paint and the
	// Symbology pane's rows (§4.5/§4.6): it is not a ledger's data, but it is
	// this page's read and its failure is the operator's to know about — until
	// it lands, the fabric keeps the technician paint it has always had.
	const [
		loadedFeatureCounts,
		loadedProducts,
		loadedInterventions,
		loadedTables,
		loadedZones,
		loadedUsers,
		loadedSymbology,
	] = await Promise.allSettled([
		store.loadFeatureCounts(dueSoonDays.value, layerVisibility.inactive),
		storeProduct.list(),
		storeIntervention.load(),
		store.load(),
		loadZoneList(),
		storeUser.withAll(),
		loadSymbologyCatalog(),
	]);
	// The saved H3 layers load beside the rest but report their own failure into
	// the tray, so one unreadable list does not raise a page-level toast the
	// operator cannot act on.
	void loadH3Layers();
	const failures: string[] = [];
	if (loadedFeatureCounts.status === "fulfilled") {
		// Stamp the clock the map bands against once the counts land, so the
		// tile points and the legend are banded against the same instant.
		nowMs.value = Date.now();
	} else {
		failures.push(errorMessage(loadedFeatureCounts.reason));
	}
	if (loadedUsers.status === "fulfilled") {
		orgUserIDs.value = loadedUsers.value.map((user) => user.id);
	}
	if (loadedProducts.status === "rejected") {
		failures.push(errorMessage(loadedProducts.reason));
	}
	if (loadedInterventions.status === "rejected") {
		failures.push(errorMessage(loadedInterventions.reason));
	}
	if (loadedTables.status === "rejected") {
		failures.push(errorMessage(loadedTables.reason));
	}
	if (loadedZones.status === "rejected") {
		failures.push(errorMessage(loadedZones.reason));
	}
	if (loadedSymbology.status === "rejected") {
		failures.push(errorMessage(loadedSymbology.reason));
	}
	if (failures.length > 0) {
		toastTitle.value = "Failed to load map data";
		toastMessage.value = failures.join("; ");
		toastShow.value = true;
	}
}

// ── Writing the URL ────────────────────────────────────────────────────────
//
// The address bar is a live picture of the view, not a history of it: every
// write is a `replace`, so browser Back leaves the page and the in-page
// navigation stays where it belongs — the popup's own ‹ Back and the drawer's
// close. Pushing would also collide with the details panel's Back control, which
// walks a node graph rather than a URL trail: two different "back"s on one key.
//
// Writes trail the state by the same 250 ms the store uses for its text
// clauses: a filter box changes on every keystroke, and a rewrite per character
// would thrash the router. A state that encodes to the query already in the bar
// writes nothing, so a change that does not alter the URL never navigates.
const URL_WRITE_DEBOUNCE_MS = 250;

// The query keys the workbench owns. Anything else in the query is carried
// through untouched, so an unrelated parameter (there is none today) survives a
// view change without the writer having to understand it.
const URL_PARAM_KEYS: readonly string[] = [
	"state",
	"ledger",
	"table",
	"item",
	"page",
	"limit",
	"sort",
	"panel",
	"map",
	"layers",
	"spec",
];

// Armed once the incoming URL's state is in hand (see the mount). Before that
// every change is a default the URL did not ask for, and writing one would
// overwrite the link the operator opened.
let urlWriterArmed = false;
let urlWriteTimer: number | null = null;

/** The state the URL should name right now. `item` is the panel's current node,
 * so walking Site → Feature → Observation leaves the URL naming the observation,
 * and a Contact — a record the panel shows but the map cannot highlight — is as
 * addressable as a ledger row. */
function urlState(): MapReviewUrlState {
	const node = current.value;
	const tab = leftTab.value;
	return {
		spec: store.spec,
		activeTable: store.activeTable,
		page: store.page,
		limit: store.limit,
		sort: store.sort,
		drawerOpen: store.drawerOpen,
		item: node ? { kind: node.kind, id: node.id } : null,
		panel:
			tab === "details" || tab === "analysis" || tab === "ask" ? tab : null,
		camera: mapCamera.value,
		layers: { ...layerVisibility },
	};
}

/** The workbench's own parameters as the address bar currently states them. */
function urlQueryInBar(): Record<string, string> {
	const named: Record<string, string> = {};
	for (const key of URL_PARAM_KEYS) {
		const value = route.query[key];
		if (typeof value === "string") named[key] = value;
	}
	return named;
}

/** Write the URL, if it no longer names the view. */
function writeUrl(): void {
	if (!urlWriterArmed) return;
	const encoded = encodeMapReviewUrl(urlState());
	const inBar = urlQueryInBar();
	const unchanged =
		Object.keys(encoded).length === Object.keys(inBar).length &&
		Object.entries(encoded).every(([key, value]) => inBar[key] === value);
	if (unchanged) return;
	const preserved: Record<string, string> = {};
	for (const [key, value] of Object.entries(route.query)) {
		if (typeof value === "string" && !URL_PARAM_KEYS.includes(key)) {
			preserved[key] = value;
		}
	}
	void router.replace({ query: { ...preserved, ...encoded } });
}

/** Note that the view changed; write the URL once the change settles. */
function scheduleUrlWrite(): void {
	if (!urlWriterArmed) return;
	if (urlWriteTimer !== null) clearTimeout(urlWriteTimer);
	urlWriteTimer = window.setTimeout(() => {
		urlWriteTimer = null;
		writeUrl();
	}, URL_WRITE_DEBOUNCE_MS);
}

// Everything the URL names. The spec and the layer checkboxes change nested
// values, so the watcher is deep; the debounce collapses a burst — a typed
// filter, a pan — into one write.
watch(
	[
		() => store.spec,
		() => store.activeTable,
		() => store.page,
		() => store.limit,
		() => store.sort,
		() => store.drawerOpen,
		current,
		leftTab,
		mapCamera,
		layerVisibility,
	],
	scheduleUrlWrite,
	{ deep: true },
);

onMounted(async () => {
	// The measured label column is in real pixels, so a webfont that arrives
	// after the first render would leave it a frame stale. Re-measure once the
	// fonts are in.
	void document.fonts?.ready.then(() => nextTick().then(measurePopupColumns));
	try {
		// The org-scoped stores address their requests from the session. FullPage
		// loads it for the first render, but a fresh sign-in drops the cached
		// session (`resetSessionState`) without re-arming `isLoading`, so this
		// view cannot assume the URLs are there when it mounts. `get()` is
		// memoized: it returns the loaded session, or shares FullPage's
		// in-flight fetch rather than issuing a second one.
		await session.get();

		// What the URL names, in the order the page needs it. The store's own
		// state (spec, table, page, limit, sort, ledger flag) was hydrated before
		// the first render; what is left is the view's — the camera, the rail tab
		// and the record — and it lands before the ledger's one round, so that
		// round is already the linked page under the linked filters.
		if (incoming.present) {
			// A link that names a camera opens where it was taken; the district
			// frame is what a visit without one opens on. If the map style has not
			// loaded yet, `onMapLoad` jumps instead of framing.
			if (suppliedCamera) {
				if (mapRef.value) jumpToSuppliedCamera(mapRef.value);
			} else {
				frameDistrict();
			}
			// The rail tab before the record: an item opens the details rail
			// whatever `panel` says, because the panel is what shows the item.
			if (incoming.state.panel) leftTab.value = incoming.state.panel;
			if (incoming.state.item) openUrlRecord(incoming.state.item);
		} else {
			// The session carries the district's extent, so the moment it lands
			// the camera can open on the district instead of waiting on the data
			// layers below (or, worse, sitting on [0, 0]). If the map style is
			// still loading, `frameDistrict` no-ops and `onMapLoad` frames it.
			frameDistrict();
		}

		// With the session in hand the map is up: every layer paints from the
		// internalized tile endpoints and the ledger carries its own loading
		// state, so the page's first paint waits on the session alone. The rest
		// streams in behind it (see loadPageData) and never holds the overlay.
		//
		// The ledger set is stated on every mount rather than left to the
		// store's default: it is shared state, and the lab trap view
		// (`/_/lab/records`) narrows it to its own two ledgers while it is up.
		// Saying "the nine grid tables" here is what makes the two surfaces
		// independent of the order they are visited in.
		store.setLedgerTables(MAP_REVIEW_TABLES);
		void loadPageData();
	} catch (err) {
		toastTitle.value = "Failed to load map data";
		toastMessage.value = errorMessage(err);
		toastShow.value = true;
	} finally {
		loading.value = false;
		// A supplied camera is already the frame the link asked for; the
		// district fit is what a visit without one settles on.
		if (!suppliedCamera) fitMap();
		// Nothing above may rewrite the incoming URL: it is only now that the
		// state it names is in hand, so the writer arms last and its first write
		// makes the address bar canonical (`state=1` and this view's fields).
		urlWriterArmed = true;
		scheduleUrlWrite();
	}
});

// Query failures surface as a toast; the table keeps its last good page.
watch(
	() => store.error,
	(message) => {
		if (!message) return;
		toastTitle.value = store.errorTitle || "Request failed";
		toastMessage.value = message;
		toastShow.value = true;
	},
);

// The station popup's counts-over-time chart follows the shared spec: a filter
// changed while the station detail is open re-reads the station's series under
// it, so the chart and the ledger never disagree. Only the chart re-reads — the
// popup's own facts are the station's, not the page's. A filter that excludes
// every one of the station's instances leaves the series empty, which the chart
// reports as "No mosquitoes captured."
watch(
	() => store.spec,
	async () => {
		const node = current.value;
		if (!node || node.kind !== "trap_station") return;
		const token = popupToken;
		try {
			const instances = await store.fetchStationInstances(node.id);
			if (token !== popupToken) return;
			popupTrapStationInstances.value = instances;
		} catch {
			if (token !== popupToken) return;
			popupTrapStationInstances.value = [];
		}
	},
	{ deep: true },
);
</script>
